/**
 * 用途：广告投放管理页，按平台（Facebook / Google / TikTok）展示广告系列、广告组、广告三级列表，
 *       支持筛选、分页、启停、删除等操作
 * 路由：/campaigns/:platform （platform 取值 facebook | google | tiktok）
 */
import { useRef, useState, useEffect, useMemo, useCallback, type CSSProperties, type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent, type ReactNode } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { MultiSelect, type SelectOption } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import { ColumnToggle } from '../components/ColumnToggle';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  accountsApi,
  type AdFlatNode,
  type AdSetFlatNode,
  type CampaignFlatNode,
  type FacebookEditAdPayload,
  type FacebookEditAdSetPayload,
  type FacebookEditCampaignPayload,
  type FacebookEditDetailResponse,
  type FacebookCampaignTemplate,
  type FacebookMaterial,
  type FacebookGeoLocation,
  type FacebookCustomAudience,
  type FacebookIssuesInfo,
  type FlatListSummary,
  type GoogleEditDetailResponse,
  type MetricFilterRule,
  type SavedViewDraftPayload,
  type SavedViewItemResponse,
  type TikTokEditAdPayload,
  type TikTokEditAdSetPayload,
  type TikTokEditCampaignPayload,
  type TikTokEditDetailResponse,
  type ValidateAdFullRequest,
} from '../api/accounts';
import { api } from '../api/client';
import { adTagsApi, type TagDefinition } from '../api/adTags';
import { useAppStore } from '../store/useAppStore';
import { confirmDialog } from '../store/useConfirmStore';
import { sf } from '../lib/fieldPolicy';
import { AdDiagnosisModal } from '../components/AdDiagnosisModal';
import { normalizeFacebookPlacementState } from './facebookPlacements';
import { TableLoadingState } from '../components/TableLoadingOverlay';

type PlatformFilter = 'all' | 'facebook' | 'google' | 'tiktok';
type StatusFilter =
  | 'all'
  | 'draft'
  | 'not_delivering'
  | 'review'
  | 'active'
  | 'paused'
  | 'processing'
  | 'archived'
  | 'deleted'
  | 'eligible'
  | 'removed'
  | 'ended'
  | 'misconfigured'
  | 'limited'
  | 'learning'
  | 'invalid'
  | 'completed'
  | 'terminated'
  | 'closed';
type WizardStep = 1 | 2 | 3 | 4;
type TabType = 'campaign' | 'adset' | 'ad';
type ToastType = 'success' | 'error' | 'warning' | 'info';
type ToastItem = { id: number; message: string; type: ToastType };
type CopyEntityType = 'campaign' | 'adset' | 'ad';
type EditEntityType = 'campaign' | 'adset' | 'ad';
type QuickEditTarget =
  | {
      kind: 'field';
      section: EditEntityType;
      field: string;
      label: string;
    }
  | {
      kind: 'budget';
      section: 'campaign' | 'adset';
      label: string;
      budgetType?: 'daily' | 'lifetime';
    }
  | {
      kind: 'tag';
      section: EditEntityType;
      tagKey: string;
      label: string;
    };
type BatchTagFormState = Record<string, string>;
type BatchTagFormStateMap = Record<TabType, BatchTagFormState>;
type BatchTagDefsByLevel = Record<TabType, TagDefinition[]>;
type MetricFilterOperator = MetricFilterRule['operator'];
type MetricSortField = SavedViewDraftPayload['sortField'];
type MetricSortFieldName = Exclude<MetricSortField, null>;

const TAG_MULTI_VALUE_SEPARATOR = '、';

const STATUS_FILTER_OPTIONS: Record<PlatformFilter, SelectOption[]> = {
  all: [
    { value: 'all', label: '全部状态' },
    { value: 'draft', label: '草稿' },
    { value: 'not_delivering', label: '未投放' },
    { value: 'review', label: '审核中' },
    { value: 'active', label: '投放中' },
    { value: 'paused', label: '已暂停' },
    { value: 'processing', label: '处理中' },
    { value: 'archived', label: '已归档' },
    { value: 'deleted', label: '已删除' },
    { value: 'eligible', label: '符合条件' },
    { value: 'removed', label: '已移除' },
    { value: 'ended', label: '已结束' },
    { value: 'misconfigured', label: '有效（配置有误）' },
    { value: 'limited', label: '有效（受限）' },
    { value: 'learning', label: '有效（正在学习）' },
    { value: 'invalid', label: '无效' },
    { value: 'completed', label: '已完成' },
    { value: 'terminated', label: '已终止' },
    { value: 'closed', label: '已关闭' },
  ],
  facebook: [
    { value: 'all', label: '全部状态' },
    { value: 'draft', label: '草稿' },
    { value: 'not_delivering', label: '未投放' },
    { value: 'review', label: '审核中' },
    { value: 'active', label: '投放中' },
    { value: 'paused', label: '已暂停' },
    { value: 'processing', label: '处理中' },
    { value: 'archived', label: '已归档' },
    { value: 'deleted', label: '已删除' },
  ],
  google: [
    { value: 'all', label: '全部状态' },
    { value: 'draft', label: '草稿' },
    { value: 'eligible', label: '符合条件' },
    { value: 'paused', label: '已暂停' },
    { value: 'removed', label: '已移除' },
    { value: 'ended', label: '已结束' },
    { value: 'not_delivering', label: '未投放' },
    { value: 'misconfigured', label: '有效（配置有误）' },
    { value: 'limited', label: '有效（受限）' },
    { value: 'learning', label: '有效（正在学习）' },
    { value: 'invalid', label: '无效' },
  ],
  tiktok: [
    { value: 'all', label: '全部状态' },
    { value: 'draft', label: '草稿' },
    { value: 'not_delivering', label: '未投放' },
    { value: 'active', label: '投放中' },
    { value: 'paused', label: '已暂停' },
    { value: 'deleted', label: '已删除' },
    { value: 'completed', label: '已完成' },
    { value: 'terminated', label: '已终止' },
    { value: 'closed', label: '已关闭' },
  ],
};

const isPlacementTag = (td: TagDefinition) => {
  const text = `${td.key} ${td.label}`.toLowerCase();
  return text.includes('版位') || text.includes('placement');
};

const parseMultiTagValue = (raw?: string) => {
  const value = (raw || '').trim();
  if (!value) return [];
  return value.split(/[、,]/).map((v) => v.trim()).filter(Boolean);
};

const serializeMultiTagValue = (values: string[]) => values.map((v) => v.trim()).filter(Boolean).join(TAG_MULTI_VALUE_SEPARATOR);

const parseJSONValue = <T,>(raw: string | undefined, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const buildPublishValidationMessage = (platformLabel: string, result: { campaign_error?: string; adset_error?: string; creative_error?: string }) => {
  if (result.campaign_error) return `${platformLabel} 广告系列校验失败：广告系列存在未填写的必填项。${result.campaign_error}`;
  if (result.adset_error) return `${platformLabel} 投放配置校验失败：广告组存在未填写的必填项。${result.adset_error}`;
  if (result.creative_error) return `${platformLabel} 广告校验失败：广告存在未填写的必填项。${result.creative_error}`;
  return `${platformLabel} 校验失败，请检查草稿配置`;
};

const buildGoogleValidatePayload = (detail: GoogleEditDetailResponse): ValidateAdFullRequest | null => {
  const campaign = detail.campaign;
  if (!campaign?.account_id) return null;
  const adset = detail.adset;
  const ad = detail.ad;
  const keywords = parseJSONValue<Array<{ text?: string; match_type?: string }>>(adset?.keywords_json, [])
    .map((item) => ({ text: (item.text || '').trim(), match_type: item.match_type || 'PHRASE' }))
    .filter((item) => item.text);
  const locationIDs = parseJSONValue<string[]>(adset?.location_ids_json, []).map((item) => item.trim()).filter(Boolean);
  const headlines = parseJSONValue<string[]>(ad?.headlines_json, []).map((item) => item.trim()).filter(Boolean);
  const descriptions = parseJSONValue<string[]>(ad?.descriptions_json, []).map((item) => item.trim()).filter(Boolean);
  return {
    platform: 'google',
    account_id: campaign.account_id,
    campaign_name: campaign.name?.trim() || undefined,
    campaign_objective: campaign.objective || undefined,
    campaign_status: campaign.campaign_status || undefined,
    daily_budget: campaign.daily_budget_micros ? campaign.daily_budget_micros / 1_000_000 : undefined,
    start_time: campaign.start_date || undefined,
    end_time: campaign.end_date || undefined,
    enable_google_search: campaign.target_google_search,
    enable_search_network: campaign.target_search_network,
    enable_partner_search_network: campaign.target_partner_search_network,
    enable_display_network: campaign.target_content_network,
    adset_name: adset?.name?.trim() || undefined,
    adset_status: adset?.adset_status || undefined,
    cpc_bid: adset?.cpc_bid_micros || undefined,
    keywords: keywords.length ? keywords : undefined,
    location_ids: locationIDs.length ? locationIDs : undefined,
    language_id: adset?.language_id?.trim() || undefined,
    ad_name: ad?.name?.trim() || undefined,
    ad_status: ad?.ad_status || undefined,
    link: ad?.final_url?.trim() || undefined,
    path1: ad?.path1?.trim() || undefined,
    path2: ad?.path2?.trim() || undefined,
    headlines: headlines.length ? headlines : undefined,
    descriptions: descriptions.length ? descriptions : undefined,
    headline: headlines[0] || undefined,
    description: descriptions[0] || undefined,
  };
};

const buildTikTokValidatePayload = (detail: TikTokEditDetailResponse): ValidateAdFullRequest | null => {
  const campaign = detail.campaign;
  if (!campaign?.account_id) return null;
  const adset = detail.adset;
  const ad = detail.ad;
  const videoIDs = parseJSONValue<string[]>(ad?.video_ids_json, []).map((item) => item.trim()).filter(Boolean);
  const itemIDs = parseJSONValue<string[]>(ad?.tiktok_item_ids_json, []).map((item) => item.trim()).filter(Boolean);
  return {
    platform: 'tiktok',
    advertiser_id: campaign.account_id,
    campaign_name: campaign.name?.trim() || undefined,
    objective_type: campaign.objective_type || undefined,
    campaign_budget_mode: campaign.budget_mode || undefined,
    campaign_budget_strategy: campaign.budget_strategy || undefined,
    campaign_budget: campaign.budget || undefined,
    campaign_status: campaign.operation_status || undefined,
    roas_bid: campaign.roas_bid || undefined,
    adgroup_name: adset?.name?.trim() || undefined,
    audience_ids: adset?.audience_ids?.length ? adset.audience_ids : undefined,
    excluded_audience_ids: adset?.excluded_audience_ids?.length ? adset.excluded_audience_ids : undefined,
    adgroup_budget_mode: adset?.budget_mode || undefined,
    adgroup_budget: adset?.budget || undefined,
    schedule_type: adset?.schedule_type || undefined,
    start_time: adset?.start_time || undefined,
    end_time: adset?.end_time || undefined,
    placement_type: adset?.placement_type || undefined,
    placements: adset?.placements?.length ? adset.placements : undefined,
    optimization_goal: adset?.optimization_goal || undefined,
    bid_type: adset?.bid_type || undefined,
    bid_price: adset?.bid_price || undefined,
    billing_event: adset?.billing_event || undefined,
    pixel_id: adset?.pixel_id || undefined,
    optimization_event: adset?.optimization_event || undefined,
    location_ids: adset?.location_ids?.length ? adset.location_ids : undefined,
    age_groups: adset?.age_groups?.length ? adset.age_groups : undefined,
    gender: adset?.gender || undefined,
    languages: adset?.languages?.length ? adset.languages : undefined,
    interest_category_ids: adset?.interest_category_ids?.length ? adset.interest_category_ids : undefined,
    video_action_category_ids: adset?.video_action_category_ids?.length ? adset.video_action_category_ids : undefined,
    interest_behavior_items: adset?.interest_behavior_items?.length ? adset.interest_behavior_items : undefined,
    operating_systems: adset?.operating_systems?.length ? adset.operating_systems : undefined,
    network_types: adset?.network_types?.length ? adset.network_types : undefined,
    identity_type: ad?.identity_type || adset?.identity_type || undefined,
    identity_id: ad?.identity_id || adset?.identity_id || undefined,
    creative_material_mode: adset?.creative_material_mode || undefined,
    app_id: adset?.app_id || undefined,
    promotion_type: adset?.promotion_type || undefined,
    adgroup_status: adset?.adgroup_status || undefined,
    ad_name: ad?.name?.trim() || undefined,
    ad_format: ad?.ad_format || undefined,
    ad_text: ad?.ad_text?.trim() || undefined,
    call_to_action: ad?.call_to_action || undefined,
    landing_page_url: ad?.landing_page_url?.trim() || undefined,
    video_id: ad?.video_id?.trim() || undefined,
    video_ids: videoIDs.length ? videoIDs : undefined,
    tiktok_item_id: ad?.tiktok_item_id?.trim() || undefined,
    tiktok_item_ids: itemIDs.length ? itemIDs : undefined,
    ad_status: ad?.ad_status || undefined,
    auth_code: ad?.auth_code || undefined,
  };
};

const buildFacebookValidatePayload = (detail: FacebookEditDetailResponse): ValidateAdFullRequest | null => {
  const campaign = detail.campaign;
  if (!campaign?.account_id) return null;
  const adset = detail.adset;
  const ad = detail.ad;
  const normalizedPlacement = adset ? normalizeFacebookPlacementState({
    publisherPlatforms: adset.publisher_platforms ?? [],
    facebookPositions: adset.facebook_positions ?? [],
    instagramPositions: adset.instagram_positions ?? [],
    devicePlatforms: adset.device_platforms ?? [],
    userOS: adset.user_os ?? [],
  }) : null;
  const carouselCards = parseJSONValue<Array<Record<string, unknown>>>(ad?.carousel_cards_json, []);
  const dynBodies = parseJSONValue<string[]>(ad?.dyn_bodies_json, []).map((item) => item.trim()).filter(Boolean);
  const dynTitles = parseJSONValue<string[]>(ad?.dyn_titles_json, []).map((item) => item.trim()).filter(Boolean);
  const dynDescs = parseJSONValue<string[]>(ad?.dyn_descs_json, []).map((item) => item.trim()).filter(Boolean);
  const dynCTAs = parseJSONValue<string[]>(ad?.dyn_ctas_json, []).map((item) => item.trim()).filter(Boolean);
  const dynImages = parseJSONValue<string[]>(ad?.dyn_images_json, []).map((item) => item.trim()).filter(Boolean);
  const dynVideos = parseJSONValue<string[]>(ad?.dyn_videos_json, []).map((item) => item.trim()).filter(Boolean);
  return {
    platform: 'facebook',
    account_id: campaign.account_id,
    campaign_name: campaign.name?.trim() || undefined,
    campaign_objective: campaign.campaign_objective || undefined,
    campaign_status: campaign.campaign_status || undefined,
    daily_budget: campaign.daily_budget || undefined,
    campaign_lifetime_budget: campaign.campaign_lifetime_budget || undefined,
    spend_cap: campaign.spend_cap || undefined,
    special_ad_categories: campaign.special_ad_categories?.length ? campaign.special_ad_categories : undefined,
    campaign_budget_scope: campaign.campaign_budget_scope || undefined,
    bid_strategy: campaign.bid_strategy || undefined,
    adset_name: adset?.name?.trim() || undefined,
    budget_type: adset?.budget_type || undefined,
    adset_daily_budget: adset?.adset_daily_budget || undefined,
    adset_lifetime_budget: adset?.adset_lifetime_budget || undefined,
    billing_event: adset?.billing_event || undefined,
    optimization_goal: adset?.optimization_goal || undefined,
    destination_type: adset?.destination_type || undefined,
    bid_amount: adset?.bid_amount || undefined,
    bid_constraints_roas: adset?.bid_constraints_roas || undefined,
    start_time: adset?.start_time || undefined,
    end_time: adset?.end_time || undefined,
    pixel_id: adset?.pixel_id || undefined,
    custom_event_type: adset?.custom_event_type || undefined,
    product_catalog_id: adset?.product_catalog_id || undefined,
    product_set_id: adset?.product_set_id || undefined,
    countries: adset?.countries?.length ? adset.countries : undefined,
    genders: adset?.genders?.length ? adset.genders : undefined,
    age_min: adset?.age_min || undefined,
    age_max: adset?.age_max || undefined,
    interests: adset?.interests?.length ? adset.interests : undefined,
    behaviors: adset?.behaviors?.length ? adset.behaviors : undefined,
    custom_audience_ids: adset?.custom_audience_ids?.length ? adset.custom_audience_ids : undefined,
    excluded_custom_audience_ids: adset?.excluded_custom_audience_ids?.length ? adset.excluded_custom_audience_ids : undefined,
    publisher_platforms: normalizedPlacement?.publisherPlatforms?.length ? normalizedPlacement.publisherPlatforms : undefined,
    facebook_positions: normalizedPlacement?.facebookPositions?.length ? normalizedPlacement.facebookPositions : undefined,
    instagram_positions: normalizedPlacement?.instagramPositions?.length ? normalizedPlacement.instagramPositions : undefined,
    device_platforms: normalizedPlacement?.devicePlatforms?.length ? normalizedPlacement.devicePlatforms : undefined,
    user_os: normalizedPlacement?.userOS?.length ? normalizedPlacement.userOS : undefined,
    adset_status: adset?.adset_status || undefined,
    frequency_cap: adset?.frequency_cap || undefined,
    frequency_cap_reset_period: adset?.frequency_cap_reset_period || undefined,
    attribution_spec: adset?.attribution_spec || undefined,
    is_incremental_attribution_enabled: adset?.is_incremental_attribution_enabled || undefined,
    locales: adset?.locales?.length ? adset.locales : undefined,
    ad_name: ad?.name?.trim() || undefined,
    ad_status: ad?.ad_status || undefined,
    page_id: ad?.page_id || undefined,
    ad_type: ad?.ad_type || undefined,
    image_hash: ad?.image_hash || undefined,
    video_id: ad?.video_id || undefined,
    thumb_hash: ad?.thumb_hash || undefined,
    message: ad?.message?.trim() || undefined,
    headline: ad?.headline?.trim() || undefined,
    description: ad?.description?.trim() || undefined,
    link: ad?.link?.trim() || undefined,
    caption: ad?.caption?.trim() || undefined,
    cta_type: ad?.cta_type || undefined,
    url_tags: ad?.url_tags?.trim() || undefined,
    creative_feature_enrollments: ad?.creative_feature_enrollments,
    offer_enabled: ad?.offer_enabled,
    offer_source: ad?.offer_source,
    offer_code: ad?.offer_code || undefined,
    offer_description: ad?.offer_description || undefined,
    offer_email_subscription_enabled: ad?.offer_email_subscription_enabled,
    offer_card_image_hash: ad?.offer_card_image_hash || undefined,
    carousel_cards: carouselCards.length ? carouselCards : undefined,
    multi_share_optimized: ad?.multi_share_optimized,
    multi_share_end_card: ad?.multi_share_end_card,
    dyn_bodies: dynBodies.length ? dynBodies : undefined,
    dyn_titles: dynTitles.length ? dynTitles : undefined,
    dyn_descs: dynDescs.length ? dynDescs : undefined,
    dyn_ctas: dynCTAs.length ? dynCTAs : undefined,
    dyn_images: dynImages.length ? dynImages : undefined,
    dyn_videos: dynVideos.length ? dynVideos : undefined,
    dyn_link_url: ad?.dyn_link_url || undefined,
    dyn_display_url: ad?.dyn_display_url || undefined,
    dyn_url_tags: ad?.dyn_url_tags || undefined,
    existing_creative_id: ad?.existing_creative_id || undefined,
    existing_post_platform: ad?.existing_post_platform || undefined,
    existing_post_id: ad?.existing_post_id || undefined,
    existing_post_page_id: ad?.existing_post_page_id || undefined,
    instagram_actor_id: ad?.instagram_actor_id || undefined,
    partnership_code: ad?.partnership_code || undefined,
    partnership_ig_user_id: ad?.partnership_ig_user_id || undefined,
    lead_gen_form_id: ad?.lead_gen_form_id || undefined,
    app_id: ad?.app_id || undefined,
    app_store_url: ad?.app_store_url || undefined,
  };
};

const setTagValue = (prev: Record<string, string> | undefined, key: string, value: string) => {
  const next = { ...(prev ?? {}) };
  const cleaned = value.trim();
  if (cleaned) next[key] = cleaned;
  else delete next[key];
  return next;
};

type CopyModalState =
  | {
      entity: CopyEntityType;
      platform: PlatformFilter;
      sourceID: number;
      sourceName: string;
      targetAccountID: string;
      targetCampaignID: string;
      targetAdSetID: string;
    }
  | null;

type EditModalState = {
  entity: EditEntityType;
  platform: PlatformFilter;
  sourceID: number;
  title: string;
  accountID: string;
  editableFields: string[];
  quickEdit: QuickEditTarget | null;
  campaign: FacebookEditCampaignPayload;
  adset: FacebookEditAdSetPayload;
  ad: FacebookEditAdPayload;
  tiktokCampaign: TikTokEditCampaignPayload;
  tiktokAdset: TikTokEditAdSetPayload;
  tiktokAd: TikTokEditAdPayload;
} | null;

type FilterAccountOption = {
  id: string;
  account_id: string;
  account_name?: string;
  platform?: string;
  is_mine?: boolean;
};

// Hierarchy types used by the create-wizard's fetchCreateTargets
type AdNode = {
  id: number; third_ad_id: string; name: string; status: number;
  publish_status?: 'pending' | 'published' | 'failed' | string;
  publish_error?: string; updated_at?: string;
};
type AdSetNode = {
  id: number; third_ad_set_id: string; name: string; status: number;
  updated_at?: string; ads: AdNode[];
};
type CampaignNode = {
  id: number; third_ad_campaign_id: string; platform_account_id: string;
  platform: string; name: string; status: number; updated_at?: string;
  ad_sets: AdSetNode[];
};
type HierarchyResponse = {
  list: CampaignNode[]; total: number; page: number; size: number; total_page: number;
};

// ── Metric formatters ─────────────────────────────────────────────────────────

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}
function fmtCount(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function findTagOptionLabel(options: TagDefinition['options'], value?: string): string | undefined {
  if (!value) return undefined;
  for (const option of options) {
    if (option.value === value) return option.label;
    if (option.children?.length) {
      const nested = findTagOptionLabel(option.children, value);
      if (nested) return nested;
    }
  }
  return undefined;
}

function flattenTagOptions(options: TagDefinition['options']): SelectOption[] {
  return options.flatMap((option) => {
    const current = [{ value: option.value, label: option.label }];
    if (!option.children?.length) return current;
    return [...current, ...flattenTagOptions(option.children)];
  });
}

function mergeTagDefinitions(definitions: TagDefinition[]): TagDefinition[] {
  const merged = new Map<string, TagDefinition>();
  for (const def of definitions) {
    const existing = merged.get(def.key);
    if (!existing) {
      merged.set(def.key, def);
      continue;
    }
    const seen = new Set<string>();
    const mergedOptions = [...existing.options, ...def.options].filter((option) => {
      const optionKey = `${option.value}@@${option.label}`;
      if (seen.has(optionKey)) return false;
      seen.add(optionKey);
      return true;
    });
    merged.set(def.key, { ...existing, options: mergedOptions });
  }
  return Array.from(merged.values());
}

function prioritizeBatchTagDefinitions(definitions: TagDefinition[], siteCode?: string): TagDefinition[] {
  if (siteCode !== 'z') return definitions;
  const priorityKeys = ['campaign_goal', 'campaign_goal_detail'];
  const priorityIndex = new Map(priorityKeys.map((key, index) => [key, index]));
  return [...definitions].sort((a, b) => {
    const aIndex = priorityIndex.get(a.key);
    const bIndex = priorityIndex.get(b.key);
    if (aIndex == null && bIndex == null) return 0;
    if (aIndex == null) return 1;
    if (bIndex == null) return -1;
    return aIndex - bIndex;
  });
}

function getMetricFilterLabel(rule: MetricFilterRule) {
  const metricLabel = METRIC_FILTER_OPTIONS.find((item) => item.value === rule.metric)?.label ?? rule.metric;
  const operatorLabel = METRIC_OPERATOR_OPTIONS.find((item) => item.value === rule.operator)?.label ?? rule.operator;
  if (rule.operator === 'between') {
    return `${metricLabel} ${operatorLabel} ${rule.value} - ${rule.value2 ?? rule.value}`;
  }
  return `${metricLabel} ${operatorLabel} ${rule.value}`;
}

function ROASCell({ roas }: { roas?: number }) {
  if (roas == null) return <>—</>;
  const color = roas >= 3 ? 'var(--green)' : roas >= 2 ? 'var(--amber)' : roas > 0 ? 'var(--text2)' : 'var(--text3)';
  return <span style={{ fontWeight: 700, fontSize: 12, color }}>{roas.toFixed(2)}x</span>;
}

const EMPTY_FLAT_LIST_SUMMARY: FlatListSummary = {
  row_count: 0,
  active_count: 0,
  spend: 0,
  roas: 0,
  cpc: 0,
  cpm: 0,
  cpa: 0,
  purchase_count: 0,
  cta_registration: 0,
  cta_purchase: 0,
  cta_purchase_value: 0,
  cta_purchase_roas: 0,
};

function fmtSummaryROAS(n?: number): string {
  return `${(n ?? 0).toFixed(2)}x`;
}

function fmtListDateTime(value?: string): string {
  const raw = (value || '').trim();
  if (!raw) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16).replace('T', ' ');
  }
  const normalized = raw.replace('T', ' ').replace('Z', '');
  if (normalized.length >= 16) return normalized.slice(0, 16);
  return normalized;
}

function toDatetimeLocalValue(value?: string): string {
  const raw = (value || '').trim();
  if (!raw) return '';
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }
  return raw.replace('Z', '').slice(0, 16);
}

function datetimeLocalToRFC3339(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
}

// Sticky-scroll table layout (module-level constants moved to Campaigns() body for responsive sizing)
const W_OBJ = 100, W_BUDGET = 120, W_CAP = 100;
const W_TAG_COL = 130; // 每个动态标签列宽

/** 名称列宽度（桌面/移动端均可拖拽，持久化 localStorage） */
const NAME_COL_LS_KEY = 'campaigns_real_name_col_px';
const NAME_COL_DEFAULT_W = 260;
const NAME_COL_MIN_W = 160;
const NAME_COL_MAX_W = 560;
const NAME_COL_MOBILE_DEFAULT_W = 140;
const NAME_COL_MOBILE_MIN_W = 96;
const NAME_COL_MOBILE_MAX_W = 320;

function readStoredNameColWidthPx(isMobileFlag: boolean): number {
  try {
    const v = localStorage.getItem(NAME_COL_LS_KEY);
    if (v) {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n)) return n;
    }
  } catch { /* ignore */ }
  return isMobileFlag ? NAME_COL_MOBILE_DEFAULT_W : NAME_COL_DEFAULT_W;
}

// ── Objective options per platform ──────────────────────────────────────────
const OBJECTIVE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  facebook: [
    { value: 'OUTCOME_AWARENESS', label: '知名度' },
    { value: 'OUTCOME_TRAFFIC', label: '流量' },
    { value: 'OUTCOME_SALES', label: '销售转化' },
  ],
  google: [
    { value: 'SALES', label: '销售' },
    { value: 'LEADS', label: '线索' },
    { value: 'WEBSITE_TRAFFIC', label: '网站流量' },
    { value: 'AWARENESS', label: '品牌认知' },
  ],
  tiktok: [
    { value: 'REACH', label: '覆盖人数' },
    { value: 'TRAFFIC', label: '访问量' },
    { value: 'VIDEO_VIEWS', label: '视频播放量' },
    { value: 'LEAD_GENERATION', label: '线索收集' },
    { value: 'PRODUCT_SALES,WEB_CONVERSIONS', label: '销量' },
  ],
};
// objective → { label, color, bg }
const OBJECTIVE_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  // Facebook
  OUTCOME_AWARENESS:    { label: '知名度',   color: '#7c3aed', bg: 'rgba(124,58,237,.10)' },
  OUTCOME_TRAFFIC:      { label: '流量',     color: '#2563eb', bg: 'rgba(37,99,235,.10)' },
  OUTCOME_SALES:        { label: '销售转化', color: '#059669', bg: 'rgba(5,150,105,.10)' },
  OUTCOME_ENGAGEMENT:   { label: '互动',     color: '#d97706', bg: 'rgba(217,119,6,.10)' },
  OUTCOME_LEADS:        { label: '线索',     color: '#0891b2', bg: 'rgba(8,145,178,.10)' },
  OUTCOME_APP_PROMOTION:{ label: '应用推广', color: '#be185d', bg: 'rgba(190,24,93,.10)' },
  // Google
  SALES:                { label: '销售',     color: '#059669', bg: 'rgba(5,150,105,.10)' },
  LEADS:                { label: '线索',     color: '#0891b2', bg: 'rgba(8,145,178,.10)' },
  WEBSITE_TRAFFIC:      { label: '网站流量', color: '#2563eb', bg: 'rgba(37,99,235,.10)' },
  AWARENESS:            { label: '品牌认知', color: '#7c3aed', bg: 'rgba(124,58,237,.10)' },
  // TikTok
  REACH:                { label: '覆盖人数',   color: '#7c3aed', bg: 'rgba(124,58,237,.10)' },
  TRAFFIC:              { label: '访问量',     color: '#2563eb', bg: 'rgba(37,99,235,.10)' },
  CONVERSIONS:          { label: '转化',       color: '#059669', bg: 'rgba(5,150,105,.10)' },
  PRODUCT_SALES:        { label: '销量',       color: '#059669', bg: 'rgba(5,150,105,.10)' },
  WEB_CONVERSIONS:      { label: '销量',   color: '#0d9488', bg: 'rgba(13,148,136,.10)' },
  VIDEO_VIEWS:          { label: '视频播放量', color: '#d97706', bg: 'rgba(217,119,6,.10)' },
  LEAD_GENERATION:      { label: '线索收集',   color: '#0891b2', bg: 'rgba(8,145,178,.10)' },
  APP_PROMOTION:        { label: '应用推广',   color: '#be185d', bg: 'rgba(190,24,93,.10)' },
  // Google BiddingStrategyType（同步时存为 objective）
  MAXIMIZE_CONVERSIONS:       { label: '尽可能多的转化', color: '#059669', bg: 'rgba(5,150,105,.10)' },
  MAXIMIZE_CONVERSION_VALUE:  { label: '尽可能提高转化价值', color: '#059669', bg: 'rgba(5,150,105,.10)' },
  TARGET_CPA:                 { label: '目标每次转化费用', color: '#0891b2', bg: 'rgba(8,145,178,.10)' },
  TARGET_ROAS:                { label: '目标广告支出回报率', color: '#0891b2', bg: 'rgba(8,145,178,.10)' },
  MAXIMIZE_CLICKS:            { label: '尽可能多的点击', color: '#2563eb', bg: 'rgba(37,99,235,.10)' },
  MANUAL_CPC:                 { label: '每次点击费用手动出价', color: '#2563eb', bg: 'rgba(37,99,235,.10)' },
  MANUAL_CPM:                 { label: '每千次展示手动出价', color: '#7c3aed', bg: 'rgba(124,58,237,.10)' },
  TARGET_IMPRESSION_SHARE:    { label: '目标展示份额', color: '#7c3aed', bg: 'rgba(124,58,237,.10)' },
  TARGET_SPEND:               { label: '目标支出', color: '#d97706', bg: 'rgba(217,119,6,.10)' },
  MANUAL_CPV:                 { label: '每次观看手动出价', color: '#d97706', bg: 'rgba(217,119,6,.10)' },
  // Google AdvertisingChannelType
  SEARCH:   { label: '搜索', color: '#2563eb', bg: 'rgba(37,99,235,.10)' },
  DISPLAY:  { label: '展示', color: '#7c3aed', bg: 'rgba(124,58,237,.10)' },
  SHOPPING: { label: '购物', color: '#059669', bg: 'rgba(5,150,105,.10)' },
  VIDEO:    { label: '视频', color: '#d97706', bg: 'rgba(217,119,6,.10)' },
  PERFORMANCE_MAX: { label: '效果最大化', color: '#be185d', bg: 'rgba(190,24,93,.10)' },
  DEMAND_GEN:      { label: '需求开发', color: '#0891b2', bg: 'rgba(8,145,178,.10)' },
};

function resolveTikTokListBudgetStrategy(c: CampaignFlatNode): string {
  const normalized = (c.budget_strategy || '').trim();
  if (normalized) return normalized;
  const mode = (c.budget_scope || '').trim();
  const objective = (c.objective || '').trim();
  const amount = Number(c.daily_budget || 0);
  const isSalesObjective = objective === 'PRODUCT_SALES' || objective === 'WEB_CONVERSIONS';
  if (isSalesObjective && amount <= 0) {
    return 'ADGROUP_BUDGET';
  }
  if (mode === 'BUDGET_MODE_INFINITE') {
    return isSalesObjective ? 'ADGROUP_BUDGET' : 'NONE';
  }
  const adsetMode = (c.adset_budget_mode || '').trim();
  if (isSalesObjective && (adsetMode === 'BUDGET_MODE_DAY' || adsetMode === 'BUDGET_MODE_TOTAL' || adsetMode === 'BUDGET_MODE_INFINITE')) {
    return 'ADGROUP_BUDGET';
  }
  return 'CAMPAIGN_BUDGET';
}

function fmtBudgetStrategy(c: CampaignFlatNode): { label: string; scope: string; amount: string } {
  const p = c.platform || 'facebook';
  if (p === 'facebook') {
    if (c.budget_scope === 'campaign') {
      const amt: string[] = [];
      if (c.daily_budget && c.daily_budget > 0) amt.push(`日 $${c.daily_budget.toFixed(2)}`);
      if (c.lifetime_budget && c.lifetime_budget > 0) amt.push(`总 $${c.lifetime_budget.toFixed(2)}`);
      return { label: 'CBO', scope: '广告系列预算', amount: amt.join(' / ') };
    }
    if (c.budget_scope === 'adset') return { label: 'ABO', scope: '广告组预算', amount: '' };
    return { label: '—', scope: '', amount: '' };
  }
  if (p === 'google') {
    if (c.daily_budget && c.daily_budget > 0) return { label: '日预算', scope: '广告系列预算', amount: `$${c.daily_budget.toFixed(2)}` };
    return { label: '—', scope: '', amount: '' };
  }
  if (p === 'tiktok') {
    const strategy = resolveTikTokListBudgetStrategy(c);
    if (strategy === 'NONE') {
      return { label: '不限', scope: '', amount: '' };
    }
    if (strategy === 'ADGROUP_BUDGET') {
      const adsetMode = (c.adset_budget_mode || '').trim();
      if (adsetMode === 'BUDGET_MODE_DAY') {
        return { label: '日预算', scope: '广告组预算', amount: c.adset_budget && c.adset_budget > 0 ? `$${c.adset_budget.toFixed(2)}` : '' };
      }
      if (adsetMode === 'BUDGET_MODE_TOTAL') {
        return { label: '总预算', scope: '广告组预算', amount: c.adset_budget && c.adset_budget > 0 ? `$${c.adset_budget.toFixed(2)}` : '' };
      }
      return { label: '不限', scope: '广告组预算', amount: '' };
    }
    if (strategy === 'CAMPAIGN_BUDGET_OPTIMIZE') {
      const label = c.budget_scope === 'BUDGET_MODE_TOTAL' ? '总预算' : '日预算';
      return { label, scope: '广告系列预算', amount: c.daily_budget && c.daily_budget > 0 ? `$${c.daily_budget.toFixed(2)}` : '' };
    }
    if (strategy === 'CAMPAIGN_BUDGET') {
      const label = c.budget_scope === 'BUDGET_MODE_TOTAL' ? '总预算' : '日预算';
      return { label, scope: '广告系列预算', amount: c.daily_budget && c.daily_budget > 0 ? `$${c.daily_budget.toFixed(2)}` : '' };
    }
    return { label: strategy || '—', scope: '', amount: '' };
  }
  return { label: '—', scope: '', amount: '' };
}

function fmtAdSetBudget(adset: AdSetFlatNode): { label: string; amount: string } {
  const p = adset.platform || 'facebook';
  if (p === 'facebook') {
    const budgetType = (adset.budget_type || '').trim();
    const daily = Number(adset.daily_budget || 0);
    const lifetime = Number(adset.lifetime_budget || 0);
    if (budgetType === 'lifetime' || lifetime > 0) {
      return lifetime > 0 ? { label: '总预算', amount: fmtMoney(lifetime) } : { label: '总预算', amount: '' };
    }
    if (budgetType === 'daily' || daily > 0) {
      return daily > 0 ? { label: '日预算', amount: fmtMoney(daily) } : { label: '日预算', amount: '' };
    }
    return { label: '—', amount: '' };
  }
  if (p === 'tiktok') {
    const mode = (adset.adset_budget_mode || '').trim();
    const amount = Number(adset.adset_budget || 0);
    if (mode === 'BUDGET_MODE_INFINITE') return { label: '不限', amount: '' };
    if (mode === 'BUDGET_MODE_TOTAL') return { label: '总预算', amount: amount > 0 ? fmtMoney(amount) : '' };
    if (mode === 'BUDGET_MODE_DAY' || mode === 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET') return { label: '日预算', amount: amount > 0 ? fmtMoney(amount) : '' };
    if (amount > 0) return { label: '预算', amount: fmtMoney(amount) };
    return { label: '—', amount: '' };
  }
  return { label: '—', amount: '' };
}

function fmtSpendCap(c: CampaignFlatNode): string {
  const p = c.platform || 'facebook';
  if (p === 'facebook') {
    return c.spend_cap && c.spend_cap > 0 ? `$${c.spend_cap.toFixed(2)}` : '—';
  }
  if (p === 'google') {
    return c.daily_budget && c.daily_budget > 0 ? `$${c.daily_budget.toFixed(2)}` : '—';
  }
  if (p === 'tiktok') {
    return c.daily_budget && c.daily_budget > 0 ? `$${c.daily_budget.toFixed(2)}` : '—';
  }
  return '—';
}

// ── Edit modal option constants ──────────────────────────────────────────────
const EDIT_SPECIAL_AD_CATEGORIES = [
  { value: 'HOUSING', label: '住房' },
  { value: 'EMPLOYMENT', label: '就业' },
  { value: 'CREDIT', label: '信贷' },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: '社会议题/选举/政治' },
];
const EDIT_BILLING_EVENTS = [
  { value: 'IMPRESSIONS', label: '展示 (CPM)' },
  { value: 'LINK_CLICKS', label: '链接点击 (CPC)' },
  { value: 'POST_ENGAGEMENT', label: '帖子互动' },
  { value: 'THRUPLAY', label: '视频播放 (ThruPlay)' },
];
const EDIT_OPTIMIZATION_GOALS = [
  { value: 'OFFSITE_CONVERSIONS', label: '网站转化' },
  { value: 'LINK_CLICKS', label: '链接点击' },
  { value: 'LANDING_PAGE_VIEWS', label: '落地页浏览' },
  { value: 'IMPRESSIONS', label: '曝光' },
  { value: 'REACH', label: '触达' },
  { value: 'VALUE', label: '转化价值 (ROAS)' },
  { value: 'POST_ENGAGEMENT', label: '帖子互动' },
  { value: 'LEAD_GENERATION', label: '潜在客户' },
  { value: 'APP_INSTALLS', label: 'App 安装' },
];
const EDIT_BID_STRATEGIES = [
  { value: 'LOWEST_COST_WITHOUT_CAP', label: '最低费用 (自动)' },
  { value: 'LOWEST_COST_WITH_BID_CAP', label: '出价上限' },
  { value: 'COST_CAP', label: '费用上限' },
];
const FALLBACK_GOAL_LABELS: Record<string, string> = {
  LINK_CLICKS: '链接点击量最大化',
  IMPRESSIONS: '展示次数最大化',
  REACH: '广告覆盖人数最大化',
  LANDING_PAGE_VIEWS: '落地页浏览量最大化',
  OFFSITE_CONVERSIONS: '转化量最大化',
  VALUE: '转化价值最大化',
};
const EDIT_OBJECTIVE_LABEL_MAP = Object.fromEntries(
  Object.entries(OBJECTIVE_STYLE).map(([value, meta]) => [value, meta.label] as const),
) as Record<string, string>;
const EDIT_OPTIMIZATION_GOAL_LABEL_MAP = Object.fromEntries(
  EDIT_OPTIMIZATION_GOALS.map((item) => [item.value, item.label] as const),
) as Record<string, string>;
const EDIT_BID_STRATEGY_LABEL_MAP = Object.fromEntries(
  EDIT_BID_STRATEGIES.map((item) => [item.value, item.label] as const),
) as Record<string, string>;
const SAVED_VIEW_ALL_NAME = '全部';
const METRIC_FILTER_OPTIONS: Array<{ value: MetricFilterRule['metric']; label: string }> = [
  { value: 'spend', label: '花费' },
  { value: 'roas', label: 'ROAS' },
  { value: 'cpc', label: 'CPC' },
  { value: 'cpm', label: 'CPM' },
  { value: 'cpa', label: 'CPA' },
  { value: 'purchase_count', label: '购买数量' },
  { value: 'cta_registration', label: 'CTA注册次数（网站）' },
  { value: 'cta_purchase', label: 'CTA付费量（网站）' },
  { value: 'cta_purchase_value', label: 'CTA付费价值（网站）' },
  { value: 'cta_purchase_roas', label: 'CTA付费ROAS（网站）' },
];
const TIKTOK_CTA_METRIC_COLUMNS: Array<{ label: string; field: MetricSortFieldName; format: 'count' | 'money' | 'roas' }> = [
  { label: 'CTA注册次数', field: 'cta_registration' as MetricSortFieldName, format: 'count' },
  { label: 'CTA付费量', field: 'cta_purchase' as MetricSortFieldName, format: 'count' },
  { label: 'CTA付费价值', field: 'cta_purchase_value' as MetricSortFieldName, format: 'money' },
  { label: 'CTA付费ROAS', field: 'cta_purchase_roas' as MetricSortFieldName, format: 'roas' },
];
const METRIC_OPERATOR_OPTIONS: Array<{ value: MetricFilterOperator; label: string }> = [
  { value: 'between', label: '介于' },
  { value: 'lt', label: '小于' },
  { value: 'gt', label: '大于' },
];

function formatCampaignObjectiveLabel(value?: string | null): string {
  const normalized = String(value || '').trim();
  if (!normalized) return '—';
  return EDIT_OBJECTIVE_LABEL_MAP[normalized] || normalized;
}

function formatOptimizationGoalLabel(value?: string | null, fallback?: string | null): string {
  const normalized = String(value || '').trim();
  if (normalized) return FALLBACK_GOAL_LABELS[normalized] || EDIT_OPTIMIZATION_GOAL_LABEL_MAP[normalized] || normalized;
  const fallbackValue = String(fallback || '').trim();
  return fallbackValue || '—';
}

function formatBidStrategyLabel(value?: string | null): string {
  const normalized = String(value || '').trim();
  if (!normalized) return '—';
  return EDIT_BID_STRATEGY_LABEL_MAP[normalized] || normalized;
}

function formatOptimizationPlacementLabel(value?: string | null): string {
  const normalized = String(value || '').trim();
  if (!normalized) return '—';
  const mapping: Record<string, string> = {
    WEBSITE: '优化位置：网站',
    APP: '优化位置：应用',
    MESSENGER: '优化位置：Messenger',
    INSTAGRAM_DIRECT: '优化位置：Instagram Direct',
    WHATSAPP: '优化位置：WhatsApp',
  };
  return mapping[normalized] || `优化位置：${normalized}`;
}

function formatScheduleSummary(start?: string | null, end?: string | null): { start: string; end: string } {
  const startText = fmtListDateTime(start ?? undefined) || '—';
  const endText = String(end || '').trim() ? fmtListDateTime(end ?? undefined) : `${startText} - 长期`;
  return { start: startText, end: endText };
}
const EDIT_PUBLISHER_PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'audience_network', label: 'Audience Network' },
  { value: 'messenger', label: 'Messenger' },
];
const EDIT_FB_POSITIONS = [
  { value: 'feed', label: '动态' },
  { value: 'right_hand_column', label: '右栏' },
  { value: 'story', label: 'Stories' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'instream_video', label: '插播视频' },
  { value: 'search', label: '搜索' },
];
const EDIT_IG_POSITIONS = [
  { value: 'stream', label: '信息流' },
  { value: 'story', label: 'Stories' },
  { value: 'explore', label: '探索' },
  { value: 'reels', label: 'Reels' },
];
const EDIT_CTA_TYPES = [
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'GET_OFFER', label: 'Get Offer' },
  { value: 'BUY_NOW', label: 'Buy Now' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'BOOK_TRAVEL', label: 'Book Travel' },
  { value: 'ORDER_NOW', label: 'Order Now' },
];

const EMPTY_EDIT_CAMPAIGN: FacebookEditCampaignPayload = {
  name: '',
  daily_budget: 0,
  campaign_lifetime_budget: 0,
  spend_cap: 0,
  special_ad_categories: [],
};

const EMPTY_EDIT_ADSET: FacebookEditAdSetPayload = {
  name: '',
  budget_type: 'daily',
  adset_daily_budget: 0,
  adset_lifetime_budget: 0,
  billing_event: '',
  optimization_goal: '',
  bid_strategy: '',
  bid_amount: 0,
  start_time: '',
  end_time: '',
  countries: [],
  genders: [],
  age_min: 18,
  age_max: 65,
  custom_audience_ids: [],
  excluded_custom_audience_ids: [],
  publisher_platforms: [],
  facebook_positions: [],
  instagram_positions: [],
  device_platforms: [],
  user_os: [],
};

const EMPTY_EDIT_AD: FacebookEditAdPayload = {
  name: '',
  ad_type: 'image',
  page_id: '',
  image_hash: '',
  video_id: '',
  thumb_hash: '',
  message: '',
  headline: '',
  description: '',
  link: '',
  caption: '',
  cta_type: '',
  url_tags: '',
};

const EMPTY_TIKTOK_EDIT_CAMPAIGN: TikTokEditCampaignPayload = {
  name: '',
  objective_type: '',
  budget_strategy: '',
  budget_mode: '',
  budget: 0,
  operation_status: '',
  special_industries: [],
  roas_bid: 0,
};

const EMPTY_TIKTOK_EDIT_ADSET: TikTokEditAdSetPayload = {
  name: '',
  adgroup_status: '',
  budget_mode: '',
  budget: 0,
  schedule_type: '',
  start_time: '',
  end_time: '',
  placement_type: '',
  placements: [],
  optimization_goal: '',
  bid_type: '',
  bid_price: 0,
  billing_event: '',
  pixel_id: '',
  optimization_event: '',
  audience_ids: [],
  excluded_audience_ids: [],
  location_ids: [],
  age_groups: [],
  gender: '',
  languages: [],
  interest_category_ids: [],
  video_action_category_ids: [],
  interest_behavior_items: [],
  operating_systems: [],
  network_types: [],
  identity_type: '',
  identity_id: '',
  creative_material_mode: '',
  app_id: '',
  promotion_type: '',
};

const EMPTY_TIKTOK_EDIT_AD: TikTokEditAdPayload = {
  name: '',
  ad_status: '',
  ad_format: '',
  ad_text: '',
  call_to_action: '',
  landing_page_url: '',
  video_id: '',
  display_name: '',
  identity_type: '',
  identity_id: '',
  tiktok_item_id: '',
};

/** 表格操作列：紧凑文字按钮 */
function opsTextStyle(isMobile: boolean, extra?: CSSProperties): CSSProperties {
  return { fontSize: isMobile ? 10 : 11, padding: '1px 5px', whiteSpace: 'nowrap', ...extra };
}

const EFFECTIVE_STATUS_MAP: Record<string, { text: string; color: string }> = {
  // ── 通用 ──
  ACTIVE: { text: '投放中', color: 'var(--green)' },
  PAUSED: { text: '已暂停', color: 'var(--text3)' },
  DELETED: { text: '已删除', color: 'var(--red, #ef4444)' },
  ARCHIVED: { text: '已归档', color: 'var(--text3)' },
  IN_PROCESS: { text: '处理中', color: 'var(--amber)' },
  WITH_ISSUES: { text: '未投放', color: 'var(--red, #ef4444)' },
  PENDING_REVIEW: { text: '审核中', color: 'var(--amber)' },
  DISAPPROVED: { text: '未投放', color: 'var(--red, #ef4444)' },
  PREAPPROVED: { text: '审核中', color: 'var(--amber)' },
  PENDING_BILLING_INFO: { text: '未投放', color: 'var(--amber)' },
  CAMPAIGN_PAUSED: { text: '已暂停', color: 'var(--text3)' },
  ADSET_PAUSED: { text: '已暂停', color: 'var(--text3)' },
  ENABLED: { text: '投放中', color: 'var(--green)' },
  ENABLE: { text: '投放中', color: 'var(--green)' },
  DISABLE: { text: '已暂停', color: 'var(--text3)' },
  // ── TikTok 标准化状态 ──
  IN_REVIEW: { text: '审核中', color: 'var(--amber)' },
  BUDGET_EXCEEDED: { text: '预算已用尽', color: 'var(--red, #ef4444)' },
  PENDING: { text: '未投放', color: 'var(--text3)' },
  NOT_DELIVERING: { text: '未投放', color: 'var(--text3)' },
  STATUS_DELIVERY_OK: { text: '投放中', color: 'var(--green)' },
  STATUS_DISABLE: { text: '已暂停', color: 'var(--text3)' },
  STATUS_NOT_DELIVERY: { text: '未投放', color: 'var(--text3)' },
  STATUS_DELETE: { text: '已删除', color: 'var(--red, #ef4444)' },
  STATUS_TIME_DONE: { text: '已完成', color: 'var(--text3)' },
  STATUS_FROZEN: { text: '已终止', color: 'var(--text3)' },
  STATUS_RF_CLOSED: { text: '已关闭', color: 'var(--text3)' },
  // ── TikTok 原始 secondary_status（未被后端归一化的值）──
  CAMPAIGN_STATUS_ENABLE: { text: '投放中', color: 'var(--green)' },
  CAMPAIGN_STATUS_DISABLE: { text: '已暂停', color: 'var(--text3)' },
  CAMPAIGN_STATUS_DELETE: { text: '已删除', color: 'var(--red, #ef4444)' },
  CAMPAIGN_STATUS_BUDGET_EXCEED: { text: '预算已用尽', color: 'var(--red, #ef4444)' },
  CAMPAIGN_STATUS_NOT_START: { text: '待投放', color: 'var(--text3)' },
  CAMPAIGN_STATUS_ADVERTISER_AUDIT: { text: '审核中', color: 'var(--amber)' },
  CAMPAIGN_STATUS_ADVERTISER_AUDIT_DENY: { text: '审核未通过', color: 'var(--red, #ef4444)' },
  ADGROUP_STATUS_DELIVERY_OK: { text: '投放中', color: 'var(--green)' },
  ADGROUP_STATUS_NOT_DELIVER: { text: '未投放', color: 'var(--text3)' },
  ADGROUP_STATUS_CAMPAIGN_DISABLE: { text: '广告系列已暂停', color: 'var(--text3)' },
  ADGROUP_STATUS_DELETE: { text: '已删除', color: 'var(--red, #ef4444)' },
  ADGROUP_STATUS_AUDIT: { text: '审核中', color: 'var(--amber)' },
  ADGROUP_STATUS_AUDIT_DENY: { text: '审核未通过', color: 'var(--red, #ef4444)' },
  ADGROUP_STATUS_BUDGET_EXCEED: { text: '预算已用尽', color: 'var(--red, #ef4444)' },
  ADGROUP_STATUS_FROZEN: { text: '已冻结', color: 'var(--text3)' },
  AD_STATUS_DELIVERY_OK: { text: '投放中', color: 'var(--green)' },
  AD_STATUS_NOT_DELIVER: { text: '未投放', color: 'var(--text3)' },
  AD_STATUS_CAMPAIGN_DISABLE: { text: '广告系列已暂停', color: 'var(--text3)' },
  AD_STATUS_ADGROUP_DISABLE: { text: '广告组已暂停', color: 'var(--text3)' },
  AD_STATUS_DELETE: { text: '已删除', color: 'var(--red, #ef4444)' },
  AD_STATUS_AUDIT: { text: '审核中', color: 'var(--amber)' },
  AD_STATUS_AUDIT_DENY: { text: '审核未通过', color: 'var(--red, #ef4444)' },
  AD_STATUS_FROZEN: { text: '已冻结', color: 'var(--text3)' },
  AD_STATUS_LIVE_PENDING: { text: '待投放', color: 'var(--amber)' },
  // ── Google primary_status / status ──
  ELIGIBLE: { text: '符合条件', color: 'var(--green)' },
  NOT_ELIGIBLE: { text: '无效', color: 'var(--red, #ef4444)' },
  ENDED: { text: '已结束', color: 'var(--text3)' },
  REMOVED: { text: '已移除', color: 'var(--red, #ef4444)' },
  LIMITED: { text: '有效（受限）', color: 'var(--amber)' },
  LEARNING: { text: '有效（正在学习）', color: 'var(--amber)' },
  MISCONFIGURED: { text: '有效（配置有误）', color: 'var(--red, #ef4444)' },
  // ── Google 广告审核状态 ──
  APPROVED: { text: '已通过', color: 'var(--green)' },
  APPROVED_LIMITED: { text: '有限通过', color: 'var(--amber)' },
  REVIEW_IN_PROGRESS: { text: '审核中', color: 'var(--amber)' },
  UNDER_REVIEW: { text: '审核中', color: 'var(--amber)' },
};
const SECONDARY_STATUS_MAP: Record<string, string> = {
  SUCCESS: '',
  LEARNING: '学习中',
  FAIL: '学习受限',
  WITH_ISSUES: '投放异常',
  DISAPPROVED: '审核未通过',
  PENDING_BILLING_INFO: '账单信息不完整或异常',
  PREAPPROVED: '预审核通过',
  CAMPAIGN_PAUSED: '广告系列已暂停',
  ADSET_PAUSED: '广告组已暂停',
  CAMPAIGN_STATUS_ENABLE: '',
  CAMPAIGN_STATUS_DISABLE: '',
  CAMPAIGN_STATUS_DELETE: '',
  CAMPAIGN_STATUS_TTS_TT_ASSET_UNAVAILABLE: '素材不可用',
  CAMPAIGN_STATUS_TTS_TT_IDENTITY_UNAVAILABLE: 'TikTok账号不可用',
  CAMPAIGN_STATUS_TIKTOK_SHOP_UNAVAILABLE: '商店不可用',
  CAMPAIGN_STATUS_REVIEW_DISAPPROVED: '审核失败',
  CAMPAIGN_STATUS_AD_UNAVAILABLE: '广告不可用',
  CAMPAIGN_STATUS_PRODUCT_UNAVAILABLE: '商品不可用',
  CAMPAIGN_STATUS_ADVERTISER_AUDIT_DENY: '广告主账号审核未通过',
  CAMPAIGN_STATUS_ADVERTISER_AUDIT: '广告主账号审核中',
  ADVERTISER_CONTRACT_PENDING: '合同未生效',
  ADVERTISER_ACCOUNT_PUNISH: '广告主账户被惩罚',
  CAMPAIGN_STATUS_BUDGET_EXCEED: '推广系列超出预算',
  CAMPAIGN_STATUS_IDENTITY_USED_BY_GMV_MAX_AD: '发布身份被占用',
  CAMPAIGN_STATUS_TTS_ACCOUNT_BALANCE_INSUFFICIENT: '广告账号余额不足',
  CAMPAIGN_STATUS_LIVE_GMV_MAX_AUTHORIZATION_CANCEL: '账号 GMV Max 已授权其他广告账号',
  CAMPAIGN_STATUS_PRODUCT_GMV_MAX_AUTHORIZATION_CANCEL: '店铺 GMV Max 已授权其他广告账号',
  CAMPAIGN_STATUS_IDENTITY_USED_BY_LIVE_GMV_MAX: '账号已有直播 GMV Max 投放中',
  CAMPAIGN_STATUS_PRODUCT_USED_BY_PRODUCT_GMV_MAX: '店铺已有商品 GMV Max 投放中',
  CAMPAIGN_STATUS_AWAITING_RELEASE: '等待释放',
  ADGROUP_STATUS_ADGROUP_PRE_ONLINE: '',
  ADGROUP_STATUS_IDENTITY_USED_BY_GMV_MAX_AD: '账号已授权其他广告账号',
  ADGROUP_STATUS_CAMPAIGN_DELETE: '推广系列已删除',
  ADGROUP_STATUS_DELETE: '',
  ADGROUP_STATUS_CAMPAIGN_DISABLE: '推广系列已暂停',
  ADGROUP_STATUS_DISABLE: '',
  ADGROUP_STATUS_ADVERTISER_AUDIT_DENY: '广告主账号审核未通过',
  ADGROUP_STATUS_ADVERTISER_AUDIT: '广告主账号审核中',
  ADGROUP_STATUS_BALANCE_EXCEED: '账户余额不足/付费失败',
  ADGROUP_STATUS_CREATE: '暂无创意',
  ADGROUP_STATUS_TRANSCODING_FAIL: '无法转换视频',
  ADGROUP_STATUS_AUDIT: '新建审核中',
  ADGROUP_STATUS_REAUDIT: '修改审核中',
  ADGROUP_STATUS_AUDIT_DENY: '审核未通过',
  ADGROUP_STATUS_PARTIAL_AUDIT_NO_DELIVERY: '新建审核中',
  ADGROUP_STATUS_LIVE_NOT_START: 'LIVE尚未开始',
  ADGROUP_STATUS_INDUSTRY_QUALIFICATION_MISSING: '待补充资质',
  ADGROUP_STATUS_INDUSTRY_QUALIFICATION_EXPIRED: '资质已过期',
  ADGROUP_STATUS_INDUSTRY_QUALIFICATION_DENY: '资质不通过',
  ADGROUP_STATUS_CAMPAIGN_EXCEED: '推广系列超出预算',
  ADGROUP_STATUS_BUDGET_EXCEED: '广告组超出预算',
  ADGROUP_STATUS_FROZEN: '广告组冻结',
  ADGROUP_STATUS_NOT_START: '未在排期内',
  ADGROUP_STATUS_ASSET_AUTHORIZATION_LOST: '素材不可用或授权被撤销',
  ADGROUP_STATUS_PIXEL_UNBIND: 'Pixel解绑',
  ADGROUP_STATUS_ADGROUP_QUOTA_LIMIT: '已超出配额',
  ADGROUP_STATUS_SEARCH_KEYWORDS_IN_REVIEW: '关键词审核中',
  ADGROUP_STATUS_SEARCH_KEYWORDS_NOT_AVAILABLE: '关键词不可用',
  ADGROUP_STATUS_SEARCH_KEYWORDS_PARTIAL_APPROVED: '关键词部分通过审核',
  ADGROUP_STATUS_PARTIAL_AUDIT_DELIVERY_OK: '',
  ADGROUP_STATUS_SHADOW_ADGROUP_REAUDIT: '修改审核中',
  ADGROUP_STATUS_DELIVERY_OK: '',
  ADGROUP_STATUS_REVIEW_PARTIALLY_APPROVED: '部分广告未通过审核',
  ADGROUP_STATUS_TIME_DONE: '',
  AD_STATUS_CAMPAIGN_DELETE: '推广系列已删除',
  AD_STATUS_ADGROUP_DELETE: '广告组已删除',
  AD_STATUS_DELETE: '',
  AD_STATUS_ADVERTISER_AUDIT_DENY: '广告主账号审核未通过',
  AD_STATUS_ADVERTISER_AUDIT: '广告主账号审核中',
  AD_STATUS_LIVE_OFFLINE: '',
  AD_STATUS_BALANCE_EXCEED: '账户余额不足/付费失败',
  AD_STATUS_ADGROUP_PARTIAL_AUDIT_NO_DELIVERY: '新建审核中',
  AD_STATUS_PARTIAL_AUDIT_NO_DELIVERY: '新建审核中',
  AD_STATUS_ADGROUP_AUDIT_DENY: '审核未通过',
  AD_STATUS_ADGROUP_INDUSTRY_QUALIFICATION_MISSING: '待补充资质',
  AD_STATUS_ADGROUP_INDUSTRY_QUALIFICATION_EXPIRED: '资质已过期',
  AD_STATUS_ADGROUP_INDUSTRY_QUALIFICATION_DENY: '资质不通过',
  AD_STATUS_MUSIC_AUTHORIZATION_MISSING: '缺失音乐资质',
  AD_STATUS_TRANSCODING_FAIL: '请替换广告组中出错的视频',
  AD_STATUS_AUDIT: '新建审核中',
  AD_STATUS_REAUDIT: '修改审核中',
  AD_STATUS_AUDIT_DENY: '审核未通过',
  AD_STATUS_PROCESS_AUDIO: '音频处理中',
  AD_STATUS_CAMPAIGN_EXCEED: '推广系列超出预算',
  AD_STATUS_BUDGET_EXCEED: '广告组超出预算',
  AD_STATUS_NOT_START: '未设置排期',
  AD_STATUS_AD_PRE_ONLINE: '',
  AD_STATUS_ADGROUP_ASSET_AUTHORIZATION_LOST: '素材不可用或授权被撤销',
  AD_STATUS_ASSET_AUTHORIZATION_LOST: '素材不可用或授权被撤销',
  AD_STAUS_PIXEL_UNBIND: 'Pixel解绑',
  AD_STATUS_AD_QUOTA_LIMIT: '已超出配额',
  AD_STATUS_PRODUCT_UNAVAILABLE: '商品不可用',
  AD_STATUS_ANCHOR_UNAVAILABLE: '商品链接不可用',
  AD_STATUS_NO_AUTHORIZATION_OF_SHOWCASE: '广告发布身份不可用',
  AD_STATUS_PARTIAL_AUDIT_DELIVERY_OK: '',
  AD_STATUS_REVIEW_PARTIALLY_APPROVED: '',
  AD_STATUS_DELIVERY_OK: '',
  AD_STATUS_CAMPAIGN_DISABLE: '推广系列已暂停',
  AD_STATUS_ADGROUP_DISABLE: '广告组已暂停',
  AD_STATUS_DISABLE: '',
  AD_STATUS_DONE: '已完成',
  AD_STATUS_RF_ADGROUP_CLOSED: '广告组已关闭',
  AD_GROUPS_PAUSED: '所有广告组已暂停',
  AD_GROUP_ADS_PAUSED: '所有广告已暂停',
  APP_NOT_RELEASED: '关联应用未在目标国家发布',
  APP_PARTIALLY_RELEASED: '关联应用仅部分目标国家发布',
  ASSET_GROUPS_PAUSED: '所有素材资源组已暂停',
  BIDDING_STRATEGY_CONSTRAINED: '出价目标限制转化价值',
  BIDDING_STRATEGY_LEARNING: '出价策略学习中',
  BIDDING_STRATEGY_LIMITED: '出价策略受设置限制',
  BIDDING_STRATEGY_MISCONFIGURED: '出价策略设置错误',
  BOOKING_CANCELLED: '固定 CPM 预订已取消',
  BOOKING_HOLD_EXPIRED: '固定 CPM 库存保留已过期',
  BOOKING_HOLD_EXPIRING: '固定 CPM 库存保留即将过期',
  BUDGET_CONSTRAINED: '预算限制投放',
  BUDGET_MISCONFIGURED: '预算设置错误',
  CALL_EXTENSION_DISAPPROVED: '电话扩展审核不通过',
  CALL_EXTENSION_UNDER_REVIEW: '电话扩展审核中',
  CAMPAIGN_DRAFT: '广告系列为草稿',
  CAMPAIGN_ENDED: '广告系列已结束',
  CAMPAIGN_GROUP_ALL_GROUP_BUDGETS_ENDED: '广告系列组预算均已结束',
  CAMPAIGN_GROUP_PAUSED: '广告系列组已暂停',
  CAMPAIGN_NOT_BOOKED: '固定 CPM 广告系列未预订',
  CAMPAIGN_PENDING: '广告系列尚未开始',
  CAMPAIGN_REMOVED: '广告系列已移除',
  HAS_ADS_DISAPPROVED: '存在审核不通过的广告',
  HAS_ADS_LIMITED_BY_POLICY: '存在受政策限制的广告',
  HAS_ASSET_GROUPS_DISAPPROVED: '存在审核不通过的素材资源组',
  HAS_ASSET_GROUPS_LIMITED_BY_POLICY: '存在受政策限制的素材资源组',
  KEYWORDS_PAUSED: '所有关键词已暂停',
  LEAD_FORM_EXTENSION_DISAPPROVED: '潜在客户表单扩展审核不通过',
  LEAD_FORM_EXTENSION_UNDER_REVIEW: '潜在客户表单扩展审核中',
  MISSING_CALL_EXTENSION: '缺少电话扩展',
  MISSING_LEAD_FORM_EXTENSION: '缺少潜在客户表单扩展',
  MISSING_LOCATION_TARGETING: '缺少地理位置定位',
  MOST_ADS_UNDER_REVIEW: '大部分广告审核中',
  MOST_ASSET_GROUPS_UNDER_REVIEW: '大部分素材资源组审核中',
  NO_AD_GROUPS: '无可投放广告组',
  NO_AD_GROUP_ADS: '无可投放广告',
  NO_ASSET_GROUPS: '无可投放素材资源组',
  NO_KEYWORDS: '无可投放关键词',
  NO_MOBILE_APPLICATION_AD_GROUP_CRITERIA: '无可投放移动应用广告组条件',
  SEARCH_VOLUME_LIMITED: '搜索覆盖受限',
  AD_GROUP_PAUSED: '广告组已暂停',
  AD_GROUP_PAUSED_DUE_TO_LOW_ACTIVITY: '因长期低活跃被暂停',
  AD_GROUP_REMOVED: '广告组已移除',
  AD_GROUP_AD_APPROVED_LABELED: '广告带有限制标签',
  AD_GROUP_AD_AREA_OF_INTEREST_ONLY: '仅在兴趣区域投放',
  AD_GROUP_AD_DISAPPROVED: '广告审核不通过',
  AD_GROUP_AD_NO_ADS: '无可生成的广告实例',
  AD_GROUP_AD_PAUSED: '广告已暂停',
  AD_GROUP_AD_POOR_QUALITY: '广告质量较差',
  AD_GROUP_AD_REMOVED: '广告已移除',
  AD_GROUP_AD_UNDER_APPEAL: '广告申诉中',
  AD_GROUP_AD_UNDER_REVIEW: '广告审核中',
};

function secondaryStatusText(status?: string): string {
  const raw = String(status || '').trim();
  if (!raw) return '';
  let keys: string[] = [];
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) keys = parsed.map((x) => String(x || ''));
    } catch {
      keys = [];
    }
  }
  if (keys.length === 0) keys = raw.split(',').map((x) => x.trim());
  const texts = keys
    .map((x) => x.trim().replace(/^"|"$/g, '').toUpperCase())
    .filter(Boolean)
    .map((key) => (Object.prototype.hasOwnProperty.call(SECONDARY_STATUS_MAP, key) ? SECONDARY_STATUS_MAP[key] : EFFECTIVE_STATUS_MAP[key]?.text || key))
    .filter(Boolean);
  return Array.from(new Set(texts)).join('、');
}

function effectiveStatusSecondaryText(status?: string): string {
  const key = String(status || '').trim().toUpperCase();
  switch (key) {
    case 'WITH_ISSUES': return '投放异常';
    case 'DISAPPROVED': return '审核未通过';
    case 'PENDING_BILLING_INFO': return '账单信息不完整或异常';
    case 'PREAPPROVED': return '预审核通过';
    case 'CAMPAIGN_PAUSED': return '所在广告系列暂停';
    case 'ADSET_PAUSED': return '所在广告组暂停';
    default: return '';
  }
}

function renderStatusValue(value?: string): string {
  return value && value.trim() ? value : '';
}

function normalizeIssueField(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function buildFacebookIssuesTooltip(issuesInfo: unknown): string {
  const list = Array.isArray(issuesInfo)
    ? issuesInfo
    : issuesInfo && typeof issuesInfo === 'object'
      ? [issuesInfo as FacebookIssuesInfo]
      : [];
  const blocks = list
    .map((issue) => {
      const level = normalizeIssueField(issue.level);
      const errorType = normalizeIssueField(issue.error_type);
      const errorSummary = normalizeIssueField(issue.error_summary);
      const errorMessage = normalizeIssueField(issue.error_message);
      if (!level && !errorType && !errorSummary && !errorMessage) return '';
      return [
        level ? `等级：${level}` : '',
        errorType ? `类型：${errorType}` : '',
        errorSummary ? `摘要：${errorSummary}` : '',
        errorMessage ? `信息：${errorMessage}` : '',
      ].filter(Boolean).join('\n');
    })
    .filter(Boolean);
  return blocks.join('\n\n');
}

const statusLineStyle = (color: string, weight: number): CSSProperties => ({
  fontSize: 11,
  color,
  fontWeight: weight,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

function getOwnDeliveryStatus(
  status: number,
  publishStatus?: string,
  publishError?: string,
  effectiveStatus?: string,
  secondaryStatus?: string,
  issuesInfo?: unknown,
  _platform?: PlatformFilter | string,
): { text: string; secondaryText?: string; color: string; tooltip?: string; issueTooltip?: string; showIssueIcon?: boolean } {
  if (publishStatus === 'pending') return { text: '草稿', secondaryText: '未发布', color: 'var(--text3)' };
  if (publishStatus === 'failed') return { text: '草稿', secondaryText: '发布失败', color: 'var(--red, #ef4444)', tooltip: publishError };
  const own = status === 1
    ? { text: '投放中', color: 'var(--green)' }
    : { text: '已暂停', color: 'var(--text3)' };
  const effective = String(effectiveStatus || '').trim();
  const effectiveText = effective && (EFFECTIVE_STATUS_MAP[effective]?.text || effective);
  const secondaryText = secondaryStatusText(secondaryStatus) || effectiveStatusSecondaryText(effective);
  const issueTooltip = buildFacebookIssuesTooltip(issuesInfo);
  const showIssueIcon = !!issueTooltip && (effective.toUpperCase() === 'WITH_ISSUES' || secondaryText.includes('投放异常'));
  if (effectiveText) {
    const mapped = EFFECTIVE_STATUS_MAP[effective];
    return {
      text: effectiveText,
      secondaryText: secondaryText && secondaryText !== effectiveText ? secondaryText : '',
      color: mapped?.color || own.color,
      tooltip: issueTooltip || (secondaryText ? `一级状态：${effectiveText}；二级状态：${secondaryText}` : `一级状态：${effectiveText}；二级状态：/`),
      issueTooltip,
      showIssueIcon,
    };
  }
  if (secondaryText && secondaryText !== own.text) {
    return { ...own, secondaryText, tooltip: issueTooltip || `平台二级状态：${secondaryText}`, issueTooltip, showIssueIcon };
  }
  return { ...own, tooltip: issueTooltip || undefined, issueTooltip, showIssueIcon };
}
function isDeliveryActive(status: number, publishStatus?: string): boolean {
  return (!publishStatus || publishStatus === 'published') && status === 1;
}
function deliveryToggleStatus(status: number, publishStatus?: string): number {
  return isDeliveryActive(status, publishStatus) ? 1 : 2;
}

type EntityLevel = 'campaign' | 'adset' | 'ad';
type OperationRules = {
  toggle: boolean;
  copy: boolean;
  createChild: boolean;
  edit: boolean;
  publish: boolean;
  delete: boolean;
};

const NO_OPERATIONS: OperationRules = {
  toggle: false,
  copy: false,
  createChild: false,
  edit: false,
  publish: false,
  delete: false,
};

const DELETE_ONLY_OPERATIONS: OperationRules = {
  ...NO_OPERATIONS,
  delete: true,
};

function operationKeys(raw?: string): string[] {
  const value = String(raw || '').trim();
  if (!value || value === '/') return [];
  let parts: string[] = [];
  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) parts = parsed.map((item) => String(item || ''));
    } catch {
      parts = [];
    }
  }
  if (parts.length === 0) parts = value.split(',');
  return parts.map((item) => item.trim().replace(/^"|"$/g, '').toUpperCase()).filter(Boolean);
}

function operationRulesFor(
  level: EntityLevel,
  platform: PlatformFilter | string | undefined,
  publishStatus?: string,
  effectiveStatus?: string,
  secondaryStatus?: string,
): OperationRules {
  const isContainer = level !== 'ad';
  const base = (overrides: Partial<OperationRules>): OperationRules => ({
    ...NO_OPERATIONS,
    ...overrides,
  });
  const editable = base({ copy: true, createChild: isContainer, edit: true, delete: true });
  const toggleable = base({ ...editable, toggle: true });
  const draft = base({ ...editable, publish: true });
  const platformKey = String(platform || '').toLowerCase();
  const effective = String(effectiveStatus || '').trim().toUpperCase();
  const secondary = operationKeys(secondaryStatus);
  const hasSecondary = (...keys: string[]) => keys.some((key) => secondary.includes(key));

  if (publishStatus === 'pending' || publishStatus === 'failed') return draft;
  if (effective === 'DELETED' || effective === 'STATUS_DELETE' || effective === 'REMOVED') return NO_OPERATIONS;
  if (effective === 'IN_PROCESS') return DELETE_ONLY_OPERATIONS;
  if (effective === 'ARCHIVED') return base({ copy: true, edit: true, delete: true });

  if (platformKey === 'facebook') {
    if (effective === 'CAMPAIGN_PAUSED' || effective === 'ADSET_PAUSED') return editable;
    if (['ACTIVE', 'PAUSED', 'WITH_ISSUES', 'DISAPPROVED', 'PENDING_BILLING_INFO', 'PENDING_REVIEW', 'PREAPPROVED'].includes(effective)) {
      return toggleable;
    }
    return toggleable;
  }

  if (platformKey === 'tiktok') {
    if (
      hasSecondary(
        'CAMPAIGN_STATUS_DELETE',
        'ADGROUP_STATUS_CAMPAIGN_DELETE',
        'ADGROUP_STATUS_DELETE',
        'AD_STATUS_CAMPAIGN_DELETE',
        'AD_STATUS_ADGROUP_DELETE',
        'AD_STATUS_DELETE',
      )
    ) {
      return NO_OPERATIONS;
    }
    if (effective === 'STATUS_TIME_DONE' || hasSecondary('ADGROUP_STATUS_TIME_DONE', 'AD_STATUS_DONE')) {
      return base({ copy: true, delete: true });
    }
    if (effective === 'STATUS_FROZEN' || hasSecondary('ADGROUP_STATUS_FROZEN', 'AD_STATUS_FROZEN')) {
      return base({ copy: true, delete: true });
    }
    if (effective === 'STATUS_RF_CLOSED' || hasSecondary('AD_STATUS_RF_ADGROUP_CLOSED')) {
      return base({ copy: true, edit: true, delete: true });
    }
    if (hasSecondary('ADGROUP_STATUS_CAMPAIGN_DISABLE', 'AD_STATUS_CAMPAIGN_DISABLE', 'AD_STATUS_ADGROUP_DISABLE')) {
      return editable;
    }
    if (['STATUS_DELIVERY_OK', 'STATUS_DISABLE', 'STATUS_NOT_DELIVERY'].includes(effective)) return toggleable;
    return toggleable;
  }

  if (platformKey === 'google') {
    if (hasSecondary('CAMPAIGN_REMOVED', 'AD_GROUP_REMOVED', 'AD_GROUP_AD_REMOVED')) return NO_OPERATIONS;
    if (hasSecondary('CAMPAIGN_PAUSED', 'AD_GROUP_PAUSED')) return editable;
    if (effective === 'ENDED') return base({ copy: true, edit: true });
    return base({ toggle: effective !== 'REMOVED', copy: true, edit: true, publish: publishStatus === 'pending' || publishStatus === 'failed' });
  }

  return toggleable;
}
function getPlatformLabel(platform: PlatformFilter): string {
  switch (platform) {
    case 'facebook': return 'Facebook';
    case 'google': return 'Google';
    case 'tiktok': return 'TikTok';
    default: return '全部平台';
  }
}

function readSavedCampaignFilters(filterKey: string) {
  try {
    const navEntry = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (navEntry?.type === 'reload') {
      sessionStorage.removeItem(filterKey);
      return null;
    }
    const raw = sessionStorage.getItem(filterKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readRouteCampaignSearch(search: string): string {
  return new URLSearchParams(search).get('search')?.trim() ?? '';
}

// ── Toast icons ───────────────────────────────────────────────────────────────

function ToastIcon({ type }: { type: ToastType }) {
  const s: React.CSSProperties = { width: 16, height: 16, flexShrink: 0 };
  if (type === 'success') return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
    </svg>
  );
  if (type === 'error') return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  );
  if (type === 'warning') return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
  return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

const TOAST_COLOR: Record<ToastType, { border: string; color: string; bg: string }> = {
  success: { border: 'rgba(34,197,94,.35)', color: 'var(--green)', bg: 'rgba(34,197,94,.06)' },
  error:   { border: 'rgba(239,68,68,.35)',  color: 'var(--red)',   bg: 'rgba(239,68,68,.06)'  },
  warning: { border: 'rgba(245,158,11,.35)', color: 'var(--amber)', bg: 'rgba(245,158,11,.06)' },
  info:    { border: 'var(--border2)',        color: 'var(--text)',  bg: 'var(--surface2)'      },
};

export function Campaigns({ forcePlatform }: { forcePlatform?: string } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { platform: urlPlatform } = useParams<{ platform: string }>();
  const { brand, brandId, siteId, hasPermission, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;

  const platformFilter: PlatformFilter = (['facebook', 'tiktok', 'google'].includes(forcePlatform || urlPlatform || '') ? (forcePlatform || urlPlatform) : 'facebook') as PlatformFilter;

  // ── 筛选条件持久化（返回时恢复） ───────────────────────────────────────────
  const FILTER_KEY = `campaigns_v1_${brandId ?? 0}_${siteId ?? 0}_${platformFilter}`;
  const savedFilters = readSavedCampaignFilters(FILTER_KEY);
  const routeCampaignSearch = readRouteCampaignSearch(location.search);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const toastIdRef = useRef(0);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev.slice(-2), { id, message, type }]);
    const ttl = type === 'error' ? 6000 : type === 'warning' ? 5000 : 3500;
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), ttl);
  }, []);

  // ── Tab & drill-down ──────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabType>(
    routeCampaignSearch
      ? 'campaign'
      : savedFilters?.activeTab === 'campaign' || savedFilters?.activeTab === 'adset' || savedFilters?.activeTab === 'ad'
      ? savedFilters.activeTab
      : 'campaign',
  );
  const [filterCampaign, setFilterCampaign] = useState<{ id: number; name: string; publishStatus?: string } | null>(
    savedFilters?.filterCampaign && typeof savedFilters.filterCampaign.id === 'number'
      ? savedFilters.filterCampaign
      : null,
  );
  const [filterAdset, setFilterAdset] = useState<{ id: number; name: string; publishStatus?: string } | null>(
    savedFilters?.filterAdset && typeof savedFilters.filterAdset.id === 'number'
      ? savedFilters.filterAdset
      : null,
  );

  // ── Global filters ────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(savedFilters?.statusFilter ?? 'all');
  const [objectiveFilter, setObjectiveFilter] = useState(savedFilters?.objectiveFilter ?? '');
  const [bidStrategyFilter, setBidStrategyFilter] = useState(savedFilters?.bidStrategyFilter ?? '');
  const [size, setSize] = useState<number>(savedFilters?.size ?? 20);
  const [accountOptions, setAccountOptions] = useState<FilterAccountOption[]>([]);
  const [selectedAccountIDs, setSelectedAccountIDs] = useState<string[]>(savedFilters?.selectedAccountIDs ?? []);

  // Convenience: single string for API calls (comma-separated or undefined)
  const selectedAccountID = selectedAccountIDs.length > 0 ? selectedAccountIDs.join(',') : '';

  // ── Campaign tab ──────────────────────────────────────────────────────────
  const [campaigns, setCampaigns] = useState<CampaignFlatNode[]>([]);
  const [campaignTotal, setCampaignTotal] = useState(0);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignTotalPage, setCampaignTotalPage] = useState(0);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState(routeCampaignSearch);
  const [campaignSummary, setCampaignSummary] = useState<FlatListSummary>(EMPTY_FLAT_LIST_SUMMARY);

  // ── AdSet tab ─────────────────────────────────────────────────────────────
  const [adsets, setAdsets] = useState<AdSetFlatNode[]>([]);
  const [adsetTotal, setAdsetTotal] = useState(0);
  const [adsetPage, setAdsetPage] = useState(1);
  const [adsetTotalPage, setAdsetTotalPage] = useState(0);
  const [adsetLoading, setAdsetLoading] = useState(false);
  const [adsetSearch, setAdsetSearch] = useState('');
  const [adsetSummary, setAdsetSummary] = useState<FlatListSummary>(EMPTY_FLAT_LIST_SUMMARY);

  // ── Ad tab ────────────────────────────────────────────────────────────────
  const [adList, setAdList] = useState<AdFlatNode[]>([]);
  const [adTotal, setAdTotal] = useState(0);
  const [adPage, setAdPage] = useState(1);
  const [adTotalPage, setAdTotalPage] = useState(0);
  const [adLoading, setAdLoading] = useState(false);
  const [adSearch, setAdSearch] = useState('');
  const [adSummary, setAdSummary] = useState<FlatListSummary>(EMPTY_FLAT_LIST_SUMMARY);

  // ── Search input (local, committed on Enter) ──────────────────────────────
  const [campaignSearchInput, setCampaignSearchInput] = useState(routeCampaignSearch);
  const [adsetSearchInput, setAdsetSearchInput] = useState('');
  const [adSearchInput, setAdSearchInput] = useState('');

  // ── Metrics date range (passed to list API) ──────────────────────────────
  const [metricsDateRange, setMetricsDateRange] = useState<string>(savedFilters?.metricsDateRange ?? '30d');
  const [customStartDate, setCustomStartDate] = useState<string>(savedFilters?.customStartDate ?? '');
  const [customEndDate, setCustomEndDate] = useState<string>(savedFilters?.customEndDate ?? '');
  const [metricFilters, setMetricFilters] = useState<MetricFilterRule[]>(Array.isArray(savedFilters?.metricFilters) ? savedFilters.metricFilters : []);
  const [metricFilterMetric, setMetricFilterMetric] = useState<MetricFilterRule['metric']>('spend');
  const [metricFilterOperator, setMetricFilterOperator] = useState<MetricFilterOperator>('between');
  const [metricFilterValue, setMetricFilterValue] = useState('');
  const [metricFilterValue2, setMetricFilterValue2] = useState('');
  const [savedViews, setSavedViews] = useState<SavedViewItemResponse[]>([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(false);
  const [selectedSavedViewName, setSelectedSavedViewName] = useState(savedFilters?.selectedSavedViewName ?? SAVED_VIEW_ALL_NAME);
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [saveViewMode, setSaveViewMode] = useState<'overwrite' | 'new'>('new');
  const [saveViewNameInput, setSaveViewNameInput] = useState('');
  const [saveViewSubmitting, setSaveViewSubmitting] = useState(false);
  const [showMetricFilterPanel, setShowMetricFilterPanel] = useState(false);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const [sortField, setSortField] = useState<MetricSortField>(savedFilters?.sortField ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(savedFilters?.sortDir ?? 'desc');

  // ── Action state ──────────────────────────────────────────────────────────
  const [highlightAdID, setHighlightAdID] = useState<number | null>(null);
  const [togglingIDs, setTogglingIDs] = useState<Record<string, boolean>>({});
  const [retryingAdIDs, setRetryingAdIDs] = useState<Record<number, boolean>>({});
  const [deletingAdIDs, setDeletingAdIDs] = useState<Record<string | number, boolean>>({});
  const [copyingKey, setCopyingKey] = useState<string | null>(null);
  const [selectedIDsByTab, setSelectedIDsByTab] = useState<Record<TabType, number[]>>({
    campaign: [],
    adset: [],
    ad: [],
  });
  const [showBatchTagModal, setShowBatchTagModal] = useState(false);
  const [batchTagForm, setBatchTagForm] = useState<BatchTagFormState>({});
  const [batchTagFormsByLevel, setBatchTagFormsByLevel] = useState<BatchTagFormStateMap>({
    campaign: {},
    adset: {},
    ad: {},
  });
  const [batchTagSubmitting, setBatchTagSubmitting] = useState(false);
  const [batchTagDefs, setBatchTagDefs] = useState<TagDefinition[]>([]);
  const [batchTagDefsByLevel, setBatchTagDefsByLevel] = useState<BatchTagDefsByLevel>({
    campaign: [],
    adset: [],
    ad: [],
  });

  // ── AI Diagnosis modal state ─────────────────────────────────────────────
  const [diagAd, setDiagAd] = useState<AdFlatNode | null>(null);
  const [diagMetrics, setDiagMetrics] = useState<{ spend: number; roas: number; cpc: number; cpm: number; cpa: number; purchase_count: number } | null>(null);
  const [copyModal, setCopyModal] = useState<CopyModalState>(null);
  const [copyAccountOptions, setCopyAccountOptions] = useState<FilterAccountOption[]>([]);
  const [copyTargetCampaigns, setCopyTargetCampaigns] = useState<CampaignFlatNode[]>([]);
  const [copyTargetAdSets, setCopyTargetAdSets] = useState<AdSetFlatNode[]>([]);
  const [copyLoadingTargets, setCopyLoadingTargets] = useState(false);
  const [editModal, setEditModal] = useState<EditModalState>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editGeoLocations, setEditGeoLocations] = useState<FacebookGeoLocation[]>([]);
  const [editGeoLoading, setEditGeoLoading] = useState(false);
  const [editGeoFilter, setEditGeoFilter] = useState('');
  const [editGeoDropdownOpen, setEditGeoDropdownOpen] = useState(false);
  const editGeoDropdownRef = useRef<HTMLDivElement>(null);
  const [editCustomAudiences, setEditCustomAudiences] = useState<FacebookCustomAudience[]>([]);
  const [editCustomAudiencesLoading, setEditCustomAudiencesLoading] = useState(false);
  const [editIncludeAudienceFilter, setEditIncludeAudienceFilter] = useState('');
  const [editExcludeAudienceFilter, setEditExcludeAudienceFilter] = useState('');

  // ── Create wizard ─────────────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [publishing, setPublishing] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templates, setTemplates] = useState<FacebookCampaignTemplate[]>([]);
  const [selectedTemplateID, setSelectedTemplateID] = useState<number | null>(null);
  const [targetCampaigns, setTargetCampaigns] = useState<CampaignNode[]>([]);
  const [targetLoading, setTargetLoading] = useState(false);
  const [materials, setMaterials] = useState<FacebookMaterial[]>([]);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialSearch, setMaterialSearch] = useState('');
  const [selectedMaterialID, setSelectedMaterialID] = useState('');
  const [showUploadMaterialModal, setShowUploadMaterialModal] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMaterialType, setUploadMaterialType] = useState<'video' | 'image'>('video');
  const [uploadMaterialName, setUploadMaterialName] = useState('');
  const [uploadMaterialFile, setUploadMaterialFile] = useState<File | null>(null);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableCardRef = useRef<HTMLDivElement>(null);
  const summaryBarRef = useRef<HTMLDivElement>(null);
  const isResizingCol = useRef(false);
  const isMobile = useIsMobile();
  const [summaryViewportBounds, setSummaryViewportBounds] = useState<{ left: number; right: number }>({ left: 0, right: 0 });
  const [isSummaryFloating, setIsSummaryFloating] = useState(true);
  const isSummaryFloatingRef = useRef(true);
  useEffect(() => { isSummaryFloatingRef.current = isSummaryFloating; }, [isSummaryFloating]);
  const [nameColWidth, setNameColWidth] = useState(() => readStoredNameColWidthPx(isMobile));
  const nameColWidthRef = useRef(nameColWidth);
  useEffect(() => { nameColWidthRef.current = nameColWidth; }, [nameColWidth]);

  useEffect(() => {
    setNameColWidth((w) => (isMobile
      ? Math.min(NAME_COL_MOBILE_MAX_W, Math.max(NAME_COL_MOBILE_MIN_W, w))
      : Math.min(NAME_COL_MAX_W, Math.max(NAME_COL_MIN_W, w))));
  }, [isMobile]);

  const persistNameColWidth = useCallback(() => {
    try { localStorage.setItem(NAME_COL_LS_KEY, String(nameColWidthRef.current)); } catch { /* ignore */ }
  }, []);

  const onNameColResizeStart = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const min = isMobile ? NAME_COL_MOBILE_MIN_W : NAME_COL_MIN_W;
    const max = isMobile ? NAME_COL_MOBILE_MAX_W : NAME_COL_MAX_W;
    const startX = e.clientX;
    const startW = nameColWidthRef.current;
    const prevSelect = document.body.style.userSelect;
    const prevPointer = document.body.style.pointerEvents;
    isResizingCol.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    const onMove = (ev: globalThis.MouseEvent) => {
      const next = Math.min(max, Math.max(min, startW + (ev.clientX - startX)));
      nameColWidthRef.current = next;
      setNameColWidth(next);
    };
    const onUp = () => {
      isResizingCol.current = false;
      document.body.style.userSelect = prevSelect;
      document.body.style.cursor = '';
      document.body.style.pointerEvents = prevPointer;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      persistNameColWidth();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [isMobile, persistNameColWidth]);

  const onNameColResizeTouchStart = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    e.stopPropagation();
    const min = isMobile ? NAME_COL_MOBILE_MIN_W : NAME_COL_MIN_W;
    const max = isMobile ? NAME_COL_MOBILE_MAX_W : NAME_COL_MAX_W;
    const startX = e.touches[0].clientX;
    const startW = nameColWidthRef.current;
    isResizingCol.current = true;
    const onMove = (ev: TouchEvent) => {
      if (ev.touches.length !== 1) return;
      ev.preventDefault();
      const next = Math.min(max, Math.max(min, startW + (ev.touches[0].clientX - startX)));
      nameColWidthRef.current = next;
      setNameColWidth(next);
    };
    const onEnd = () => {
      isResizingCol.current = false;
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
      persistNameColWidth();
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
  }, [isMobile, persistNameColWidth]);

  // 移动端列宽缩减，并取消左右 sticky（防止固定列遮盖所有可见区域）；名称列宽度仍跟状态走
  const W_NAME = isMobile
    ? Math.min(NAME_COL_MOBILE_MAX_W, Math.max(NAME_COL_MOBILE_MIN_W, nameColWidth))
    : Math.min(NAME_COL_MAX_W, Math.max(NAME_COL_MIN_W, nameColWidth));
  const W_TOGGLE = isMobile ?  40 :  44;
  const W_STATUS = isMobile ?  70 :  90;
  /** 操作列：各 tab 统一宽度（文字按钮 + 换行），与广告组一致 */
  const W_OPS = isMobile ? 112 : 176;
  const W_METRIC = isMobile ?  80 : 100;
  const W_GROUP = isMobile ? 52 : 76;
  const W_ADSET_BUDGET = isMobile ? 116 : 124;
  const W_ADSET_GOAL = isMobile ? 124 : 138;
  const W_ADSET_SCHEDULE = isMobile ? 118 : 132;
  const W_ADSET_BID_STRATEGY = isMobile ? 124 : 138;
  const W_CAMPAIGN_BID_STRATEGY = isMobile ? 112 : 124;
  const colSticky = isMobile ? undefined : ('sticky' as const);

  // ── 隐藏列 ───────────────────────────────────────────────────────────────

  // 从 localStorage 读取各 tab 的隐藏列配置
  const loadHiddenCols = (tab: TabType): Set<string> => {
    try {
      const raw = localStorage.getItem(`campaigns_real_hidden_cols_${tab}`);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
    return new Set();
  };
  const [hiddenColsMap, setHiddenColsMap] = useState<Record<TabType, Set<string>>>(() => ({
    campaign: loadHiddenCols('campaign'),
    adset: loadHiddenCols('adset'),
    ad: loadHiddenCols('ad'),
  }));

  const hiddenCols = hiddenColsMap[activeTab];

  const toggleHiddenCol = (tab: TabType, key: string) => {
    setHiddenColsMap(prev => {
      const next = new Set(prev[tab]);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(`campaigns_real_hidden_cols_${tab}`, JSON.stringify([...next])); } catch { /* ignore */ }
      return { ...prev, [tab]: next };
    });
  };

// 追踪已见过的 tag key，首次出现时默认隐藏
const seenTagKeysRef = useRef<Record<TabType, Set<string>>>({
  campaign: new Set((() => { try { return JSON.parse(localStorage.getItem('campaigns_real_seen_tags_campaign') || '[]') as string[]; } catch { return []; } })()),
  adset: new Set((() => { try { return JSON.parse(localStorage.getItem('campaigns_real_seen_tags_adset') || '[]') as string[]; } catch { return []; } })()),
  ad: new Set((() => { try { return JSON.parse(localStorage.getItem('campaigns_real_seen_tags_ad') || '[]') as string[]; } catch { return []; } })()),
});
  // ── 动态标签配置 ──────────────────────────────────────────────────────────
  const [tagConfig, setTagConfig] = useState<TagDefinition[]>([]);
  const siteCode = brand === 'vooglam' ? 'v' : 'z';
  const channelCode = platformFilter === 'facebook' ? 'fb' : platformFilter === 'google' ? 'google' : platformFilter === 'tiktok' ? 'tiktok' : '';

  const [wizardForm, setWizardForm] = useState({
    campaignID: '', adSetID: '', adName: '',
    material: '钛合金半框-办公-V3',
    adCopy: 'Lightweight Titanium Frames - Only $29.95 | Free Shipping',
    cta: 'Shop Now', landingURL: 'https://www.zeelool.com/titanium',
    site: 'us', startDate: '2026-03-26', endDate: '', schedule: 'all_day', channel: 'facebook',
  });

  // ── Derived ───────────────────────────────────────────────────────────────
  const myAccountIDs = useMemo(
    () => new Set(accountOptions.filter((o) => o.is_mine).map((o) => o.account_id)),
    [accountOptions],
  );
  const canWriteAccount = useCallback((accountId: string) => {
    const id = accountId.trim();
    if (!id || userLevel < 2) return false;
    if (userLevel >= 3) return true;
    return myAccountIDs.has(id);
  }, [myAccountIDs, userLevel]);
  const canCopyAccount = useCallback((_accountId: string) => userLevel >= 2, [userLevel]);
  const canCreateAd = hasPermission('ad-mgmt:campaigns:create') && (
    userLevel >= 3 || myAccountIDs.size > 0
  );

  const selectedTemplate = useMemo(() => templates.find((t) => t.id === selectedTemplateID) ?? null, [templates, selectedTemplateID]);
  const selectedTargetCampaign = useMemo(() => targetCampaigns.find((t) => String(t.id) === wizardForm.campaignID) ?? null, [targetCampaigns, wizardForm.campaignID]);
  const availableAdSets = selectedTargetCampaign?.ad_sets ?? [];
  const selectedPlatformLabel = getPlatformLabel(platformFilter);
  const statusOptions = STATUS_FILTER_OPTIONS[platformFilter] || STATUS_FILTER_OPTIONS.all;
  useEffect(() => {
    if (!statusOptions.some((opt) => opt.value === statusFilter)) {
      setStatusFilter('all');
    }
  }, [statusFilter, statusOptions]);
  const getCopyPlatformLabel = (platform: PlatformFilter) => getPlatformLabel(platform);
  const copyAvailableCampaigns = useMemo(
    () => copyTargetCampaigns.filter((item) => !copyModal?.targetAccountID || item.platform_account_id === copyModal.targetAccountID),
    [copyTargetCampaigns, copyModal?.targetAccountID],
  );
  const copyAvailableAdSets = useMemo(
    () => copyTargetAdSets.filter((item) => !copyModal?.targetCampaignID || item.campaign_id === Number(copyModal.targetCampaignID)),
    [copyTargetAdSets, copyModal?.targetCampaignID],
  );
  const copyFilteredAccounts = useMemo(
    () => {
      const source = copyModal ? copyAccountOptions : accountOptions;
      if (!copyModal) return source;
      const p = copyModal.platform;
      if (p === 'all') return source;
      return source.filter((a) => a.platform === p);
    },
    [accountOptions, copyAccountOptions, copyModal?.platform],
  );

  const buildCreateAdPath = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedAccountIDs.length === 1) {
      params.set('account_id', selectedAccountIDs[0]);
    }
    const query = params.toString();
    return `/campaigns/${platformFilter}/create${query ? `?${query}` : ''}`;
  }, [platformFilter, selectedAccountIDs]);

  const handleCreateAdClick = useCallback(() => {
    navigate(buildCreateAdPath());
  }, [buildCreateAdPath, navigate]);


  // ── 标签配置拉取 ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!channelCode) { setTagConfig([]); return; }
    adTagsApi.getConfig(siteCode, channelCode)
      .then(res => setTagConfig(res?.data ?? []))
      .catch(() => setTagConfig([]));
  }, [siteCode, channelCode]);

  // 首次出现的 tag 列默认隐藏（StrictMode 安全：ref mutation 在 updater 外部）
  useEffect(() => {
    if (!tagConfig.length) return;
    // 先收集需要新增隐藏的 key，不在 updater 内部做副作用
    const newKeysByTab: Partial<Record<TabType, string[]>> = {};
    (['campaign', 'adset', 'ad'] as TabType[]).forEach(tab => {
      const seen = seenTagKeysRef.current[tab];
      const newKeys = tagConfig
        .filter(t => t.level === tab)
        .map(td => `tag_${td.key}`)
        .filter(key => !seen.has(key));
      if (newKeys.length > 0) newKeysByTab[tab] = newKeys;
    });
    if (!Object.keys(newKeysByTab).length) return;
    // 在 updater 外部 mutation ref 和写 localStorage
    Object.entries(newKeysByTab).forEach(([tab, keys]) => {
      keys!.forEach(k => seenTagKeysRef.current[tab as TabType].add(k));
      try { localStorage.setItem(`campaigns_real_seen_tags_${tab}`, JSON.stringify([...seenTagKeysRef.current[tab as TabType]])); } catch { /* ignore */ }
    });
    setHiddenColsMap(prev => {
      const next = { ...prev };
      Object.entries(newKeysByTab).forEach(([tab, keys]) => {
        const newSet = new Set(prev[tab as TabType]);
        keys!.forEach(k => newSet.add(k));
        next[tab as TabType] = newSet;
        try { localStorage.setItem(`campaigns_real_hidden_cols_${tab}`, JSON.stringify([...newSet])); } catch { /* ignore */ }
      });
      return next;
    });
  }, [tagConfig]);

  // ── Date range ────────────────────────────────────────────────────────────
  const getMetricsDates = (range: string) => {
    const today = new Date();
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    const shift = (n: number) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };
    if (range === '7d') return { start_date: fmt(shift(-6)), end_date: fmt(today) };
    if (range === '30d') return { start_date: fmt(shift(-29)), end_date: fmt(today) };
    if (range === 'today') return { start_date: fmt(today), end_date: fmt(today) };
    if (range === 'custom' && customStartDate && customEndDate) return { start_date: customStartDate, end_date: customEndDate };
    if (range === 'custom') return { start_date: fmt(shift(-29)), end_date: fmt(today) };
    return {};
  };

  // ── Fetch tab data ────────────────────────────────────────────────────────
  const fetchCampaigns = async (pageValue = campaignPage, sizeValue = size) => {
    if (!brandId) return;
    setCampaignLoading(true);
    try {
      const dates = getMetricsDates(metricsDateRange);
      const res = await accountsApi.listFacebookCampaigns({
        brand_id: brandId,
        site_id: siteId,
        platform: platformFilter,
        account_id: selectedAccountID || undefined,
        status_filter: statusFilter === 'all' ? undefined : statusFilter,
        search: campaignSearch.trim() || undefined,
        objective: objectiveFilter || undefined,
        bid_strategy: bidStrategyFilter || undefined,
        metric_filters: metricFilters.length ? JSON.stringify(metricFilters) : undefined,
        start_date: dates.start_date,
        end_date: dates.end_date,
        sort_by: sortField || undefined,
        sort_dir: sortField ? sortDir : undefined,
        page: pageValue, size: sizeValue,
      });
      const list = Array.isArray(res?.list) ? res.list : [];
      setCampaigns(list);
      setCampaignTotal(res?.total ?? 0);
      setCampaignTotalPage(res?.total_page ?? 0);
      setCampaignSummary(res?.summary ?? EMPTY_FLAT_LIST_SUMMARY);
    } catch (err) {
      setCampaignSummary(EMPTY_FLAT_LIST_SUMMARY);
      showToast(err instanceof Error ? err.message : '加载广告计划失败', 'error');
    } finally {
      setCampaignLoading(false);
    }
  };

  const fetchAdsets = async (pageValue = adsetPage, sizeValue = size) => {
    if (!brandId) return;
    setAdsetLoading(true);
    try {
      const dates = getMetricsDates(metricsDateRange);
      const res = await accountsApi.listFacebookAdSets({
        brand_id: brandId,
        site_id: siteId,
        platform: platformFilter,
        account_id: selectedAccountID || undefined,
        campaign_id: filterCampaign?.id,
        status_filter: statusFilter === 'all' ? undefined : statusFilter,
        search: adsetSearch.trim() || undefined,
        bid_strategy: bidStrategyFilter || undefined,
        metric_filters: metricFilters.length ? JSON.stringify(metricFilters) : undefined,
        start_date: dates.start_date,
        end_date: dates.end_date,
        sort_by: sortField || undefined,
        sort_dir: sortField ? sortDir : undefined,
        page: pageValue, size: sizeValue,
      });
      const list = Array.isArray(res?.list) ? res.list : [];
      setAdsets(list);
      setAdsetTotal(res?.total ?? 0);
      setAdsetTotalPage(res?.total_page ?? 0);
      setAdsetSummary(res?.summary ?? EMPTY_FLAT_LIST_SUMMARY);
    } catch (err) {
      setAdsetSummary(EMPTY_FLAT_LIST_SUMMARY);
      showToast(err instanceof Error ? err.message : '加载广告组失败', 'error');
    } finally {
      setAdsetLoading(false);
    }
  };

  const fetchAds = async (pageValue = adPage, sizeValue = size) => {
    if (!brandId) return;
    setAdLoading(true);
    try {
      const dates = getMetricsDates(metricsDateRange);
      const res = await accountsApi.listFacebookAds({
        brand_id: brandId,
        site_id: siteId,
        platform: platformFilter,
        account_id: selectedAccountID || undefined,
        adset_id: filterAdset?.id,
        status_filter: statusFilter === 'all' ? undefined : statusFilter,
        search: adSearch.trim() || undefined,
        metric_filters: metricFilters.length ? JSON.stringify(metricFilters) : undefined,
        start_date: dates.start_date,
        end_date: dates.end_date,
        sort_by: sortField || undefined,
        sort_dir: sortField ? sortDir : undefined,
        page: pageValue, size: sizeValue,
      });
      const list = Array.isArray(res?.list) ? res.list : [];
      setAdList(list);
      setAdTotal(res?.total ?? 0);
      setAdTotalPage(res?.total_page ?? 0);
      setAdSummary(res?.summary ?? EMPTY_FLAT_LIST_SUMMARY);
    } catch (err) {
      setAdSummary(EMPTY_FLAT_LIST_SUMMARY);
      showToast(err instanceof Error ? err.message : '加载广告失败', 'error');
    } finally {
      setAdLoading(false);
    }
  };

  const refreshCurrentTab = () => {
    if (activeTab === 'campaign') return fetchCampaigns(campaignPage, size);
    if (activeTab === 'adset') return fetchAdsets(adsetPage, size);
    return fetchAds(adPage, size);
  };

  const buildSavedViewDraft = useCallback((): SavedViewDraftPayload => ({
    statusFilter,
    selectedAccountIDs,
    metricsDateRange,
    customStartDate,
    customEndDate,
    objectiveFilter,
    bidStrategyFilter,
    metricFilters,
    sortField,
    sortDir,
  }), [
    bidStrategyFilter,
    customEndDate,
    customStartDate,
    metricFilters,
    metricsDateRange,
    objectiveFilter,
    selectedAccountIDs,
    sortDir,
    sortField,
    statusFilter,
  ]);

  const availableMetricFilterOptions = useMemo(
    () => METRIC_FILTER_OPTIONS
      .filter((item) => (platformFilter === 'tiktok' || !String(item.value).startsWith('cta_')))
      .filter((item) => !metricFilters.some((rule) => rule.metric === item.value) || item.value === metricFilterMetric)
      .map((item) => ({ value: item.value, label: item.label })),
    [metricFilterMetric, metricFilters, platformFilter],
  );

  const applySavedViewDraft = useCallback((draft: SavedViewDraftPayload) => {
    setStatusFilter((draft.statusFilter || 'all') as StatusFilter);
    setSelectedAccountIDs(Array.isArray(draft.selectedAccountIDs) ? draft.selectedAccountIDs : []);
    setMetricsDateRange(draft.metricsDateRange || '30d');
    setCustomStartDate(draft.customStartDate || '');
    setCustomEndDate(draft.customEndDate || '');
    setObjectiveFilter(draft.objectiveFilter || '');
    setBidStrategyFilter(draft.bidStrategyFilter || '');
    setMetricFilters(Array.isArray(draft.metricFilters) ? draft.metricFilters : []);
    setSortField(draft.sortField ?? null);
    setSortDir(draft.sortDir ?? 'desc');
    setCampaignPage(1);
    setAdsetPage(1);
    setAdPage(1);
    setFilterCampaign(null);
    setFilterAdset(null);
  }, []);

  const fetchSavedViews = useCallback(async () => {
    if (!brandId || platformFilter === 'all') return;
    setSavedViewsLoading(true);
    try {
      const res = await accountsApi.listSavedViews({ brand_id: brandId, site_id: siteId, platform_scope: platformFilter });
      setSavedViews(Array.isArray(res.list) ? res.list : []);
    } catch (err) {
      setSavedViews([]);
      showToast(err instanceof Error ? err.message : '加载视图失败', 'error');
    } finally {
      setSavedViewsLoading(false);
    }
  }, [brandId, platformFilter, siteId]);

  const handleSelectSavedView = useCallback((name: string) => {
    if (!name || name === SAVED_VIEW_ALL_NAME) {
      setSelectedSavedViewName(SAVED_VIEW_ALL_NAME);
      applySavedViewDraft({
        statusFilter: 'all',
        selectedAccountIDs: [],
        metricsDateRange: '30d',
        customStartDate: '',
        customEndDate: '',
        objectiveFilter: '',
        bidStrategyFilter: '',
        metricFilters: [],
        sortField: null,
        sortDir: 'desc',
      });
      return;
    }
    const target = savedViews.find((item) => item.name === name);
    if (!target) return;
    setSelectedSavedViewName(name);
    applySavedViewDraft(target.draft);
  }, [applySavedViewDraft, savedViews]);

  const submitSaveView = useCallback(async () => {
    if (!brandId || platformFilter === 'all') return;
    const nextName = (saveViewMode === 'overwrite' ? selectedSavedViewName : saveViewNameInput).trim();
    if (!nextName || nextName === SAVED_VIEW_ALL_NAME) {
      showToast('请输入有效的视图名称', 'warning');
      return;
    }
    setSaveViewSubmitting(true);
    try {
      await accountsApi.saveSavedView(brandId, {
        site_id: siteId,
        platform_scope: platformFilter,
        name: nextName,
        draft: buildSavedViewDraft(),
      });
      showToast('视图已保存', 'success');
      setSelectedSavedViewName(nextName);
      setShowSaveViewModal(false);
      await fetchSavedViews();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存视图失败', 'error');
    } finally {
      setSaveViewSubmitting(false);
    }
  }, [brandId, buildSavedViewDraft, fetchSavedViews, platformFilter, saveViewMode, saveViewNameInput, selectedSavedViewName, siteId]);

  const handleDeleteSavedView = useCallback(async () => {
    if (!brandId || platformFilter === 'all' || selectedSavedViewName === SAVED_VIEW_ALL_NAME) return;
    if (!await confirmDialog({ title: '删除视图', message: `确认删除视图「${selectedSavedViewName}」吗？`, confirmText: '删除', danger: true })) return;
    try {
      await accountsApi.deleteSavedView({ brand_id: brandId, site_id: siteId, platform_scope: platformFilter, name: selectedSavedViewName });
      showToast('视图已删除', 'success');
      setSelectedSavedViewName(SAVED_VIEW_ALL_NAME);
      await fetchSavedViews();
      handleSelectSavedView(SAVED_VIEW_ALL_NAME);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除视图失败', 'error');
    }
  }, [brandId, fetchSavedViews, handleSelectSavedView, platformFilter, selectedSavedViewName, siteId]);

  const openOverwriteSavedViewModal = useCallback(() => {
    if (platformFilter === 'all') return;
    if (selectedSavedViewName === SAVED_VIEW_ALL_NAME) {
      setSaveViewMode('new');
      setSaveViewNameInput('');
    } else {
      setSaveViewMode('overwrite');
      setSaveViewNameInput(selectedSavedViewName);
    }
    setShowSaveViewModal(true);
  }, [platformFilter, selectedSavedViewName]);

  const openSaveAsViewModal = useCallback(() => {
    if (platformFilter === 'all') return;
    setSaveViewMode('new');
    setSaveViewNameInput(selectedSavedViewName !== SAVED_VIEW_ALL_NAME ? `${selectedSavedViewName}-副本` : '');
    setShowSaveViewModal(true);
  }, [platformFilter, selectedSavedViewName]);

  const addMetricFilterRule = useCallback(() => {
    if (metricFilters.some((rule) => rule.metric === metricFilterMetric)) {
      showToast('相同指标只能添加一次', 'warning');
      return;
    }
    const value = Number(metricFilterValue);
    if (!Number.isFinite(value)) {
      showToast('请输入有效的指标值', 'warning');
      return;
    }
    let value2: number | undefined;
    if (metricFilterOperator === 'between') {
      value2 = Number(metricFilterValue2);
      if (!Number.isFinite(value2)) {
        showToast('请输入区间结束值', 'warning');
        return;
      }
    }
    setMetricFilters((prev) => [
      ...prev,
      { metric: metricFilterMetric, operator: metricFilterOperator, value, value2 },
    ]);
    setMetricFilterValue('');
    setMetricFilterValue2('');
    setCampaignPage(1);
    setAdsetPage(1);
    setAdPage(1);
  }, [metricFilterMetric, metricFilterOperator, metricFilterValue, metricFilterValue2, metricFilters]);

  // ── Wizard helpers ────────────────────────────────────────────────────────
  const fetchTemplates = async (searchText: string) => {
    if (!selectedAccountID || !showCreateModal) return;
    setTemplateLoading(true);
    try {
      const res = await accountsApi.listFacebookTemplates({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, search: searchText.trim() || undefined, page: 1, size: 4 });
      setTemplates(Array.isArray(res.list) ? res.list : []);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '模板加载失败', 'error');
      setTemplates([]);
    } finally {
      setTemplateLoading(false);
    }
  };

  const fetchMaterials = async (searchText: string, templateCampaignID: number | null) => {
    if (!selectedAccountID || !showCreateModal || !templateCampaignID) return;
    setMaterialLoading(true);
    try {
      const res = await accountsApi.listFacebookMaterials({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, template_campaign_id: templateCampaignID, search: searchText.trim() || undefined, page: 1, size: 40 });
      setMaterials(Array.isArray(res.list) ? res.list : []);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '素材加载失败', 'error');
      setMaterials([]);
    } finally {
      setMaterialLoading(false);
    }
  };

  const fetchCreateTargets = async () => {
    if (!selectedAccountID || !showCreateModal) return;
    setTargetLoading(true);
    try {
      const merged: CampaignNode[] = [];
      let currentPage = 1, totalPages = 1;
      while (currentPage <= totalPages && currentPage <= 10) {
        const res = await api.get<HierarchyResponse>('/accounts/facebook/hierarchy', {
          params: { brand_id: brandId, site_id: siteId, account_id: selectedAccountID, page: currentPage, size: 100 },
        });
        merged.push(...(Array.isArray(res.list) ? res.list : []));
        totalPages = Math.max(1, Number(res.total_page || 1));
        currentPage += 1;
      }
      setTargetCampaigns(merged);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '广告系列加载失败', 'error');
      setTargetCampaigns([]);
    } finally {
      setTargetLoading(false);
    }
  };

  // ── Tab navigation ────────────────────────────────────────────────────────
  const handleTabClick = (tab: TabType) => {
    resetAllBatchSelections();
    setFilterCampaign(null); setFilterAdset(null);
    setCampaignPage(1); setAdsetPage(1); setAdPage(1);
    setActiveTab(tab);
  };

  const drillDownToAdset = (campaign: { id: number; name: string; publishStatus?: string }) => {
    resetAllBatchSelections();
    setFilterCampaign(campaign); setFilterAdset(null);
    setAdsetPage(1); setAdsetSearch(''); setAdsetSearchInput('');
    setActiveTab('adset');
  };

  const drillDownToAd = (adset: { id: number; name: string; publishStatus?: string }) => {
    resetAllBatchSelections();
    setFilterAdset(adset); setAdPage(1); setAdSearch(''); setAdSearchInput('');
    setActiveTab('ad');
  };

  const backToCampaignTab = () => {
    resetAllBatchSelections();
    setFilterCampaign(null); setFilterAdset(null); setCampaignPage(1); setActiveTab('campaign');
  };

  const backToAdsetTab = () => {
    resetAllBatchSelections();
    setFilterAdset(null); setAdsetPage(1); setActiveTab('adset');
  };

  // ── Sort (server-side: triggers re-fetch) ─────────────────────────────────
  const handleSortClick = (field: MetricSortField) => {
    if (sortField === field) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const toggleStatus = async (platform: PlatformFilter, entity: 'campaign' | 'adset' | 'ad', id: number, currentStatus: number) => {
    // Viewer 禁止任何开关操作
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行开关操作', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:campaigns:edit')) {
      showToast('暂无操作广告权限', 'error');
      return;
    }
    const key = `${entity}-${id}`;
    if (togglingIDs[key]) return;
    const pause = currentStatus === 1;
    setTogglingIDs((prev) => ({ ...prev, [key]: true }));
    try {
      if (platform === 'google') {
        await accountsApi.toggleGoogleStatus({ brand_id: brandId, site_id: siteId, entity, id, pause });
        showToast(pause ? 'Google 已暂停' : 'Google 已重新投放', 'success');
      } else if (platform === 'tiktok') {
        await accountsApi.toggleTikTokStatus({ brand_id: brandId, site_id: siteId, entity, id, pause });
        showToast(pause ? 'TikTok 已暂停' : 'TikTok 已重新投放', 'success');
      } else {
        const res = await accountsApi.toggleFacebookStatus({ brand_id: brandId, site_id: siteId, entity, id, pause });
        if (pause) {
          showToast('已暂停', 'success');
        } else if (res.data) {
          const parts = [
            `Campaign ${res.data.campaigns_published}`,
            `Ad Set ${res.data.adsets_published}`,
            `Ad ${res.data.ads_published}`,
          ];
          showToast(`Facebook 发布成功 · ${parts.join(' / ')}`, 'success');
        } else {
          showToast('已重新投放', 'success');
        }
      }
      await refreshCurrentTab();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '操作失败', 'error');
    } finally {
      setTogglingIDs((prev) => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  // ── Edit modal ──────────────────────────────────────────────────────────
  const openEditModal = async (
    platform: PlatformFilter,
    entity: EditEntityType,
    id: number,
    title: string,
    quickEdit: QuickEditTarget | null = null,
  ) => {
    setEditLoading(true);
    setEditGeoFilter('');
    setEditGeoDropdownOpen(false);
    setEditIncludeAudienceFilter('');
    setEditExcludeAudienceFilter('');
    setEditModal({
      entity, platform, sourceID: id, title,
      accountID: selectedAccountIDs[0] || '',
      editableFields: [],
      quickEdit,
      campaign: { ...EMPTY_EDIT_CAMPAIGN },
      adset: { ...EMPTY_EDIT_ADSET },
      ad: { ...EMPTY_EDIT_AD },
      tiktokCampaign: { ...EMPTY_TIKTOK_EDIT_CAMPAIGN },
      tiktokAdset: { ...EMPTY_TIKTOK_EDIT_ADSET },
      tiktokAd: { ...EMPTY_TIKTOK_EDIT_AD },
    });
    try {
      if (platform === 'tiktok') {
        let res: TikTokEditDetailResponse;
        if (entity === 'campaign') {
          res = await accountsApi.getTikTokCampaignEditDetail(brandId, id, siteId);
        } else if (entity === 'adset') {
          res = await accountsApi.getTikTokAdSetEditDetail(brandId, id, siteId);
        } else {
          res = await accountsApi.getTikTokAdEditDetail(brandId, id, siteId);
        }
        setEditModal((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            accountID: res.campaign?.account_id || prev.accountID,
            editableFields: res.editable_fields || [],
            quickEdit: prev.quickEdit,
            tiktokCampaign: res.campaign ? {
              ...EMPTY_TIKTOK_EDIT_CAMPAIGN,
              ...res.campaign,
            } : prev.tiktokCampaign,
            tiktokAdset: res.adset ? {
              ...EMPTY_TIKTOK_EDIT_ADSET,
              ...res.adset,
            } : prev.tiktokAdset,
            tiktokAd: res.ad ? {
              ...EMPTY_TIKTOK_EDIT_AD,
              ...res.ad,
            } : prev.tiktokAd,
          };
        });
      } else {
        let res: FacebookEditDetailResponse;
        if (entity === 'campaign') {
          res = await accountsApi.getFacebookCampaignEditDetail(brandId, id);
        } else if (entity === 'adset') {
          res = await accountsApi.getFacebookAdSetEditDetail(brandId, id);
        } else {
          res = await accountsApi.getFacebookAdEditDetail(brandId, id);
        }
        setEditModal((prev) => {
          if (!prev) return prev;
          const normalizedPlacement = res.adset ? normalizeFacebookPlacementState({
            publisherPlatforms: res.adset.publisher_platforms ?? [],
            facebookPositions: res.adset.facebook_positions ?? [],
            instagramPositions: res.adset.instagram_positions ?? [],
            devicePlatforms: res.adset.device_platforms ?? [],
            userOS: res.adset.user_os ?? [],
          }) : null;
          return {
            ...prev,
            accountID: res.campaign?.account_id || prev.accountID,
            editableFields: res.editable_fields || [],
            quickEdit: prev.quickEdit,
            campaign: res.campaign ? {
              name: res.campaign.name ?? '',
              daily_budget: res.campaign.daily_budget ?? 0,
              campaign_lifetime_budget: res.campaign.campaign_lifetime_budget ?? 0,
              spend_cap: res.campaign.spend_cap ?? 0,
              special_ad_categories: res.campaign.special_ad_categories ?? [],
              tags: res.campaign.tags ?? {},
            } : prev.campaign,
            adset: res.adset ? {
              name: res.adset.name ?? '',
              budget_type: res.adset.budget_type ?? 'daily',
              adset_daily_budget: res.adset.adset_daily_budget ?? 0,
              adset_lifetime_budget: res.adset.adset_lifetime_budget ?? 0,
              billing_event: res.adset.billing_event ?? '',
              optimization_goal: res.adset.optimization_goal ?? '',
              bid_strategy: res.adset.bid_strategy ?? '',
              bid_amount: res.adset.bid_amount ?? 0,
              start_time: res.adset.start_time ?? '',
              end_time: res.adset.end_time ?? '',
              countries: res.adset.countries ?? [],
              genders: res.adset.genders ?? [],
              age_min: res.adset.age_min ?? 18,
              age_max: res.adset.age_max ?? 65,
              custom_audience_ids: res.adset.custom_audience_ids ?? [],
              excluded_custom_audience_ids: res.adset.excluded_custom_audience_ids ?? [],
              publisher_platforms: normalizedPlacement?.publisherPlatforms ?? [],
              facebook_positions: normalizedPlacement?.facebookPositions ?? [],
              instagram_positions: normalizedPlacement?.instagramPositions ?? [],
              device_platforms: normalizedPlacement?.devicePlatforms ?? [],
              user_os: normalizedPlacement?.userOS ?? [],
              tags: res.adset.tags ?? {},
            } : prev.adset,
            ad: res.ad ? {
              name: res.ad.name ?? '',
              ad_type: res.ad.ad_type ?? 'image',
              page_id: res.ad.page_id ?? '',
              image_hash: res.ad.image_hash ?? '',
              video_id: res.ad.video_id ?? '',
              thumb_hash: res.ad.thumb_hash ?? '',
              message: res.ad.message ?? '',
              headline: res.ad.headline ?? '',
              description: res.ad.description ?? '',
              link: res.ad.link ?? '',
              caption: res.ad.caption ?? '',
              cta_type: res.ad.cta_type ?? '',
              url_tags: res.ad.url_tags ?? '',
              tags: res.ad.tags ?? {},
            } : prev.ad,
          };
        });
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : '加载编辑详情失败', 'error');
      setEditModal(null);
    } finally {
      setEditLoading(false);
    }
  };

  const openQuickEditModal = (
    platform: PlatformFilter,
    entity: EditEntityType,
    id: number,
    title: string,
    quickEdit: QuickEditTarget,
  ) => {
    void openEditModal(platform, entity, id, title, quickEdit);
  };

  const saveEditModal = async () => {
    if (!editModal) return;
    if (!hasPermission('ad-mgmt:campaigns:edit')) {
      showToast('暂无编辑广告权限', 'error');
      return;
    }
    setSavingEdit(true);
    try {
      const normalizedEditAdSet = normalizeFacebookPlacementState({
        publisherPlatforms: editModal.adset.publisher_platforms ?? [],
        facebookPositions: editModal.adset.facebook_positions ?? [],
        instagramPositions: editModal.adset.instagram_positions ?? [],
        devicePlatforms: editModal.adset.device_platforms ?? [],
        userOS: editModal.adset.user_os ?? [],
      });
      const adsetForSave: FacebookEditAdSetPayload = {
        ...editModal.adset,
        publisher_platforms: normalizedEditAdSet.publisherPlatforms,
        facebook_positions: normalizedEditAdSet.facebookPositions,
        instagram_positions: normalizedEditAdSet.instagramPositions,
        device_platforms: normalizedEditAdSet.devicePlatforms,
        user_os: normalizedEditAdSet.userOS,
      };
      if (editModal.platform === 'tiktok') {
        if (editModal.entity === 'campaign') {
          await accountsApi.updateTikTokCampaign(editModal.sourceID, {
            brand_id: brandId,
            site_id: siteId,
            campaign: editModal.tiktokCampaign,
          });
        } else if (editModal.entity === 'adset') {
          await accountsApi.updateTikTokAdSet(editModal.sourceID, {
            brand_id: brandId,
            site_id: siteId,
            campaign: editModal.tiktokCampaign,
            adset: editModal.tiktokAdset,
          });
        } else {
          const remoteFields = editModal.quickEdit?.kind === 'field'
            ? [editModal.quickEdit.field]
            : undefined;
          await accountsApi.updateTikTokAd(editModal.sourceID, {
            brand_id: brandId,
            site_id: siteId,
            adset: editModal.tiktokAdset,
            ad: editModal.tiktokAd,
            remote_fields: remoteFields,
          });
        }
      } else if (editModal.entity === 'campaign') {
        await accountsApi.updateFacebookCampaign(editModal.sourceID, {
          brand_id: brandId,
          site_id: siteId,
          campaign: editModal.campaign,
        });
      } else if (editModal.entity === 'adset') {
        const remoteFields = editModal.quickEdit?.kind === 'field'
          ? [editModal.quickEdit.field]
          : undefined;
        await accountsApi.updateFacebookAdSet(editModal.sourceID, {
          brand_id: brandId,
          site_id: siteId,
          campaign: editModal.campaign,
          adset: adsetForSave,
          remote_fields: remoteFields,
        });
      } else {
        await accountsApi.updateFacebookAd(editModal.sourceID, {
          brand_id: brandId,
          site_id: siteId,
          adset: adsetForSave,
          ad: editModal.ad,
        });
      }
      showToast('保存成功', 'success');
      setEditModal(null);
      await refreshCurrentTab();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  // Load geo locations when edit modal opens
  useEffect(() => {
    if (!editModal?.accountID || editModal.platform !== 'facebook' || editModal.quickEdit) {
      setEditGeoLocations([]);
      setEditGeoLoading(false);
      return;
    }
    setEditGeoLoading(true);
    accountsApi.listFacebookGeoLocations({ brand_id: brandId, site_id: siteId, account_id: editModal.accountID })
      .then((r) => setEditGeoLocations(Array.isArray(r.list) ? r.list : []))
      .catch(() => setEditGeoLocations([]))
      .finally(() => setEditGeoLoading(false));
  }, [editModal?.accountID, editModal?.platform, editModal?.quickEdit, brandId, siteId]);

  useEffect(() => {
    if (
      !editModal?.accountID
      || editModal.platform !== 'facebook'
      || editModal.quickEdit
      || (editModal.entity !== 'adset' && editModal.entity !== 'ad')
    ) {
      setEditCustomAudiences([]);
      setEditCustomAudiencesLoading(false);
      return;
    }
    setEditCustomAudiencesLoading(true);
    accountsApi.listFacebookCustomAudiences({ brand_id: brandId, site_id: siteId, account_id: editModal.accountID })
      .then((r) => setEditCustomAudiences(Array.isArray(r.list) ? r.list : []))
      .catch(() => setEditCustomAudiences([]))
      .finally(() => setEditCustomAudiencesLoading(false));
  }, [editModal?.accountID, editModal?.entity, brandId, siteId]);

  const editCustomAudienceByID = useMemo(() => {
    const map = new Map<string, FacebookCustomAudience>();
    editCustomAudiences.forEach((audience) => map.set(audience.id, audience));
    return map;
  }, [editCustomAudiences]);

  const formatEditCustomAudienceLabel = useCallback((audience: FacebookCustomAudience) => {
    const size = audience.approximate_count != null && audience.approximate_count > 0
      ? audience.approximate_count >= 1000000
        ? `${(audience.approximate_count / 1000000).toFixed(1)}M`
        : `${(audience.approximate_count / 1000).toFixed(0)}K`
      : '';
    return `${audience.name}${size ? ` (${size})` : ''}`;
  }, []);

  const filterEditCustomAudiences = useCallback((keyword: string) => {
    const q = keyword.trim().toLowerCase();
    if (!q) return editCustomAudiences;
    return editCustomAudiences.filter((audience) => (
      audience.name.toLowerCase().includes(q)
      || audience.id.toLowerCase().includes(q)
      || (audience.description || '').toLowerCase().includes(q)
      || (audience.subtype || '').toLowerCase().includes(q)
    ));
  }, [editCustomAudiences]);

  const editIncludeAudienceOptions = useMemo(
    () => filterEditCustomAudiences(editIncludeAudienceFilter),
    [filterEditCustomAudiences, editIncludeAudienceFilter],
  );
  const editExcludeAudienceOptions = useMemo(
    () => filterEditCustomAudiences(editExcludeAudienceFilter),
    [filterEditCustomAudiences, editExcludeAudienceFilter],
  );

  const toggleEditIncludedAudience = useCallback((id: string) => {
    setEditModal((prev) => {
      if (!prev) return prev;
      const included = prev.adset.custom_audience_ids || [];
      const nextIncluded = included.includes(id) ? included.filter((item) => item !== id) : [...included, id];
      return {
        ...prev,
        adset: {
          ...prev.adset,
          custom_audience_ids: nextIncluded,
          excluded_custom_audience_ids: (prev.adset.excluded_custom_audience_ids || []).filter((item) => item !== id),
        },
      };
    });
  }, []);

  const toggleEditExcludedAudience = useCallback((id: string) => {
    setEditModal((prev) => {
      if (!prev) return prev;
      const excluded = prev.adset.excluded_custom_audience_ids || [];
      const nextExcluded = excluded.includes(id) ? excluded.filter((item) => item !== id) : [...excluded, id];
      return {
        ...prev,
        adset: {
          ...prev.adset,
          excluded_custom_audience_ids: nextExcluded,
          custom_audience_ids: (prev.adset.custom_audience_ids || []).filter((item) => item !== id),
        },
      };
    });
  }, []);

  // Close edit geo dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (editGeoDropdownRef.current && !editGeoDropdownRef.current.contains(e.target as Node)) {
        setEditGeoDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const editField = (section: 'campaign' | 'adset' | 'ad') => editModal?.editableFields?.some((f) => f.startsWith(`${section}.`)) ?? false;
  const hasField = (f: string) => editModal?.editableFields?.includes(f) ?? false;
  const parseCSV = (value: string) => value.split(',').map((v) => v.trim()).filter(Boolean);
  const parseNumCSV = (value: string) => parseCSV(value).map((v) => Number(v)).filter((v) => !Number.isNaN(v));
  const supportsQuickEditTarget = useCallback((platform: PlatformFilter, quickEdit: QuickEditTarget) => {
    if (platform !== 'facebook' && platform !== 'tiktok') return false;
    if (quickEdit.kind === 'budget') return quickEdit.section === 'campaign' || quickEdit.section === 'adset';
    if (quickEdit.kind === 'tag') return platform === 'facebook';
    switch (quickEdit.field) {
      case 'campaign.name':
      case 'adset.name':
      case 'adset.end_time':
      case 'ad.name':
        return platform === 'facebook' || platform === 'tiktok';
      case 'ad.link':
      case 'ad.url_tags':
        return platform === 'facebook';
      case 'ad.landing_page_url':
        return platform === 'tiktok';
      default:
        return false;
    }
  }, []);
  const canShowQuickEdit = useCallback((platform: PlatformFilter, quickEdit: QuickEditTarget) => (
    hasPermission('ad-mgmt:campaigns:edit') && supportsQuickEditTarget(platform, quickEdit)
  ), [hasPermission, supportsQuickEditTarget]);
  const quickEditButtonStyle: CSSProperties = {
    border: '1px solid rgba(18,16,184,.16)',
    background: 'rgba(18,16,184,.06)',
    color: 'var(--primary-light)',
    borderRadius: 999,
    fontSize: 10,
    lineHeight: 1,
    padding: '2px 6px',
    cursor: 'pointer',
    flexShrink: 0,
  };
  const setEditTagSelection = useCallback((section: EditEntityType, tagKey: string, nextValue?: string) => {
    setEditModal((prev) => {
      if (!prev) return prev;
      if (section === 'campaign') {
        return { ...prev, campaign: { ...prev.campaign, tags: setTagValue(prev.campaign.tags, tagKey, nextValue || '') } };
      }
      if (section === 'adset') {
        return { ...prev, adset: { ...prev.adset, tags: setTagValue(prev.adset.tags, tagKey, nextValue || '') } };
      }
      return { ...prev, ad: { ...prev.ad, tags: setTagValue(prev.ad.tags, tagKey, nextValue || '') } };
    });
  }, []);
  const getEditTagValue = useCallback((section: EditEntityType, tagKey: string) => {
    if (!editModal) return '';
    if (section === 'campaign') return editModal.campaign.tags?.[tagKey] ?? '';
    if (section === 'adset') return editModal.adset.tags?.[tagKey] ?? '';
    return editModal.ad.tags?.[tagKey] ?? '';
  }, [editModal]);
  const renderTagEditor = useCallback((section: EditEntityType, tagDef: TagDefinition): ReactNode => {
    const flatOpts = tagDef.options.flatMap((option) => (
      option.children && option.children.length > 0
        ? option.children.map((child) => ({ value: child.value, label: `${option.label} · ${child.label}` }))
        : [{ value: option.value, label: option.label }]
    ));
    const value = getEditTagValue(section, tagDef.key);
    if (isPlacementTag(tagDef)) {
      return (
        <MultiSelect
          searchable
          clearable
          tagDisplay
          placeholder="请选择版位（可多选）"
          options={flatOpts}
          value={parseMultiTagValue(value)}
          onChange={(vals) => setEditTagSelection(section, tagDef.key, serializeMultiTagValue(vals))}
        />
      );
    }
    return (
      <MultiSelect
        single
        placeholder="请选择"
        options={flatOpts}
        value={value ? [value] : []}
        onChange={(vals) => setEditTagSelection(section, tagDef.key, vals[0] ?? '')}
      />
    );
  }, [getEditTagValue, setEditTagSelection]);
  const renderQuickEditBody = useCallback(() => {
    if (!editModal?.quickEdit) return null;
    const quickEdit = editModal.quickEdit;
    const isTikTokQuickEdit = editModal.platform === 'tiktok';
    if (quickEdit.kind === 'field') {
      if (quickEdit.field === 'campaign.name') {
        return (
          <div className="form-group">
            <label className="form-label">广告系列名称</label>
            <input
              className="form-input"
              value={isTikTokQuickEdit ? editModal.tiktokCampaign.name : editModal.campaign.name}
              onChange={(e) => setEditModal((prev) => prev ? {
                ...prev,
                campaign: { ...prev.campaign, name: e.target.value },
                tiktokCampaign: { ...prev.tiktokCampaign, name: e.target.value },
              } : prev)}
            />
          </div>
        );
      }
      if (quickEdit.field === 'adset.name') {
        return (
          <div className="form-group">
            <label className="form-label">广告组名称</label>
            <input
              className="form-input"
              value={isTikTokQuickEdit ? editModal.tiktokAdset.name : editModal.adset.name}
              onChange={(e) => setEditModal((prev) => prev ? {
                ...prev,
                adset: { ...prev.adset, name: e.target.value },
                tiktokAdset: { ...prev.tiktokAdset, name: e.target.value },
              } : prev)}
            />
          </div>
        );
      }
      if (quickEdit.field === 'adset.end_time') {
        return (
          <div className="form-group">
            <label className="form-label">结束时间</label>
            <input
              className="form-input"
              type="datetime-local"
              value={toDatetimeLocalValue(isTikTokQuickEdit ? editModal.tiktokAdset.end_time : editModal.adset.end_time)}
              onChange={(e) => setEditModal((prev) => prev ? {
                ...prev,
                adset: { ...prev.adset, end_time: datetimeLocalToRFC3339(e.target.value) },
                tiktokAdset: { ...prev.tiktokAdset, end_time: e.target.value ? `${e.target.value}:00` : '' },
              } : prev)}
            />
            <div className="form-hint">快捷编辑仅调整结束时间，不会变更开始时间。</div>
          </div>
        );
      }
      if (quickEdit.field === 'ad.name') {
        return (
          <div className="form-group">
            <label className="form-label">广告名称</label>
            <input
              className="form-input"
              value={isTikTokQuickEdit ? editModal.tiktokAd.name : editModal.ad.name}
              onChange={(e) => setEditModal((prev) => prev ? {
                ...prev,
                ad: { ...prev.ad, name: e.target.value },
                tiktokAd: { ...prev.tiktokAd, name: e.target.value },
              } : prev)}
            />
          </div>
        );
      }
      if (quickEdit.field === 'ad.link') {
        return (
          <div className="form-group">
            <label className="form-label">链接</label>
            <input className="form-input" value={editModal.ad.link} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, link: e.target.value } } : prev)} />
          </div>
        );
      }
      if (quickEdit.field === 'ad.url_tags') {
        return (
          <div className="form-group">
            <label className="form-label">网址参数</label>
            <input className="form-input" value={editModal.ad.url_tags} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, url_tags: e.target.value } } : prev)} />
          </div>
        );
      }
      if (quickEdit.field === 'ad.landing_page_url') {
        return (
          <div className="form-group">
            <label className="form-label">目标页面URL</label>
            <input
              className="form-input"
              value={editModal.tiktokAd.landing_page_url}
              onChange={(e) => setEditModal((prev) => prev ? {
                ...prev,
                tiktokAd: { ...prev.tiktokAd, landing_page_url: e.target.value },
              } : prev)}
            />
          </div>
        );
      }
    }
    if (quickEdit.kind === 'budget') {
      if (isTikTokQuickEdit) {
        const currentValue = quickEdit.section === 'campaign' ? editModal.tiktokCampaign.budget : editModal.tiktokAdset.budget;
        return (
          <div className="form-group">
            <label className="form-label">预算金额</label>
            <input
              className="form-input"
              type="number"
              value={currentValue}
              onChange={(e) => {
                const nextValue = Number(e.target.value) || 0;
                setEditModal((prev) => {
                  if (!prev) return prev;
                  if (quickEdit.section === 'campaign') {
                    return {
                      ...prev,
                      tiktokCampaign: {
                        ...prev.tiktokCampaign,
                        budget: nextValue,
                      },
                    };
                  }
                  return {
                    ...prev,
                    tiktokAdset: {
                      ...prev.tiktokAdset,
                      budget: nextValue,
                    },
                  };
                });
              }}
            />
            <div className="form-hint">仅调整预算金额，预算模式保持当前设置不变。</div>
          </div>
        );
      }
      if (quickEdit.section === 'campaign') {
        const isLifetime = quickEdit.budgetType === 'lifetime';
        return (
          <div className="form-group">
            <label className="form-label">{isLifetime ? '总预算 (USD)' : '日预算 (USD)'}</label>
            <input
              className="form-input"
              type="number"
              value={isLifetime ? editModal.campaign.campaign_lifetime_budget : editModal.campaign.daily_budget}
              onChange={(e) => {
                const nextValue = Number(e.target.value) || 0;
                setEditModal((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    campaign: {
                      ...prev.campaign,
                      daily_budget: isLifetime ? prev.campaign.daily_budget : nextValue,
                      campaign_lifetime_budget: isLifetime ? nextValue : prev.campaign.campaign_lifetime_budget,
                    },
                  };
                });
              }}
            />
            <div className="form-hint">仅调整广告系列预算金额，预算策略保持当前设置不变。</div>
          </div>
        );
      }
      const isLifetime = editModal.adset.budget_type === 'lifetime';
      return (
        <div className="form-group">
          <label className="form-label">{isLifetime ? '总预算 (USD)' : '日预算 (USD)'}</label>
          <input
            className="form-input"
            type="number"
            value={isLifetime ? editModal.adset.adset_lifetime_budget : editModal.adset.adset_daily_budget}
            onChange={(e) => {
              const nextValue = Number(e.target.value) || 0;
              setEditModal((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  adset: {
                    ...prev.adset,
                    adset_daily_budget: isLifetime ? prev.adset.adset_daily_budget : nextValue,
                    adset_lifetime_budget: isLifetime ? nextValue : prev.adset.adset_lifetime_budget,
                  },
                };
              });
            }}
          />
          <div className="form-hint">仅调整预算金额，预算类型保持当前设置不变。</div>
        </div>
      );
    }
    if (quickEdit.kind === 'tag') {
      const tagDef = tagConfig.find((item) => item.level === quickEdit.section && item.key === quickEdit.tagKey);
      if (!tagDef) {
        return <div className="form-hint">当前标签配置不存在，无法快捷编辑。</div>;
      }
      return (
        <div className="form-group">
          <label className="form-label">{tagDef.label}</label>
          {renderTagEditor(quickEdit.section, tagDef)}
        </div>
      );
    }
    return <div className="form-hint">当前字段暂不支持快捷编辑。</div>;
  }, [editModal, renderTagEditor, tagConfig]);
  const openEditPage = (
    platform: PlatformFilter,
    entity: EditEntityType,
    id: number,
    statuses: { campaignStatus?: string; adsetStatus?: string; adStatus?: string },
  ) => {
    const p = platform === 'all' ? 'facebook' : platform;
    const params = new URLSearchParams();
    if (statuses.campaignStatus) params.set('campaign_status', statuses.campaignStatus);
    if (statuses.adsetStatus) params.set('adset_status', statuses.adsetStatus);
    if (statuses.adStatus) params.set('ad_status', statuses.adStatus);
    navigate(`/campaigns/edit/${p}/${entity}/${id}?${params.toString()}`);
  };
  const retryPublishAd = async (adID: number) => {
    if (retryingAdIDs[adID]) return;
    setRetryingAdIDs((prev) => ({ ...prev, [adID]: true }));
    try {
      const res = await accountsApi.retryFacebookAdPublish(brandId, adID, siteId);
      if (res.publish_status === 'failed') {
        showToast(res.publish_error || '重试发布失败，请稍后再试', 'error');
      } else {
        showToast('Facebook 发布重试成功', 'success');
        setHighlightAdID(adID);
        window.setTimeout(() => setHighlightAdID((prev) => (prev === adID ? null : prev)), 5000);
      }
      await fetchAds(adPage, size);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '重试发布失败', 'error');
    } finally {
      setRetryingAdIDs((prev) => { const n = { ...prev }; delete n[adID]; return n; });
    }
  };

  const deleteLocalAd = async (ad: AdFlatNode) => {
    if (!hasPermission('ad-mgmt:campaigns:delete')) {
      showToast('暂无删除广告权限', 'error');
      return;
    }
    if (!canWriteAccount(ad.platform_account_id || '')) {
      showToast('暂无删除该账户广告权限', 'error');
      return;
    }
    if (deletingAdIDs[ad.id]) return;
    if (!await confirmDialog({ title: '删除广告', message: '确认删除该广告吗？删除后不可恢复。', confirmText: '删除', danger: true })) return;
    setDeletingAdIDs((prev) => ({ ...prev, [ad.id]: true }));
    try {
      const plat = ad.platform || platformFilter || 'facebook';
      if (plat === 'tiktok') {
        await accountsApi.deleteTikTokEntity(brandId, 'ad', ad.id, siteId);
      } else {
        await accountsApi.deleteFacebookEntity(brandId, 'ad', ad.id, siteId);
      }
      showToast('广告已删除', 'success');
      await fetchAds(adPage, size);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败', 'error');
    } finally {
      setDeletingAdIDs((prev) => { const n = { ...prev }; delete n[ad.id]; return n; });
    }
  };

  const handleCampaignDelete = async (campaign: CampaignFlatNode) => {
    if (!hasPermission('ad-mgmt:campaigns:delete')) {
      showToast('暂无删除广告计划权限', 'error');
      return;
    }
    if (!canWriteAccount(campaign.platform_account_id)) {
      showToast('暂无删除该账户广告计划权限', 'error');
      return;
    }
    if (!await confirmDialog({ title: '删除广告计划', message: '确认删除该广告计划吗？其下所有广告组和广告也将被删除，不可恢复。', confirmText: '删除', danger: true })) return;
    setDeletingAdIDs((prev) => ({ ...prev, [`campaign-${campaign.id}`]: true }));
    try {
      const plat = campaign.platform || platformFilter || 'facebook';
      if (plat === 'tiktok') {
        await accountsApi.deleteTikTokEntity(brandId, 'campaign', campaign.id, siteId);
      } else {
        await accountsApi.deleteFacebookEntity(brandId, 'campaign', campaign.id, siteId);
      }
      showToast('广告计划已删除', 'success');
      await fetchCampaigns(campaignPage, size);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败', 'error');
    } finally {
      setDeletingAdIDs((prev) => { const n = { ...prev }; delete n[`campaign-${campaign.id}`]; return n; });
    }
  };

  const handleAdSetDelete = async (adset: AdSetFlatNode) => {
    if (!hasPermission('ad-mgmt:campaigns:delete')) {
      showToast('暂无删除广告组权限', 'error');
      return;
    }
    if (!canWriteAccount(adset.platform_account_id || '')) {
      showToast('暂无删除该账户广告组权限', 'error');
      return;
    }
    if (!await confirmDialog({ title: '删除广告组', message: '确认删除该广告组吗？其下所有广告也将被删除，不可恢复。', confirmText: '删除', danger: true })) return;
    setDeletingAdIDs((prev) => ({ ...prev, [`adset-${adset.id}`]: true }));
    try {
      const plat = adset.platform || platformFilter || 'facebook';
      if (plat === 'tiktok') {
        await accountsApi.deleteTikTokEntity(brandId, 'adset', adset.id, siteId);
      } else {
        await accountsApi.deleteFacebookEntity(brandId, 'adset', adset.id, siteId);
      }
      showToast('广告组已删除', 'success');
      await fetchAdsets(adsetPage, size);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败', 'error');
    } finally {
      setDeletingAdIDs((prev) => { const n = { ...prev }; delete n[`adset-${adset.id}`]; return n; });
    }
  };

  const validateBeforePublish = useCallback(async (platform: Exclude<PlatformFilter, 'all'>, entity: 'campaign' | 'adset' | 'ad', id: number) => {
    let payload: ValidateAdFullRequest | null = null;
    let platformLabel = 'Facebook';

    if (platform === 'google') {
      platformLabel = 'Google';
      const detail = entity === 'campaign'
        ? await accountsApi.getGoogleCampaignEditDetail(brandId, id, siteId)
        : entity === 'adset'
          ? await accountsApi.getGoogleAdSetEditDetail(brandId, id, siteId)
          : await accountsApi.getGoogleAdEditDetail(brandId, id, siteId);
      payload = buildGoogleValidatePayload(detail);
    } else if (platform === 'tiktok') {
      platformLabel = 'TikTok';
      const detail = entity === 'campaign'
        ? await accountsApi.getTikTokCampaignEditDetail(brandId, id, siteId)
        : entity === 'adset'
          ? await accountsApi.getTikTokAdSetEditDetail(brandId, id, siteId)
          : await accountsApi.getTikTokAdEditDetail(brandId, id, siteId);
      payload = buildTikTokValidatePayload(detail);
    } else {
      const detail = entity === 'campaign'
        ? await accountsApi.getFacebookCampaignEditDetail(brandId, id, siteId)
        : entity === 'adset'
          ? await accountsApi.getFacebookAdSetEditDetail(brandId, id, siteId)
          : await accountsApi.getFacebookAdEditDetail(brandId, id, siteId);
      payload = buildFacebookValidatePayload(detail);
    }

    if (!payload) {
      showToast(`${platformLabel} 草稿缺少账户信息，无法投放，请先补全并保存`, 'error');
      return false;
    }

    const result = await accountsApi.validateAdFull(brandId, payload);
    if (!result.valid) {
      showToast(buildPublishValidationMessage(platformLabel, result), 'error');
      return false;
    }

    return true;
  }, [brandId, siteId]);

  const publishEntity = async (platform: PlatformFilter, entity: 'campaign' | 'adset' | 'ad', id: number) => {
    if (platform === 'all') {
      showToast('请先切换到具体平台后再投放', 'warning');
      return;
    }
    const key = `${entity}-${id}`;
    if (togglingIDs[key]) return;
    setTogglingIDs((prev) => ({ ...prev, [key]: true }));
    try {
      const canPublish = await validateBeforePublish(platform, entity, id);
      if (!canPublish) return;
      if (platform === 'google') {
        const summary = entity === 'campaign'
          ? await accountsApi.publishGoogleCampaign(id, { brand_id: brandId, site_id: siteId })
          : entity === 'adset'
            ? await accountsApi.publishGoogleAdSet(id, { brand_id: brandId, site_id: siteId })
            : await accountsApi.publishGoogleAd(id, { brand_id: brandId, site_id: siteId });
        const parts = [`Campaign ${summary.campaigns_published}`, `Ad Set ${summary.adsets_published}`, `Ad ${summary.ads_published}`];
        showToast(`Google 发布成功 · ${parts.join(' / ')}`, 'success');
        await refreshCurrentTab();
        return;
      }
      if (platform === 'tiktok') {
        const summary = entity === 'campaign'
          ? await accountsApi.publishTikTokCampaign(id, { brand_id: brandId, site_id: siteId })
          : entity === 'adset'
            ? await accountsApi.publishTikTokAdSet(id, { brand_id: brandId, site_id: siteId })
            : await accountsApi.publishTikTokAd(id, { brand_id: brandId, site_id: siteId });
        const parts = [`Campaign ${summary.campaigns_published}`, `Ad Set ${summary.adsets_published}`, `Ad ${summary.ads_published}`];
        showToast(`TikTok 发布成功 · ${parts.join(' / ')}`, 'success');
        await refreshCurrentTab();
        return;
      }
      const res = await accountsApi.toggleFacebookStatus({ brand_id: brandId, site_id: siteId, entity, id, pause: false });
      if (res.data) {
        const parts = [
          `Campaign ${res.data.campaigns_published}`,
          `Ad Set ${res.data.adsets_published}`,
          `Ad ${res.data.ads_published}`,
        ];
        showToast(`发布成功 · ${parts.join(' / ')}`, 'success');
      } else {
        showToast('发布成功', 'success');
      }
      await refreshCurrentTab();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '发布失败', 'error');
    } finally {
      setTogglingIDs((prev) => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const openCopyModal = (platform: PlatformFilter, entity: CopyEntityType, sourceID: number, sourceName: string) => {
    setCopyAccountOptions([]);
    setCopyModal({
      entity,
      platform,
      sourceID,
      sourceName,
      targetAccountID: '',
      targetCampaignID: '',
      targetAdSetID: '',
    });
    accountsApi.list({
      brand_id: brandId,
      site_id: siteId,
      platform: platform === 'all' ? undefined : platform,
      scope: 'campaign_write',
      page: 1,
      size: 500,
    })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const unique = new Map<string, FilterAccountOption>();
        rows.forEach((item) => {
          const accountID = String(item.account_id || '').trim();
          if (!accountID || unique.has(accountID)) return;
          unique.set(accountID, {
            id: item.id,
            account_id: accountID,
            account_name: item.account_name,
            platform: item.platform,
            is_mine: item.is_mine,
          });
        });
        const options = Array.from(unique.values());
        const selectedSingleAccount = selectedAccountIDs.length === 1 ? selectedAccountIDs[0] : '';
        setCopyAccountOptions(options);
        setCopyModal((prev) => {
          if (!prev) return prev;
          if (prev.targetAccountID && options.some((item) => item.account_id === prev.targetAccountID)) return prev;
          const targetAccountID = options.some((item) => item.account_id === selectedSingleAccount)
            ? selectedSingleAccount
            : (options[0]?.account_id ?? '');
          return { ...prev, targetAccountID, targetCampaignID: '', targetAdSetID: '' };
        });
      })
      .catch(() => setCopyAccountOptions([]));
  };

  const closeCopyModal = () => {
    if (copyingKey) return;
    setCopyModal(null);
    setCopyAccountOptions([]);
    setCopyTargetCampaigns([]);
    setCopyTargetAdSets([]);
  };

  const submitCopy = async () => {
    if (!copyModal) return;
    if (!copyModal.targetAccountID) {
      showToast(`请选择目标${getCopyPlatformLabel(copyModal.platform)}账户`, 'warning');
      return;
    }
    const key = `${copyModal.entity}-${copyModal.sourceID}`;
    setCopyingKey(key);
    try {
      if (copyModal.platform === 'google') {
        if (copyModal.entity === 'campaign') {
          await accountsApi.copyGoogleCampaign(copyModal.sourceID, { brand_id: brandId, site_id: siteId, target_account_id: copyModal.targetAccountID });
        } else if (copyModal.entity === 'adset') {
          if (!copyModal.targetCampaignID) {
            showToast('请选择目标 Campaign', 'warning');
            return;
          }
          await accountsApi.copyGoogleAdSet(copyModal.sourceID, { brand_id: brandId, site_id: siteId, target_account_id: copyModal.targetAccountID, target_campaign_id: Number(copyModal.targetCampaignID) });
        } else {
          if (!copyModal.targetAdSetID) {
            showToast('请选择目标 Ad Set', 'warning');
            return;
          }
          await accountsApi.copyGoogleAd(copyModal.sourceID, { brand_id: brandId, site_id: siteId, target_account_id: copyModal.targetAccountID, target_adset_id: Number(copyModal.targetAdSetID) });
        }
        showToast('复制成功，新 Google 对象默认为未发布', 'success');
        closeCopyModal();
        await refreshCurrentTab();
        return;
      }
      if (copyModal.platform === 'tiktok') {
        if (copyModal.entity === 'campaign') {
          await accountsApi.copyTikTokCampaign(copyModal.sourceID, { brand_id: brandId, site_id: siteId, target_account_id: copyModal.targetAccountID });
        } else if (copyModal.entity === 'adset') {
          if (!copyModal.targetCampaignID) {
            showToast('请选择目标 Campaign', 'warning');
            return;
          }
          await accountsApi.copyTikTokAdSet(copyModal.sourceID, { brand_id: brandId, site_id: siteId, target_account_id: copyModal.targetAccountID, target_campaign_id: Number(copyModal.targetCampaignID) });
        } else {
          if (!copyModal.targetAdSetID) {
            showToast('请选择目标 Ad Set', 'warning');
            return;
          }
          await accountsApi.copyTikTokAd(copyModal.sourceID, { brand_id: brandId, site_id: siteId, target_account_id: copyModal.targetAccountID, target_adset_id: Number(copyModal.targetAdSetID) });
        }
        showToast('复制成功，新 TikTok 对象默认为未发布', 'success');
        closeCopyModal();
        await refreshCurrentTab();
        return;
      }
      if (copyModal.entity === 'campaign') {
        await accountsApi.copyFacebookCampaign(copyModal.sourceID, {
          brand_id: brandId,
          site_id: siteId,
          target_account_id: copyModal.targetAccountID,
        });
      } else if (copyModal.entity === 'adset') {
        if (!copyModal.targetCampaignID) {
          showToast('请选择目标 Campaign', 'warning');
          return;
        }
        await accountsApi.copyFacebookAdSet(copyModal.sourceID, {
          brand_id: brandId,
          site_id: siteId,
          target_account_id: copyModal.targetAccountID,
          target_campaign_id: Number(copyModal.targetCampaignID),
        });
      } else {
        if (!copyModal.targetAdSetID) {
          showToast('请选择目标 Ad Set', 'warning');
          return;
        }
        await accountsApi.copyFacebookAd(copyModal.sourceID, {
          brand_id: brandId,
          site_id: siteId,
          target_account_id: copyModal.targetAccountID,
          target_adset_id: Number(copyModal.targetAdSetID),
        });
      }
      showToast('复制成功，新对象默认为未发布', 'success');
      closeCopyModal();
      await refreshCurrentTab();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '复制失败', 'error');
    } finally {
      setCopyingKey(null);
    }
  };

  // ── Wizard actions ────────────────────────────────────────────────────────
  const resetWizard = () => {
    setWizardStep(1); setPublishing(false); setWizardError(null);
    setSelectedTemplateID(null); setTemplateSearch(''); setTemplates([]);
    setTargetCampaigns([]); setMaterials([]); setMaterialSearch(''); setSelectedMaterialID('');
    setShowUploadMaterialModal(false); setUploadingMaterial(false);
    setUploadMaterialType('video'); setUploadMaterialName(''); setUploadMaterialFile(null);
    setWizardForm({
      campaignID: '', adSetID: '', adName: '',
      material: '钛合金半框-办公-V3',
      adCopy: 'Lightweight Titanium Frames - Only $29.95 | Free Shipping',
      cta: 'Shop Now', landingURL: 'https://www.zeelool.com/titanium',
      site: 'us', startDate: '2026-03-26', endDate: '', schedule: 'all_day',
      channel: platformFilter,
    });
  };

  const openCreateModal = () => {
    if (platformFilter !== 'facebook') { showToast(`当前仅支持 Facebook 新建广告，已选择 ${selectedPlatformLabel}`, 'error'); return; }
    if (selectedAccountIDs.length === 0) { showToast('请先选择授权账号', 'error'); return; }
    resetWizard();
    setShowCreateModal(true);
    void fetchTemplates('');
    void fetchCreateTargets();
  };

  const onSelectTemplate = (tpl: FacebookCampaignTemplate) => {
    setSelectedTemplateID(tpl.id);
    setWizardForm((prev) => ({
      ...prev,
      adName: `${tpl.name || tpl.third_ad_campaign_id}_Draft`,
      material: tpl.template_material_name || tpl.name || tpl.third_ad_campaign_id || prev.material,
      adCopy: tpl.template_ad_copy || `基于模板「${tpl.name || tpl.third_ad_campaign_id}」自动回写，可继续编辑`,
      cta: tpl.template_cta || prev.cta,
      landingURL: tpl.template_landing_url || prev.landingURL,
      channel: platformFilter,
    }));
    setSelectedMaterialID(tpl.template_material_asset_id || '');
    setMaterialSearch('');
    void fetchMaterials('', tpl.id);
  };

  const submitUploadMaterial = async () => {
    if (!selectedAccountID) { showToast('请先选择授权账号', 'warning'); return; }
    if (!selectedTemplateID) { showToast('请先选择模板后再上传素材', 'warning'); return; }
    if (!uploadMaterialFile) { showToast('请先选择要上传的素材文件', 'warning'); return; }
    const fd = new FormData();
    fd.append('account_id', selectedAccountID);
    fd.append('material_type', uploadMaterialType);
    fd.append('material_name', uploadMaterialName.trim() || uploadMaterialFile.name);
    fd.append('file', uploadMaterialFile);
    setUploadingMaterial(true);
    setUploadProgress(0);
    try {
      const created = await accountsApi.uploadFacebookMaterial(brandId, fd, (pct) => setUploadProgress(pct));
      showToast('素材上传 Facebook 成功', 'success');
      setShowUploadMaterialModal(false);
      setUploadMaterialFile(null); setUploadMaterialName('');
      if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
      await fetchMaterials(materialSearch, selectedTemplateID);
      setSelectedMaterialID(created.id);
      setWizardForm((prev) => ({ ...prev, material: created.material_name || prev.material }));
    } catch (err) {
      showToast(err instanceof Error ? err.message : '素材上传失败', 'error');
    } finally {
      setUploadingMaterial(false);
    }
  };

  const wizardNext = async () => {
    setWizardError(null);
    if (!selectedTemplateID) {
      setWizardError('请先选择一个模板（模板来自当前已选授权账号下的 Campaign）'); return;
    }
    if (wizardStep === 1 && (!wizardForm.campaignID || !wizardForm.adSetID)) {
      setWizardError('请选择 Campaign 和 AdSet'); return;
    }
    if (wizardStep === 2) {
      if (!wizardForm.adName.trim()) {
        setWizardError('请填写广告名称'); return;
      }
      if (!selectedMaterialID) {
        setWizardError('请选择一个素材（单选）'); return;
      }
    }
    if (wizardStep === 4) {
      setPublishing(true);
      try {
        const created = await accountsApi.createFacebookAd(brandId, {
          account_id: selectedAccountID,
          template_campaign_id: Number(selectedTemplateID),
          campaign_id: Number(wizardForm.campaignID),
          ad_set_id: Number(wizardForm.adSetID),
          ad_name: wizardForm.adName.trim(),
          ad_copy: wizardForm.adCopy.trim(),
          cta: wizardForm.cta,
          landing_url: wizardForm.landingURL.trim(),
          material_asset_id: selectedMaterialID || undefined,
          site_id: siteId || undefined,
        });
        if (created.publish_status === 'failed') {
          showToast(`本地创建成功，但 Facebook 发布失败：${created.publish_error || '请在列表中点击重试发布'}`, 'warning');
        } else {
          showToast('新建广告成功 · 广告已在 Facebook 以【暂停】状态创建', 'success');
        }
        const adSetID = Number(wizardForm.adSetID);
        const campaignID = Number(wizardForm.campaignID);
        const adSetName = availableAdSets.find((a) => String(a.id) === wizardForm.adSetID)?.name || wizardForm.adSetID;
        const campName = selectedTargetCampaign?.name || wizardForm.campaignID;
        setShowCreateModal(false);
        resetWizard();
        setFilterCampaign({ id: campaignID, name: campName });
        setFilterAdset({ id: adSetID, name: adSetName });
        setAdPage(1);
        setHighlightAdID(created.id);
        setActiveTab('ad');
        window.setTimeout(() => setHighlightAdID(null), 5000);
      } catch (err) {
        setWizardError(err instanceof Error ? err.message : '新建广告失败');
      } finally {
        setPublishing(false);
      }
      return;
    }
    setWizardStep((prev) => (prev >= 4 ? 4 : (prev + 1) as WizardStep));
  };

  const wizardPrev = () => setWizardStep((prev) => (prev <= 1 ? 1 : (prev - 1) as WizardStep));

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    accountsApi.list({
      brand_id: brandId,
      site_id: siteId,
      platform: platformFilter === 'all' ? undefined : platformFilter,
      scope: 'campaign_read',
      page: 1,
      size: 500,
    })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const unique = new Map<string, FilterAccountOption>();
        rows.forEach((item) => {
          const accountID = String(item.account_id || '').trim();
          if (!accountID || unique.has(accountID)) return;
          unique.set(accountID, {
            id: item.id,
            account_id: accountID,
            account_name: item.account_name,
            platform: item.platform,
            is_mine: item.is_mine,
          });
        });
        const options = Array.from(unique.values());
        setAccountOptions(options);
        setSelectedAccountIDs(prev => {
          const validSet = new Set(options.map(o => o.account_id));
          const filtered = prev.filter(id => validSet.has(id));
          return filtered.length === prev.length ? prev : filtered;
        });
      })
      .catch((err) => { if (!active) return; showToast(err instanceof Error ? err.message : '账号列表加载失败', 'error'); setAccountOptions([]); setSelectedAccountIDs([]); });
    return () => { active = false; };
  }, [brandId, siteId, platformFilter]);

  useEffect(() => {
    if (activeTab === 'campaign') void fetchCampaigns(campaignPage, size);
  }, [activeTab, brandId, siteId, platformFilter, selectedAccountID, statusFilter, campaignSearch, campaignPage, size, objectiveFilter, bidStrategyFilter, metricFilters, sortField, sortDir, metricsDateRange, customStartDate, customEndDate]);

  useEffect(() => {
    if (activeTab === 'adset') void fetchAdsets(adsetPage, size);
  }, [activeTab, brandId, siteId, platformFilter, selectedAccountID, statusFilter, adsetSearch, adsetPage, size, filterCampaign, bidStrategyFilter, metricFilters, sortField, sortDir, metricsDateRange, customStartDate, customEndDate]);

  useEffect(() => {
    if (activeTab === 'ad') void fetchAds(adPage, size);
  }, [activeTab, brandId, siteId, platformFilter, selectedAccountID, statusFilter, adSearch, adPage, size, filterAdset, metricFilters, sortField, sortDir, metricsDateRange, customStartDate, customEndDate]);


  useEffect(() => {
    const search = readRouteCampaignSearch(location.search);
    if (!search || search === campaignSearch) return;
    setActiveTab('campaign');
    setFilterCampaign(null);
    setFilterAdset(null);
    setCampaignPage(1);
    setCampaignSearchInput(search);
    setCampaignSearch(search);
  }, [campaignSearch, location.search]);

  useEffect(() => {
    if (metricsDateRange === 'custom' && !customStartDate && !customEndDate) {
      const today = new Date(); const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);
      setCustomStartDate(lastWeek.toISOString().slice(0, 10));
      setCustomEndDate(today.toISOString().slice(0, 10));
    }
  }, [metricsDateRange]);

  useEffect(() => {
    setCampaignPage(1);
    setAdsetPage(1);
    setAdPage(1);
    setFilterCampaign(null);
    setFilterAdset(null);
    setObjectiveFilter('');
    setBidStrategyFilter('');
    setMetricFilters([]);
    setSelectedSavedViewName(SAVED_VIEW_ALL_NAME);
  }, [brandId, siteId, platformFilter]);

  // 筛选条件变化时持久化，返回页面时可恢复
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_KEY, JSON.stringify({
        activeTab,
        filterCampaign,
        filterAdset,
        statusFilter, selectedAccountIDs, metricsDateRange,
        customStartDate, customEndDate, sortField, sortDir, size,
        objectiveFilter, bidStrategyFilter, metricFilters, selectedSavedViewName,
      }));
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterCampaign, filterAdset, statusFilter, selectedAccountIDs, metricsDateRange, customStartDate, customEndDate, sortField, sortDir, size, objectiveFilter, bidStrategyFilter, metricFilters, selectedSavedViewName]);

  useEffect(() => {
    void fetchSavedViews();
  }, [fetchSavedViews]);

  useEffect(() => {
    if (!showCreateModal) return;
    const timer = setTimeout(() => void fetchTemplates(templateSearch), 220);
    return () => clearTimeout(timer);
  }, [showCreateModal, templateSearch, selectedAccountID, brandId, siteId]);

  useEffect(() => {
    if (!showCreateModal || !selectedTemplateID) return;
    const timer = setTimeout(() => void fetchMaterials(materialSearch, selectedTemplateID), 220);
    return () => clearTimeout(timer);
  }, [showCreateModal, selectedTemplateID, materialSearch, selectedAccountID, brandId, siteId]);

  useEffect(() => {
    if (!showCreateModal) return;
    if (!selectedMaterialID && materials.length > 0) {
      setSelectedMaterialID(materials[0].id);
      setWizardForm((prev) => ({ ...prev, material: materials[0].material_name || prev.material }));
    }
  }, [showCreateModal, materials, selectedMaterialID]);

  useEffect(() => {
    if (!showCreateModal) return;
    void fetchCreateTargets();
  }, [showCreateModal, selectedAccountID, brandId, siteId]);

  useEffect(() => {
    if (!copyModal?.targetAccountID) {
      setCopyTargetCampaigns([]);
      setCopyTargetAdSets([]);
      return;
    }
    let active = true;
    setCopyLoadingTargets(true);
    Promise.all([
      accountsApi.listFacebookCampaigns({
        brand_id: brandId,
        site_id: siteId,
        platform: copyModal.platform,
        account_id: copyModal.targetAccountID,
        page: 1,
        size: 200,
      }),
      accountsApi.listFacebookAdSets({
        brand_id: brandId,
        site_id: siteId,
        platform: copyModal.platform,
        account_id: copyModal.targetAccountID,
        page: 1,
        size: 200,
      }),
    ])
      .then(([campaignRes, adsetRes]) => {
        if (!active) return;
        setCopyTargetCampaigns(Array.isArray(campaignRes.list) ? campaignRes.list : []);
        setCopyTargetAdSets(Array.isArray(adsetRes.list) ? adsetRes.list : []);
      })
      .catch((err) => {
        if (!active) return;
        showToast(err instanceof Error ? err.message : '复制目标加载失败', 'error');
        setCopyTargetCampaigns([]);
        setCopyTargetAdSets([]);
      })
      .finally(() => {
        if (active) setCopyLoadingTargets(false);
      });
    return () => { active = false; };
  }, [copyModal?.targetAccountID, copyModal?.platform, brandId, siteId]);

  useEffect(() => {
    if (!copyModal || copyLoadingTargets) return;
    if (copyModal.entity === 'campaign') return;
    if (!copyModal.targetCampaignID && copyAvailableCampaigns.length > 0) {
      setCopyModal((prev) => prev ? { ...prev, targetCampaignID: String(copyAvailableCampaigns[0].id) } : prev);
      return;
    }
    if (copyModal.entity === 'ad' && copyModal.targetCampaignID) {
      const filteredAdSets = copyTargetAdSets.filter((item) => item.campaign_id === Number(copyModal.targetCampaignID));
      if (!copyModal.targetAdSetID && filteredAdSets.length > 0) {
        setCopyModal((prev) => prev ? { ...prev, targetAdSetID: String(filteredAdSets[0].id) } : prev);
      }
    }
  }, [copyModal, copyLoadingTargets, copyAvailableCampaigns, copyTargetAdSets]);

  useEffect(() => {
    if (!copyModal || copyModal.entity !== 'ad' || !copyModal.targetAdSetID) return;
    const exists = copyAvailableAdSets.some((item) => String(item.id) === copyModal.targetAdSetID);
    if (!exists) {
      setCopyModal((prev) => prev ? { ...prev, targetAdSetID: '' } : prev);
    }
  }, [copyModal, copyAvailableAdSets]);

  // ── Drag-to-scroll for table ────────────────────────────────────────────
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    let isDown = false, startX = 0, sl = 0;
    const skip = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      return !!t.closest('button, a, label, select, input, .toggle-switch');
    };
    const onDown = (e: MouseEvent) => {
      if (skip(e)) return;
      isDown = true; startX = e.pageX; sl = el.scrollLeft;
      el.style.cursor = 'grabbing'; el.style.userSelect = 'none';
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false; el.style.cursor = ''; el.style.userSelect = '';
    };
    const onMove = (e: MouseEvent) => {
      if (!isDown || isResizingCol.current) return;
      el.scrollLeft = sl - (e.pageX - startX);
    };
    // Touch support for mobile
    const onTouchStart = (e: TouchEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('button, a, label, select, input, .toggle-switch')) return;
      isDown = true; startX = e.touches[0].pageX; sl = el.scrollLeft;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!isDown) return;
      el.scrollLeft = sl - (e.touches[0].pageX - startX);
    };
    const onTouchEnd = () => { isDown = false; };

    el.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMove);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  useEffect(() => {
    let rafID = 0;
    const updateBounds = () => {
      if (rafID) return;
      rafID = window.requestAnimationFrame(() => {
        rafID = 0;
        const rect = tableCardRef.current?.getBoundingClientRect();
        if (!rect) return;
        const summaryHeight = summaryBarRef.current?.getBoundingClientRect().height ?? (isMobile ? 92 : 84);
        const floatThreshold = window.innerHeight + summaryHeight + 32;
        const dockThreshold = window.innerHeight + summaryHeight + 8;
        const shouldFloat = isSummaryFloatingRef.current
          ? rect.bottom > dockThreshold
          : rect.bottom > floatThreshold;
        const nextBounds = isMobile
          ? { left: 0, right: 0 }
          : {
              left: Math.max(0, Math.round(rect.left)),
              right: Math.max(0, Math.round(window.innerWidth - rect.right)),
            };
        setSummaryViewportBounds((prev) => (
          prev.left === nextBounds.left && prev.right === nextBounds.right ? prev : nextBounds
        ));
        if (shouldFloat !== isSummaryFloatingRef.current) {
          isSummaryFloatingRef.current = shouldFloat;
          setIsSummaryFloating(shouldFloat);
        }
      });
    };
    updateBounds();
    window.addEventListener('resize', updateBounds);
    window.addEventListener('scroll', updateBounds, true);
    return () => {
      if (rafID) window.cancelAnimationFrame(rafID);
      window.removeEventListener('resize', updateBounds);
      window.removeEventListener('scroll', updateBounds, true);
    };
  }, [isMobile, activeTab, platformFilter]);

  // ── Table header ──────────────────────────────────────────────────────────
  const pinBg = 'var(--surface)';
  const activeTagCols = useMemo(() => {
    const normalize = (value?: string) => String(value || '').replace(/\s+/g, '').toLowerCase();
    const desiredLabelOrder =
      activeTab === 'campaign'
        ? (platformFilter === 'facebook' ? ['系列类型', '版位'] : ['系列类型'])
        : activeTab === 'adset'
          ? ['推广目标', '推广目标明细', '新老客', '新客市场', '老客类型', '首单类型', '竞价策略', '归因模型', '性别']
          : ['素材类型', '材质', '素材标签', '素材标签明细', '价格带', '新老品', '商品定位', '落地页', '素材发布年月'];
    const desiredLabelMap = new Map(desiredLabelOrder.map((label, index) => [normalize(label), index]));
    return tagConfig
      .filter((t) => t.level === activeTab)
      .filter((t) => desiredLabelMap.has(normalize(t.label)))
      .sort((a, b) => (desiredLabelMap.get(normalize(a.label)) ?? 999) - (desiredLabelMap.get(normalize(b.label)) ?? 999));
  }, [activeTab, platformFilter, tagConfig]);
  const batchTagCols = batchTagDefs;

  const colDefs: { key: string; label: string; section?: string }[] = [
    { key: 'toggle', label: '开关', section: '设置' },
    { key: 'status', label: '状态', section: '设置' },
    ...(activeTab === 'campaign' ? [
      { key: 'objective', label: '目标', section: '设置' },
      { key: 'budget', label: '预算策略', section: '设置' },
      { key: 'spendCap', label: '花费上限', section: '设置' },
      { key: 'grouping', label: '分组', section: '设置' },
      { key: 'bid_strategy', label: '竞价策略', section: '设置' },
    ] : []),
    ...(activeTab === 'adset' ? [
      { key: 'adsetBudget', label: '预算', section: '设置' },
      { key: 'optimization_goal', label: '成效目标', section: '设置' },
      { key: 'schedule', label: '排期', section: '设置' },
      { key: 'bid_strategy', label: '竞价策略', section: '设置' },
      { key: 'grouping', label: '分组', section: '设置' },
    ] : []),
    ...(activeTab === 'ad'
      ? (platformFilter === 'tiktok'
        ? [
            { key: 'landing_page_url', label: '目标页面URL', section: '设置' },
          ]
        : [
            { key: 'link', label: '链接', section: '设置' },
            { key: 'url_tags', label: '网址参数', section: '设置' },
          ])
      : []),
    { key: 'spend', label: '花费', section: '表现' },
    { key: 'roas', label: 'ROAS', section: '表现' },
    { key: 'cpc', label: 'CPC', section: '表现' },
    { key: 'cpm', label: 'CPM', section: '表现' },
    { key: 'cpa', label: 'CPA', section: '表现' },
    { key: 'purchase_count', label: '购买数量', section: '表现' },
    ...(platformFilter === 'tiktok'
      ? TIKTOK_CTA_METRIC_COLUMNS.map((item) => ({ key: item.field, label: item.label, section: '表现' }))
      : []),
    ...activeTagCols.map(td => ({ key: `tag_${td.key}`, label: td.label, section: '标签' })),
  ];
  const metricHeaderColumns: Array<{ label: string; field: MetricSortFieldName }> = [
    { label: '花费', field: 'spend' as MetricSortFieldName },
    { label: 'ROAS', field: 'roas' as MetricSortFieldName },
    { label: 'CPC', field: 'cpc' as MetricSortFieldName },
    { label: 'CPM', field: 'cpm' as MetricSortFieldName },
    { label: 'CPA', field: 'cpa' as MetricSortFieldName },
    { label: '购买数量', field: 'purchase_count' as MetricSortFieldName },
    ...(platformFilter === 'tiktok'
      ? TIKTOK_CTA_METRIC_COLUMNS.map(({ label, field }) => ({ label, field }))
      : []),
  ];

  // 隐藏列后 sticky left offset 动态计算（开关/状态列随名称列右侧定位）
  const stickyToggleLeft = W_NAME;
  const stickyStatusLeft = W_NAME + (hiddenCols.has('toggle') ? 0 : W_TOGGLE);
  const TableHeader = () => (
    <div style={{
      display: 'flex',
      position: isMobile ? undefined : 'sticky', top: 0, zIndex: 3,
      background: 'var(--surface)', borderBottom: '1px solid var(--border)',
    }}>
      <div
        style={{
          flex: `0 0 ${W_NAME}px`,
          position: isMobile ? 'relative' : colSticky,
          left: isMobile ? undefined : 0,
          zIndex: 2,
          background: pinBg,
          padding: isMobile ? '9px 6px 9px 14px' : '9px 12px 9px 14px', fontSize: 11, fontWeight: 600, color: 'var(--text3)',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <input
          type="checkbox"
          checked={allRowsSelected}
          ref={(node) => {
            if (node) node.indeterminate = !allRowsSelected && someRowsSelected;
          }}
          onChange={toggleSelectAllCurrentRows}
          disabled={currentRowIDs.length === 0}
          style={{ margin: 0, flexShrink: 0 }}
        />
        <span>名称</span>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="拖动调整名称列宽"
          title="拖动调整名称列宽"
          onMouseDown={onNameColResizeStart}
          onTouchStart={onNameColResizeTouchStart}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 14,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 4,
            touchAction: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text3)',
          }}
        >
          <svg width="7" height="16" viewBox="0 0 7 16" fill="none" aria-hidden style={{ opacity: 0.45, flexShrink: 0, pointerEvents: 'none' }}>
            <line x1="1.5" y1="3" x2="1.5" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="5.5" y1="3" x2="5.5" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      </div>
      <div style={{ display: hiddenCols.has('toggle') ? 'none' : undefined, flex: `0 0 ${W_TOGGLE}px`, position: colSticky, left: stickyToggleLeft, zIndex: 2, background: pinBg, padding: '9px 4px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>开关</div>
      <div style={{ display: hiddenCols.has('status') ? 'none' : undefined, flex: `0 0 ${W_STATUS}px`, position: colSticky, left: stickyStatusLeft, zIndex: 2, background: pinBg, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center', borderRight: '2px solid var(--border2)', boxShadow: '2px 0 4px rgba(0,0,0,.04)' }}>状态</div>
      {activeTab === 'campaign' && <>
        <div style={{ display: hiddenCols.has('objective') ? 'none' : undefined, flex: `0 0 ${W_OBJ}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>目标</div>
        <div style={{ display: hiddenCols.has('budget') ? 'none' : undefined, flex: `0 0 ${W_BUDGET}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>预算策略</div>
        <div style={{ display: hiddenCols.has('spendCap') ? 'none' : undefined, flex: `0 0 ${W_CAP}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>花费上限</div>
        <div style={{ display: hiddenCols.has('grouping') ? 'none' : undefined, flex: `0 0 ${W_GROUP}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center', whiteSpace: 'nowrap' }}>分组</div>
        <div style={{ display: hiddenCols.has('bid_strategy') ? 'none' : undefined, flex: `0 0 ${W_CAMPAIGN_BID_STRATEGY}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>竞价策略</div>
      </>}
      {activeTab === 'adset' && (
        <>
          <div style={{ display: hiddenCols.has('adsetBudget') ? 'none' : undefined, flex: `0 0 ${W_ADSET_BUDGET}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>预算</div>
          <div style={{ display: hiddenCols.has('optimization_goal') ? 'none' : undefined, flex: `0 0 ${W_ADSET_GOAL}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>成效目标</div>
          <div style={{ display: hiddenCols.has('schedule') ? 'none' : undefined, flex: `0 0 ${W_ADSET_SCHEDULE}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>排期</div>
          <div style={{ display: hiddenCols.has('bid_strategy') ? 'none' : undefined, flex: `0 0 ${W_ADSET_BID_STRATEGY}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center' }}>竞价策略</div>
          <div style={{ display: hiddenCols.has('grouping') ? 'none' : undefined, flex: `0 0 ${W_GROUP}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textAlign: 'center', whiteSpace: 'nowrap' }}>分组</div>
        </>
      )}
      {activeTab === 'ad' && platformFilter !== 'tiktok' && (
        <>
          <div style={{ display: hiddenCols.has('link') ? 'none' : undefined, flex: `0 0 ${W_BUDGET}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>链接</div>
          <div style={{ display: hiddenCols.has('url_tags') ? 'none' : undefined, flex: `0 0 ${W_BUDGET}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>网址参数</div>
        </>
      )}
      {activeTab === 'ad' && platformFilter === 'tiktok' && (
        <div style={{ display: hiddenCols.has('landing_page_url') ? 'none' : undefined, flex: `0 0 ${W_BUDGET}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>目标页面URL</div>
      )}
      {metricHeaderColumns.map(({ label, field }) => {
        const active = sortField === field;
        return (
          <div key={field} onClick={() => handleSortClick(field)}
            style={{
              display: hiddenCols.has(field) ? 'none' : 'flex',
              flex: `0 0 ${W_METRIC}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
              textAlign: 'right', color: active ? 'var(--primary-light)' : 'var(--text3)',
              cursor: 'pointer', userSelect: 'none',
              alignItems: 'center', justifyContent: 'flex-end', gap: 3,
            }}
          >
            {label}
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{ opacity: active ? 1 : 0.35, transition: 'transform .15s', transform: active && sortDir === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        );
      })}
      {activeTagCols.map(td => (
        <div key={td.key} style={{ display: hiddenCols.has(`tag_${td.key}`) ? 'none' : undefined, flex: `0 0 ${W_TAG_COL}px`, padding: '9px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>{td.label}</div>
      ))}
      <div style={{ flex: 1 }} />
      <div style={{ flex: `0 0 ${W_OPS}px`, position: colSticky, right: 0, zIndex: 2, background: pinBg, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '5px 6px 5px 6px', borderLeft: '2px solid var(--border2)', boxShadow: '-2px 0 4px rgba(0,0,0,.04)' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>操作</span>
      </div>
    </div>
  );

  const loading = activeTab === 'campaign' ? campaignLoading : activeTab === 'adset' ? adsetLoading : adLoading;
  const isEmpty = activeTab === 'campaign' ? campaigns.length === 0 : activeTab === 'adset' ? adsets.length === 0 : adList.length === 0;
  const total = activeTab === 'campaign' ? campaignTotal : activeTab === 'adset' ? adsetTotal : adTotal;
  const page = activeTab === 'campaign' ? campaignPage : activeTab === 'adset' ? adsetPage : adPage;
  const totalPage = activeTab === 'campaign' ? campaignTotalPage : activeTab === 'adset' ? adsetTotalPage : adTotalPage;
  const setPage = activeTab === 'campaign' ? setCampaignPage : activeTab === 'adset' ? setAdsetPage : setAdPage;
  const currentRows = activeTab === 'campaign' ? campaigns : activeTab === 'adset' ? adsets : adList;
  const currentSummary = activeTab === 'campaign' ? campaignSummary : activeTab === 'adset' ? adsetSummary : adSummary;
  const currentRowIDs = currentRows.map((item) => item.id);
  const summaryEntityLabel = activeTab === 'campaign' ? '广告系列' : activeTab === 'adset' ? '广告组' : '广告';
  const summaryItems = useMemo(() => {
    const items: Array<{ key: string; label: string; value: string }> = [
      {
        key: 'row_count',
        label: `${summaryEntityLabel}总计`,
        value: `${fmtCount(currentSummary.row_count ?? 0)} 个${summaryEntityLabel}`,
      },
    ];
    if (statusFilter === 'all') {
      items.push({
        key: 'active_count',
        label: '投放中',
        value: fmtCount(currentSummary.active_count ?? 0),
      });
    }
    items.push(
      { key: 'spend', label: '花费总计', value: fmtMoney(currentSummary.spend ?? 0) },
      { key: 'roas', label: '平均ROAS', value: fmtSummaryROAS(currentSummary.roas) },
      { key: 'cpc', label: '平均CPC', value: fmtMoney(currentSummary.cpc ?? 0) },
      { key: 'cpm', label: '平均CPM', value: fmtMoney(currentSummary.cpm ?? 0) },
      { key: 'cpa', label: '平均CPA', value: fmtMoney(currentSummary.cpa ?? 0) },
      { key: 'purchase_count', label: '购买数量总计', value: fmtCount(currentSummary.purchase_count ?? 0) },
    );
    if (platformFilter === 'tiktok') {
      items.push(
        { key: 'cta_registration', label: 'CTA注册次数总计', value: fmtCount(currentSummary.cta_registration ?? 0) },
        { key: 'cta_purchase', label: 'CTA付费量总计', value: fmtCount(currentSummary.cta_purchase ?? 0) },
        { key: 'cta_purchase_value', label: 'CTA付费价值总计', value: fmtMoney(currentSummary.cta_purchase_value ?? 0) },
        { key: 'cta_purchase_roas', label: 'CTA付费ROAS', value: fmtSummaryROAS(currentSummary.cta_purchase_roas) },
      );
    }
    return items;
  }, [currentSummary, platformFilter, statusFilter, summaryEntityLabel]);
  const selectedIDs = selectedIDsByTab[activeTab];
  const selectedIDSet = useMemo(() => new Set(selectedIDs), [selectedIDs]);
  const selectedCount = selectedIDs.length;
  const isTikTokDualLevelBatchTag = platformFilter === 'tiktok' && (activeTab === 'campaign' || activeTab === 'adset');
  const batchTagHasAnyDefs = isTikTokDualLevelBatchTag
    ? batchTagDefsByLevel.campaign.length > 0 || batchTagDefsByLevel.adset.length > 0
    : batchTagCols.length > 0;
  const batchTagFilledCount = Object.values(batchTagForm).filter((value) => typeof value === 'string' && value.trim() !== '').length;
  const batchTagFilledCountByLevel = Object.values(batchTagFormsByLevel).reduce((sum, form) => (
    sum + Object.values(form).filter((value) => typeof value === 'string' && value.trim() !== '').length
  ), 0);
  const allRowsSelected = currentRowIDs.length > 0 && currentRowIDs.every((id) => selectedIDSet.has(id));
  const someRowsSelected = currentRowIDs.some((id) => selectedIDSet.has(id));

  const setSelectedIDsForTab = useCallback((tab: TabType, updater: (prev: number[]) => number[]) => {
    setSelectedIDsByTab((prev) => ({ ...prev, [tab]: updater(prev[tab]) }));
  }, []);

  const resetAllBatchSelections = useCallback(() => {
    setSelectedIDsByTab({
      campaign: [],
      adset: [],
      ad: [],
    });
  }, []);

  const toggleSelectRow = useCallback((tab: TabType, id: number) => {
    setSelectedIDsForTab(tab, (prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, [setSelectedIDsForTab]);

  const toggleSelectAllCurrentRows = useCallback(() => {
    setSelectedIDsForTab(activeTab, (prev) => {
      const prevSet = new Set(prev);
      const pageIDs = currentRowIDs;
      const isAllSelected = pageIDs.length > 0 && pageIDs.every((id) => prevSet.has(id));
      if (isAllSelected) {
        return prev.filter((id) => !pageIDs.includes(id));
      }
      const next = [...prev];
      pageIDs.forEach((id) => {
        if (!prevSet.has(id)) next.push(id);
      });
      return next;
    });
  }, [activeTab, currentRowIDs, setSelectedIDsForTab]);

  const resetBatchSelection = useCallback((tab: TabType = activeTab) => {
    setSelectedIDsByTab((prev) => ({ ...prev, [tab]: [] }));
  }, [activeTab]);

  const openBatchTagModal = useCallback(async () => {
    if (selectedCount === 0) {
      showToast('请先勾选需要批量打标的数据', 'warning');
      return;
    }
    setBatchTagForm({});
    setBatchTagFormsByLevel({ campaign: {}, adset: {}, ad: {} });
    setBatchTagDefsByLevel({ campaign: [], adset: [], ad: [] });
    if (channelCode) {
      try {
        if (isTikTokDualLevelBatchTag) {
          const [campaignRes, adsetRes] = await Promise.all([
            adTagsApi.getConfig(siteCode, channelCode, 'campaign'),
            adTagsApi.getConfig(siteCode, channelCode, 'adset'),
          ]);
          setBatchTagDefsByLevel({
            campaign: prioritizeBatchTagDefinitions(campaignRes.data || [], siteCode),
            adset: prioritizeBatchTagDefinitions(adsetRes.data || [], siteCode),
            ad: [],
          });
          setBatchTagDefs([]);
        } else if (platformFilter === 'facebook' && activeTab === 'campaign') {
          const [campaignRes, adsetRes] = await Promise.all([
            adTagsApi.getConfig(siteCode, channelCode, 'campaign'),
            adTagsApi.getConfig(siteCode, channelCode, 'adset'),
          ]);
          setBatchTagDefs(
            prioritizeBatchTagDefinitions(
              mergeTagDefinitions([
                ...(campaignRes.data || []),
                ...(adsetRes.data || []),
              ]),
              siteCode,
            ),
          );
        } else {
          const level = activeTab === 'campaign' ? 'campaign' : activeTab === 'adset' ? 'adset' : 'ad';
          const res = await adTagsApi.getConfig(siteCode, channelCode, level);
          setBatchTagDefs(prioritizeBatchTagDefinitions(res.data || [], siteCode));
        }
      } catch {
        setBatchTagDefs([]);
      }
    } else {
      setBatchTagDefs([]);
    }
    setShowBatchTagModal(true);
  }, [activeTab, channelCode, isTikTokDualLevelBatchTag, platformFilter, selectedCount, showToast, siteCode]);

  const closeBatchTagModal = useCallback(() => {
    if (batchTagSubmitting) return;
    setShowBatchTagModal(false);
    setBatchTagForm({});
    setBatchTagFormsByLevel({ campaign: {}, adset: {}, ad: {} });
    setBatchTagDefs([]);
    setBatchTagDefsByLevel({ campaign: [], adset: [], ad: [] });
  }, [batchTagSubmitting]);

  const submitBatchTags = useCallback(async () => {
    if (!brandId) {
      showToast('缺少品牌信息，暂时无法批量打标', 'error');
      return;
    }
    if (platformFilter !== 'facebook' && platformFilter !== 'google' && platformFilter !== 'tiktok') {
      showToast('当前平台暂不支持批量打标', 'warning');
      return;
    }
    if (selectedCount === 0) {
      showToast('请先勾选需要批量打标的数据', 'warning');
      return;
    }
    const tags = Object.fromEntries(
      Object.entries(batchTagForm).filter(([, value]) => typeof value === 'string' && value.trim() !== ''),
    );
    const campaignTags = Object.fromEntries(
      Object.entries(batchTagFormsByLevel.campaign).filter(([, value]) => typeof value === 'string' && value.trim() !== ''),
    );
    const adsetTags = Object.fromEntries(
      Object.entries(batchTagFormsByLevel.adset).filter(([, value]) => typeof value === 'string' && value.trim() !== ''),
    );
    const adTags = Object.fromEntries(
      Object.entries(batchTagFormsByLevel.ad).filter(([, value]) => typeof value === 'string' && value.trim() !== ''),
    );
    if (Object.keys(tags).length === 0 && Object.keys(campaignTags).length === 0 && Object.keys(adsetTags).length === 0 && Object.keys(adTags).length === 0) {
      showToast('请至少选择一个标签值', 'warning');
      return;
    }
    setBatchTagSubmitting(true);
    try {
      const res = await accountsApi.batchUpdatePlatformTags(platformFilter, {
        brand_id: brandId,
        site_id: siteId || undefined,
        entity: activeTab,
        ids: selectedIDs,
        tags,
        campaign_tags: Object.keys(campaignTags).length > 0 ? campaignTags : undefined,
        adset_tags: Object.keys(adsetTags).length > 0 ? adsetTags : undefined,
        ad_tags: Object.keys(adTags).length > 0 ? adTags : undefined,
      });
      const updatedCount = res?.updated_count ?? selectedCount;
      showToast(`批量打标成功，已更新 ${updatedCount} 条${activeTab === 'campaign' ? '广告计划' : activeTab === 'adset' ? '广告组' : '广告'}`, 'success');
      setShowBatchTagModal(false);
      setBatchTagForm({});
      setBatchTagFormsByLevel({ campaign: {}, adset: {}, ad: {} });
      setBatchTagDefsByLevel({ campaign: [], adset: [], ad: [] });
      resetBatchSelection(activeTab);
      if (activeTab === 'campaign') await fetchCampaigns();
      else if (activeTab === 'adset') await fetchAdsets();
      else await fetchAds();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '批量打标失败', 'error');
    } finally {
      setBatchTagSubmitting(false);
    }
  }, [activeTab, batchTagForm, batchTagFormsByLevel, brandId, fetchAds, fetchAdsets, fetchCampaigns, platformFilter, resetBatchSelection, selectedCount, selectedIDs, showToast, siteId]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page-section visible">
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <div className="breadcrumb" style={{ marginRight: 2 }}>
              <span className="bc-brand">广告计划</span>
            </div>
            {platformFilter !== 'all' && (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => handleSelectSavedView(SAVED_VIEW_ALL_NAME)}
                    style={{
                      padding: '7px 16px',
                      borderRadius: 12,
                      border: selectedSavedViewName === SAVED_VIEW_ALL_NAME ? '1px solid var(--primary)' : '1px solid var(--border)',
                      background: selectedSavedViewName === SAVED_VIEW_ALL_NAME ? 'var(--primary)' : 'var(--surface)',
                      color: selectedSavedViewName === SAVED_VIEW_ALL_NAME ? '#fff' : 'var(--text)',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    全部
                  </button>
                  {savedViews.map((item) => {
                    const active = selectedSavedViewName === item.name;
                    return (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => handleSelectSavedView(item.name)}
                        style={{
                          padding: '7px 16px',
                          borderRadius: 12,
                          border: active ? '1px solid rgba(18,16,184,.28)' : '1px solid var(--border)',
                          background: active ? 'rgba(18,16,184,.08)' : 'var(--surface)',
                          color: active ? 'var(--primary)' : 'var(--text)',
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        {item.name}
                      </button>
                    );
                  })}
                  {savedViewsLoading && <span style={{ fontSize: 12, color: 'var(--text3)' }}>加载视图中...</span>}
                </div>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', paddingLeft: 4 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={openOverwriteSavedViewModal} style={{ padding: 0, color: 'var(--text2)' }}>
                    保存视图
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={openSaveAsViewModal} style={{ padding: 0, color: 'var(--text2)' }}>
                    另存为
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => void handleDeleteSavedView()}
                    disabled={selectedSavedViewName === SAVED_VIEW_ALL_NAME}
                    style={{ padding: 0, color: selectedSavedViewName === SAVED_VIEW_ALL_NAME ? 'var(--text4)' : 'var(--text2)' }}
                  >
                    删除视图
                  </button>
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {canCreateAd && platformFilter !== 'google' && <button className="btn btn-primary btn-sm" onClick={handleCreateAdClick} style={{ background: 'var(--green)', borderColor: 'var(--green)' }}>+ 创建新广告</button>}
            {false && <button className="btn btn-primary btn-sm" onClick={openCreateModal}>+ 从模板新建</button>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 0 }}>
            {(['campaign', 'adset', 'ad'] as TabType[]).map((tab, i) => (
              <button key={tab} onClick={() => handleTabClick(tab)}
                style={{
                  padding: '6px 16px', fontSize: 13,
                  fontWeight: activeTab === tab ? 600 : 400,
                  border: '1px solid var(--border)',
                  borderRight: i < 2 ? 'none' : '1px solid var(--border)',
                  borderRadius: i === 0 ? '6px 0 0 6px' : i === 2 ? '0 6px 6px 0' : 0,
                  background: activeTab === tab ? 'var(--primary)' : 'var(--surface)',
                  color: activeTab === tab ? '#fff' : 'var(--text)',
                  cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap',
                }}
              >
                {tab === 'campaign' ? '广告计划' : tab === 'adset' ? '广告组' : '广告'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>成效统计：</span>
              <MultiSelect
                single
                options={[
                  { value: 'today', label: '今日' },
                  { value: '7d', label: '近 7 天' },
                  { value: '30d', label: '近 30 天' },
                  { value: 'all', label: '全部时间' },
                  { value: 'custom', label: '自定义时间' },
                ]}
                value={metricsDateRange ? [metricsDateRange] : []}
                onChange={(vals) => setMetricsDateRange(vals[0] ?? '30d')}
                placeholder="近 30 天"
                style={{ minWidth: 110 }}
              />
              {metricsDateRange === 'custom' && (
                <>
                  <input type="date" className="form-input" style={{ height: 28, fontSize: 11, width: 120 }} value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} max={customEndDate || undefined} />
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>至</span>
                  <input type="date" className="form-input" style={{ height: 28, fontSize: 11, width: 120 }} value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} min={customStartDate || undefined} max={new Date().toISOString().slice(0, 10)} />
                </>
              )}
            </div>
            <MultiSelect
              options={accountOptions.map((o): SelectOption => ({ value: o.account_id, label: o.account_name || o.account_id }))}
              value={selectedAccountIDs}
              onChange={setSelectedAccountIDs}
              placeholder="全部账户"
              style={{ minWidth: 200 }}
            />
            <MultiSelect
              single
              options={statusOptions}
              value={statusFilter ? [statusFilter] : ['all']}
              onChange={(vals) => setStatusFilter((vals[0] ?? 'all') as StatusFilter)}
              placeholder="全部状态"
              style={{ minWidth: 150 }}
            />
            {activeTab === 'campaign' && (OBJECTIVE_OPTIONS[platformFilter] || []).length > 0 && (
              <MultiSelect
                single
                options={[{ value: '', label: '全部目标' }, ...(OBJECTIVE_OPTIONS[platformFilter] || [])]}
                value={objectiveFilter !== undefined ? [objectiveFilter] : ['']}
                onChange={(vals) => { setObjectiveFilter(vals[0] ?? ''); setCampaignPage(1); }}
                placeholder="全部目标"
                style={{ minWidth: 120 }}
              />
            )}
            {(activeTab === 'campaign' || activeTab === 'adset') && (
              <MultiSelect
                single
                options={[{ value: '', label: '全部竞价策略' }, ...EDIT_BID_STRATEGIES.map((item) => ({ value: item.value, label: item.label }))]}
                value={[bidStrategyFilter]}
                onChange={(vals) => { setBidStrategyFilter(vals[0] ?? ''); setCampaignPage(1); setAdsetPage(1); }}
                placeholder="全部竞价策略"
                style={{ minWidth: 150 }}
              />
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setShowMetricFilterPanel((prev) => !prev)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <span style={{ width: 14, height: 14, borderRadius: 4, border: '1px solid var(--border2)', background: showMetricFilterPanel ? 'var(--primary)' : 'transparent', display: 'inline-block' }} />
            指标筛选
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {(platformFilter === 'facebook' || platformFilter === 'google' || platformFilter === 'tiktok') && (
              <>
                {selectedCount > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                    已勾选 {selectedCount} 条
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={openBatchTagModal}
                  disabled={selectedCount === 0}
                >
                  批量打标
                </button>
              </>
            )}
            <div className="search-box" style={{ width: 260 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder={activeTab === 'campaign' ? '请输入广告系列名称/id' : activeTab === 'adset' ? '请输入广告组名称/id' : '请输入广告名称/id'}
                value={activeTab === 'campaign' ? campaignSearchInput : activeTab === 'adset' ? adsetSearchInput : adSearchInput}
                onChange={(e) => {
                  const v = e.target.value;
                  if (activeTab === 'campaign') setCampaignSearchInput(v);
                  else if (activeTab === 'adset') setAdsetSearchInput(v);
                  else setAdSearchInput(v);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (activeTab === 'campaign') { setCampaignSearch(campaignSearchInput); setCampaignPage(1); }
                    else if (activeTab === 'adset') { setAdsetSearch(adsetSearchInput); setAdsetPage(1); }
                    else { setAdSearch(adSearchInput); setAdPage(1); }
                  }
                }}
              />
            </div>
            <ColumnToggle
              cols={colDefs}
              hiddenKeys={hiddenCols}
              onToggle={key => toggleHiddenCol(activeTab, key)}
              onReset={() => {
                setHiddenColsMap(prev => ({ ...prev, [activeTab]: new Set<string>() }));
                try { localStorage.removeItem(`campaigns_real_hidden_cols_${activeTab}`); } catch { /* ignore */ }
              }}
            />
          </div>
        </div>

        {showMetricFilterPanel && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', paddingTop: 2 }}>
            <MultiSelect
              single
              options={availableMetricFilterOptions}
              value={[metricFilterMetric]}
              onChange={(vals) => setMetricFilterMetric((vals[0] as MetricFilterRule['metric']) ?? 'spend')}
              placeholder="指标"
              style={{ minWidth: 170 }}
            />
            <MultiSelect
              single
              options={METRIC_OPERATOR_OPTIONS.map((item) => ({ value: item.value, label: item.label }))}
              value={[metricFilterOperator]}
              onChange={(vals) => setMetricFilterOperator((vals[0] as MetricFilterOperator) ?? 'between')}
              placeholder="判定"
              style={{ minWidth: 110 }}
            />
            <input
              className="form-input"
              style={{ width: 110, height: 32 }}
              inputMode="decimal"
              placeholder="数值"
              value={metricFilterValue}
              onChange={(e) => setMetricFilterValue(e.target.value)}
            />
            {metricFilterOperator === 'between' && (
              <input
                className="form-input"
                style={{ width: 110, height: 32 }}
                inputMode="decimal"
                placeholder="结束值"
                value={metricFilterValue2}
                onChange={(e) => setMetricFilterValue2(e.target.value)}
              />
            )}
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={addMetricFilterRule}
              disabled={availableMetricFilterOptions.length === 0}
            >
              添加指标
            </button>
            {metricFilters.length > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMetricFilters([])}>清空指标</button>
            )}
          </div>
        )}

        {metricFilters.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', paddingTop: 2, borderTop: '1px solid var(--border)', marginTop: -2, paddingBottom: 2 }}>
            {metricFilters.map((rule, index) => (
              <button
                key={`${rule.metric}-${rule.operator}-${rule.value}-${rule.value2 ?? ''}-${index}`}
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ border: '1px solid rgba(18,16,184,.14)', background: 'rgba(18,16,184,.05)' }}
                onClick={() => setMetricFilters((prev) => prev.filter((_, idx) => idx !== index))}
              >
                {getMetricFilterLabel(rule)} ×
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Breadcrumb ───────────────────────────────────────────────────── */}
      {activeTab === 'adset' && filterCampaign && (
        <div className="breadcrumb" style={{ marginBottom: 8, fontSize: 12 }}>
          <span className="bc-brand" style={{ cursor: 'pointer', color: 'var(--primary-light)' }} onClick={backToCampaignTab}>广告计划</span>
          <span className="bc-sep">/</span>
          <span style={{ color: 'var(--text2)' }}>{filterCampaign.name}</span>
        </div>
      )}
      {activeTab === 'ad' && filterAdset && (
        <div className="breadcrumb" style={{ marginBottom: 8, fontSize: 12 }}>
          {filterCampaign ? (
            <>
              <span className="bc-brand" style={{ cursor: 'pointer', color: 'var(--primary-light)' }} onClick={backToCampaignTab}>广告计划</span>
              <span className="bc-sep">/</span>
              <span style={{ cursor: 'pointer', color: 'var(--primary-light)' }} onClick={backToAdsetTab}>{filterCampaign.name}</span>
              <span className="bc-sep">/</span>
              <span style={{ color: 'var(--text2)' }}>{filterAdset.name}</span>
            </>
          ) : (
            <>
              <span className="bc-brand" style={{ cursor: 'pointer', color: 'var(--primary-light)' }} onClick={backToAdsetTab}>广告组</span>
              <span className="bc-sep">/</span>
              <span style={{ color: 'var(--text2)' }}>{filterAdset.name}</span>
            </>
          )}
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div ref={tableCardRef} className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: !loading && isSummaryFloating ? (isMobile ? 92 : 84) : 0 }}>
      <div ref={tableScrollRef} className="table-h-scroll" style={{ cursor: 'grab' }}>
        <TableHeader />

        {loading && <TableLoadingState />}
        {!loading && isEmpty && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>暂无数据</div>}

        {/* Campaign rows */}
        {activeTab === 'campaign' && !loading && campaigns.map((campaign) => {
          const rowBg = 'var(--surface)';
          const rowPinBg = pinBg;
          const rowPlatform = ((campaign.platform || platformFilter || 'facebook') as PlatformFilter);
          const ops = operationRulesFor('campaign', rowPlatform, campaign.publish_status, campaign.effective_status, campaign.secondary_status);
          const canWriteRow = canWriteAccount(campaign.platform_account_id);
          const canCopyRow = canCopyAccount(campaign.platform_account_id);
          const canToggleRow = ops.toggle && campaign.publish_status === 'published' && canWriteRow && !togglingIDs[`campaign-${campaign.id}`];
          return (
            <div key={campaign.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', background: rowBg }}>
              <div style={{ flex: `0 0 ${W_NAME}px`, position: colSticky, left: 0, zIndex: 1, background: rowPinBg, padding: '10px 6px 10px 14px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, alignSelf: 'stretch' }}>
                <input
                  type="checkbox"
                  checked={selectedIDSet.has(campaign.id)}
                  onChange={() => toggleSelectRow('campaign', campaign.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ margin: 0, flexShrink: 0 }}
                />
                <span className={`ch-tag ${campaign.platform === 'google' ? 'google' : campaign.platform === 'tiktok' ? 'tiktok' : 'fb'}`} style={{ fontSize: 9, padding: '1px 5px', flexShrink: 0 }}>
                  {campaign.platform === 'google' ? 'GG' : campaign.platform === 'tiktok' ? 'TT' : 'FB'}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: 'var(--text)' }}
                    title={`${campaign.name || campaign.third_ad_campaign_id}（点击查看广告组）`}
                    onClick={() => drillDownToAdset({ id: campaign.id, name: campaign.name || campaign.third_ad_campaign_id, publishStatus: campaign.publish_status })}>
                    {campaign.name || campaign.third_ad_campaign_id}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{campaign.third_ad_campaign_id}</div>
                  {canShowQuickEdit(rowPlatform, {
                    kind: 'field',
                    section: 'campaign',
                    field: 'campaign.name',
                    label: '广告系列名称',
                  }) && canWriteRow && (
                    <div style={{ marginTop: 4 }}>
                      <button
                        type="button"
                        style={quickEditButtonStyle}
                        onClick={() => openQuickEditModal(rowPlatform, 'campaign', campaign.id, campaign.name || campaign.third_ad_campaign_id, {
                          kind: 'field',
                          section: 'campaign',
                          field: 'campaign.name',
                          label: '广告系列名称',
                        })}
                      >
                        编辑名称
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: hiddenCols.has('toggle') ? 'none' : 'flex', flex: `0 0 ${W_TOGGLE}px`, position: colSticky, left: stickyToggleLeft, zIndex: 1, background: rowPinBg, justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch' }}>
                <label
                  className={`toggle-switch${!canToggleRow ? ' disabled' : ''}${togglingIDs[`campaign-${campaign.id}`] ? ' loading' : ''}`}
                  title={userLevel < 2 ? '只读角色，无法执行开关操作' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!canToggleRow) return;
                    void toggleStatus(rowPlatform, 'campaign', campaign.id, deliveryToggleStatus(campaign.status, campaign.publish_status));
                  }}
                >
                  <input type="checkbox" checked={isDeliveryActive(campaign.status, campaign.publish_status)} readOnly />
                  <span className="slider" />
                </label>
              </div>
              {(() => { const ds = getOwnDeliveryStatus(campaign.status, campaign.publish_status || '', campaign.publish_error, campaign.effective_status, campaign.secondary_status, campaign.issues_info, campaign.platform || platformFilter); return (
              <div style={{ display: hiddenCols.has('status') ? 'none' : 'flex', flex: `0 0 ${W_STATUS}px`, position: colSticky, left: stickyStatusLeft, zIndex: 1, background: rowPinBg, padding: '4px 6px', textAlign: 'center', borderRight: '2px solid var(--border2)', boxShadow: '2px 0 4px rgba(0,0,0,.04)', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch', cursor: (ds.tooltip || ds.text) ? 'help' : undefined }} title={ds.tooltip || ds.text}>
                <div style={{ maxWidth: '100%', lineHeight: 1.2, display: 'grid', justifyItems: 'center', gap: 2 }}>
                  <div style={statusLineStyle(ds.color, 600)}>{renderStatusValue(ds.text)}</div>
                  <div style={{ ...statusLineStyle('var(--text3)', 500), fontSize: 10, marginTop: 2 }}>{renderStatusValue(ds.secondaryText)}</div>
                  {ds.showIssueIcon && <span title={ds.issueTooltip} style={{ display: 'inline-grid', placeItems: 'center', width: 14, height: 14, borderRadius: '50%', background: 'rgba(239,68,68,.12)', color: '#ef4444', fontSize: 10, fontWeight: 700, lineHeight: 1, cursor: 'help' }}>!</span>}
                </div>
              </div>
              ); })()}
              {(() => { const os = OBJECTIVE_STYLE[campaign.objective || '']; const objText = os ? os.label : (campaign.objective || '—'); return (
              <div style={{ display: hiddenCols.has('objective') ? 'none' : 'flex', flex: `0 0 ${W_OBJ}px`, padding: '10px 6px', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }} title={objText}>
                {os ? <span style={{ fontSize: 10, fontWeight: 600, color: os.color, background: os.bg, borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}>{os.label}</span>
                     : <span style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', display: 'inline-block' }}>{formatCampaignObjectiveLabel(campaign.objective)}</span>}
              </div>
              ); })()}
              {(() => { const bs = fmtBudgetStrategy(campaign); return (
              <div style={{ display: hiddenCols.has('budget') ? 'none' : 'flex', flex: `0 0 ${W_BUDGET}px`, padding: '6px 6px', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{bs.label}</span>
                {bs.scope && <span style={{ fontSize: 9, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{bs.scope}</span>}
                {bs.amount && <span style={{ fontSize: 10, color: '#2563eb', fontWeight: 500, whiteSpace: 'nowrap' }}>{sf(bs.amount, 'budget', userLevel).value ?? '🔒'}</span>}
                {canShowQuickEdit(rowPlatform, {
                  kind: 'budget',
                  section: 'campaign',
                  label: '预算金额',
                  budgetType: Number(campaign.lifetime_budget || 0) > 0 && Number(campaign.daily_budget || 0) <= 0 ? 'lifetime' : 'daily',
                }) && canWriteRow && campaign.budget_scope === 'campaign' && (
                  <button
                    type="button"
                    style={quickEditButtonStyle}
                    onClick={() => openQuickEditModal(rowPlatform, 'campaign', campaign.id, campaign.name || campaign.third_ad_campaign_id, {
                      kind: 'budget',
                      section: 'campaign',
                      label: '预算金额',
                      budgetType: Number(campaign.lifetime_budget || 0) > 0 && Number(campaign.daily_budget || 0) <= 0 ? 'lifetime' : 'daily',
                    })}
                  >
                    编辑预算
                  </button>
                )}
              </div>
              ); })()}
              <div style={{ display: hiddenCols.has('spendCap') ? 'none' : undefined, flex: `0 0 ${W_CAP}px`, padding: '10px 6px', textAlign: 'center', fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(() => { const cap = fmtSpendCap(campaign); return cap === '—' ? '—' : (sf(cap, 'spend_cap', userLevel).value ?? '🔒'); })()}</div>
              <div style={{ display: hiddenCols.has('grouping') ? 'none' : 'flex', flex: `0 0 ${W_GROUP}px`, padding: '10px 6px', textAlign: 'center', fontSize: 11, color: 'var(--text2)', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch' }} title="该计划下的广告组数量">
                {campaign.ad_set_count !== undefined ? <span style={{ fontWeight: 600 }}>{fmtCount(campaign.ad_set_count)}</span> : <span style={{ color: 'var(--text4)' }}>—</span>}
              </div>
              <div style={{ display: hiddenCols.has('bid_strategy') ? 'none' : 'flex', flex: `0 0 ${W_CAMPAIGN_BID_STRATEGY}px`, padding: '10px 6px', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden' }}>
                <span style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}>{formatBidStrategyLabel(campaign.budget_strategy)}</span>
              </div>
              <div style={{ display: hiddenCols.has('spend') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{sf(fmtMoney(campaign.spend ?? 0), 'spend', userLevel).value ?? '🔒'}</span></div>
              <div style={{ display: hiddenCols.has('roas') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><ROASCell roas={campaign.roas} /></div>
              <div style={{ display: hiddenCols.has('cpc') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{sf(fmtMoney(campaign.cpc ?? 0), 'cpc', userLevel).value ?? '🔒'}</span></div>
              <div style={{ display: hiddenCols.has('cpm') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{sf(fmtMoney(campaign.cpm ?? 0), 'cpm', userLevel).value ?? '🔒'}</span></div>
              <div style={{ display: hiddenCols.has('cpa') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtMoney(campaign.cpa ?? 0)}</span></div>
              <div style={{ display: hiddenCols.has('purchase_count') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtCount(campaign.purchase_count ?? 0)}</span></div>
              {platformFilter === 'tiktok' && (
                <>
                  <div style={{ display: hiddenCols.has('cta_registration') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtCount(campaign.cta_registration ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtCount(campaign.cta_purchase ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase_value') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtMoney(campaign.cta_purchase_value ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase_roas') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><ROASCell roas={campaign.cta_purchase_roas} /></div>
                </>
              )}
              {/* 动态标签列（campaign 级） */}
              {activeTagCols.map(td => {
                const val = campaign.tags?.[td.key];
                const optLabel = val ? (findTagOptionLabel(td.options, val) ?? val) : undefined;
                return (
                  <div key={td.key} style={{ display: hiddenCols.has(`tag_${td.key}`) ? 'none' : 'flex', flex: `0 0 ${W_TAG_COL}px`, padding: '8px 6px', alignItems: 'center' }}>
                    {optLabel ? (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,.1)', color: '#2563eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: W_TAG_COL - 12 }}>
                        {optLabel}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text4)' }}>—</span>
                    )}
                  </div>
                );
              })}
              <div style={{ flex: 1 }} />
              <div style={{ flex: `0 0 ${W_OPS}px`, position: colSticky, right: 0, zIndex: 1, background: rowPinBg, borderLeft: '2px solid var(--border2)', boxShadow: '-2px 0 4px rgba(0,0,0,.04)', padding: '6px 4px 6px 4px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2, flexWrap: 'wrap', alignSelf: 'stretch', rowGap: 3 }}>
                <>
                    {canCopyRow && ops.copy && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={opsTextStyle(isMobile)}
                      onClick={() => openCopyModal(rowPlatform, 'campaign', campaign.id, campaign.name || campaign.third_ad_campaign_id)}
                    >
                      复制
                    </button>
                    )}
                    {canWriteRow && ops.createChild && rowPlatform === 'facebook' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--green)' })}
                        onClick={() => navigate(`/campaigns/facebook/create?mode=add_adset&campaign_id=${campaign.id}&platform=facebook&account_id=${campaign.platform_account_id}`)}
                      >
                        新增组
                      </button>
                    )}
                    {canWriteRow && ops.createChild && rowPlatform === 'tiktok' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--primary-light)' })}
                        onClick={() => navigate(`/campaigns/tiktok/create?under=campaign&parent_id=${campaign.id}`)}
                      >
                        新增组
                      </button>
                    )}
                    {canWriteRow && ops.edit && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--primary-light)' })}
                        onClick={() => openEditPage(rowPlatform, 'campaign', campaign.id, { campaignStatus: campaign.publish_status })}
                      >
                        编辑
                      </button>
                    )}
                    {canWriteRow && ops.publish && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile)}
                        disabled={!!togglingIDs[`campaign-${campaign.id}`]}
                        onClick={() => { void publishEntity(rowPlatform, 'campaign', campaign.id); }}
                      >
                        {togglingIDs[`campaign-${campaign.id}`] ? '处理中…' : '投放'}
                      </button>
                    )}
                    {canWriteRow && ops.toggle && campaign.publish_status === 'published' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: isDeliveryActive(campaign.status, campaign.publish_status) ? 'var(--text3)' : 'var(--primary-light)' })}
                        disabled={!!togglingIDs[`campaign-${campaign.id}`] || userLevel < 2}
                        title={userLevel < 2 ? '只读角色，无法执行开关操作' : undefined}
                        onClick={() => { void toggleStatus(rowPlatform, 'campaign', campaign.id, deliveryToggleStatus(campaign.status, campaign.publish_status)); }}
                      >
                        {togglingIDs[`campaign-${campaign.id}`] ? '处理中…' : isDeliveryActive(campaign.status, campaign.publish_status) ? '关闭' : '投放'}
                      </button>
                    )}
                    {canWriteRow && ops.delete && rowPlatform !== 'google' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--red, #ef4444)' })}
                        onClick={() => { void handleCampaignDelete(campaign); }}
                      >
                        删除
                      </button>
                    )}
                  </>
              </div>
            </div>
          );
        })}

        {/* AdSet rows */}
        {activeTab === 'adset' && !loading && adsets.map((adset) => {
          const rowPlatform = ((adset.platform || platformFilter || 'facebook') as PlatformFilter);
          const ops = operationRulesFor('adset', rowPlatform, adset.publish_status, adset.effective_status, adset.secondary_status);
          const canWriteRow = canWriteAccount(adset.platform_account_id || '');
          const canCopyRow = canCopyAccount(adset.platform_account_id || '');
          const canToggleRow = ops.toggle && adset.publish_status === 'published' && canWriteRow && !togglingIDs[`adset-${adset.id}`];
          return (
            <div key={adset.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
              <div style={{ flex: `0 0 ${W_NAME}px`, position: colSticky, left: 0, zIndex: 1, background: pinBg, padding: '10px 6px 10px 14px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, alignSelf: 'stretch' }}>
                <input
                  type="checkbox"
                  checked={selectedIDSet.has(adset.id)}
                  onChange={() => toggleSelectRow('adset', adset.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ margin: 0, flexShrink: 0 }}
                />
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: 'var(--text)' }}
                    title={`${adset.name || adset.third_ad_set_id}（点击查看广告）`}
                    onClick={() => drillDownToAd({ id: adset.id, name: adset.name || adset.third_ad_set_id, publishStatus: adset.publish_status })}>
                    {adset.name || adset.third_ad_set_id}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {adset.third_ad_set_id}
                    {adset.campaign_name && <span style={{ marginLeft: 6, opacity: 0.7 }}>· {adset.campaign_name}</span>}
                  </div>
                  {canShowQuickEdit(rowPlatform, {
                    kind: 'field',
                    section: 'adset',
                    field: 'adset.name',
                    label: '广告组名称',
                  }) && canWriteRow && (
                    <div style={{ marginTop: 4 }}>
                      <button
                        type="button"
                        style={quickEditButtonStyle}
                        onClick={() => openQuickEditModal(rowPlatform, 'adset', adset.id, adset.name || adset.third_ad_set_id, {
                          kind: 'field',
                          section: 'adset',
                          field: 'adset.name',
                          label: '广告组名称',
                        })}
                      >
                        编辑名称
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: hiddenCols.has('toggle') ? 'none' : 'flex', flex: `0 0 ${W_TOGGLE}px`, position: colSticky, left: stickyToggleLeft, zIndex: 1, background: pinBg, justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch' }}>
                <label
                  className={`toggle-switch${!canToggleRow ? ' disabled' : ''}${togglingIDs[`adset-${adset.id}`] ? ' loading' : ''}`}
                  title={userLevel < 2 ? '只读角色，无法执行开关操作' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!canToggleRow) return;
                    void toggleStatus(rowPlatform, 'adset', adset.id, deliveryToggleStatus(adset.status, adset.publish_status));
                  }}
                >
                  <input type="checkbox" checked={isDeliveryActive(adset.status, adset.publish_status)} readOnly />
                  <span className="slider" />
                </label>
              </div>
              {(() => { const ds = getOwnDeliveryStatus(adset.status, adset.publish_status || '', adset.publish_error, adset.effective_status, adset.secondary_status, adset.issues_info, adset.platform || platformFilter); return (
              <div style={{ display: hiddenCols.has('status') ? 'none' : 'flex', flex: `0 0 ${W_STATUS}px`, position: colSticky, left: stickyStatusLeft, zIndex: 1, background: pinBg, padding: '4px 6px', textAlign: 'center', borderRight: '2px solid var(--border2)', boxShadow: '2px 0 4px rgba(0,0,0,.04)', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch', cursor: (ds.tooltip || ds.text) ? 'help' : undefined }} title={ds.tooltip || ds.text}>
                <div style={{ maxWidth: '100%', lineHeight: 1.2, display: 'grid', justifyItems: 'center', gap: 2 }}>
                  <div style={statusLineStyle(ds.color, 600)}>{renderStatusValue(ds.text)}</div>
                  <div style={{ ...statusLineStyle('var(--text3)', 500), fontSize: 10, marginTop: 2 }}>{renderStatusValue(ds.secondaryText)}</div>
                  {ds.showIssueIcon && <span title={ds.issueTooltip} style={{ display: 'inline-grid', placeItems: 'center', width: 14, height: 14, borderRadius: '50%', background: 'rgba(239,68,68,.12)', color: '#ef4444', fontSize: 10, fontWeight: 700, lineHeight: 1, cursor: 'help' }}>!</span>}
                </div>
              </div>
              ); })()}
              {(() => { const budget = fmtAdSetBudget(adset); return (
              <div style={{ display: hiddenCols.has('adsetBudget') ? 'none' : 'flex', flex: `0 0 ${W_ADSET_BUDGET}px`, padding: '6px 6px', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, overflow: 'hidden' }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{budget.label}</span>
                {budget.amount && <span style={{ fontSize: 10, color: '#2563eb', fontWeight: 500, whiteSpace: 'nowrap' }}>{sf(budget.amount, 'budget', userLevel).value ?? '🔒'}</span>}
                {canShowQuickEdit(rowPlatform, {
                  kind: 'budget',
                  section: 'adset',
                  label: '预算',
                }) && canWriteRow && (
                  <button
                    type="button"
                    style={quickEditButtonStyle}
                    onClick={() => openQuickEditModal(rowPlatform, 'adset', adset.id, adset.name || adset.third_ad_set_id, {
                      kind: 'budget',
                      section: 'adset',
                      label: '预算',
                    })}
                  >
                    编辑预算
                  </button>
                )}
              </div>
              ); })()}
              <div style={{ display: hiddenCols.has('optimization_goal') ? 'none' : 'flex', flex: `0 0 ${W_ADSET_GOAL}px`, padding: '8px 6px', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden' }}>
                <div style={{ display: 'grid', justifyItems: 'center', gap: 2, maxWidth: '100%' }}>
                  <span style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}>
                    {formatOptimizationGoalLabel(adset.optimization_goal)}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}>
                    {formatOptimizationPlacementLabel(adset.optimization_event || adset.destination_type)}
                  </span>
                </div>
              </div>
              <div style={{ display: hiddenCols.has('schedule') ? 'none' : 'flex', flex: `0 0 ${W_ADSET_SCHEDULE}px`, padding: '8px 6px', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, overflow: 'hidden' }}>
                {(() => {
                  const schedule = formatScheduleSummary(adset.start_time, adset.end_time);
                  return (
                    <>
                      <span style={{ fontSize: 10, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{schedule.start}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{schedule.end}</span>
                    </>
                  );
                })()}
                {canShowQuickEdit(rowPlatform, {
                  kind: 'field',
                  section: 'adset',
                  field: 'adset.end_time',
                  label: '结束时间',
                }) && canWriteRow && (
                  <button
                    type="button"
                    style={quickEditButtonStyle}
                    onClick={() => openQuickEditModal(rowPlatform, 'adset', adset.id, adset.name || adset.third_ad_set_id, {
                      kind: 'field',
                      section: 'adset',
                      field: 'adset.end_time',
                      label: '结束时间',
                    })}
                  >
                    编辑排期
                  </button>
                )}
              </div>
              <div style={{ display: hiddenCols.has('bid_strategy') ? 'none' : 'flex', flex: `0 0 ${W_ADSET_BID_STRATEGY}px`, padding: '10px 6px', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden' }}>
                <span style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}>{formatBidStrategyLabel(adset.bid_strategy)}</span>
              </div>
              <div style={{ display: hiddenCols.has('grouping') ? 'none' : 'flex', flex: `0 0 ${W_GROUP}px`, padding: '10px 6px', textAlign: 'center', fontSize: 11, color: 'var(--text2)', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch' }} title="该广告组下的广告数量">
                {adset.ad_count !== undefined ? <span style={{ fontWeight: 600 }}>{fmtCount(adset.ad_count)}</span> : <span style={{ color: 'var(--text4)' }}>—</span>}
              </div>
              <div style={{ display: hiddenCols.has('spend') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{sf(fmtMoney(adset.spend ?? 0), 'spend', userLevel).value ?? '🔒'}</span></div>
              <div style={{ display: hiddenCols.has('roas') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><ROASCell roas={adset.roas} /></div>
              <div style={{ display: hiddenCols.has('cpc') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{sf(fmtMoney(adset.cpc ?? 0), 'cpc', userLevel).value ?? '🔒'}</span></div>
              <div style={{ display: hiddenCols.has('cpm') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{sf(fmtMoney(adset.cpm ?? 0), 'cpm', userLevel).value ?? '🔒'}</span></div>
              <div style={{ display: hiddenCols.has('cpa') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtMoney(adset.cpa ?? 0)}</span></div>
              <div style={{ display: hiddenCols.has('purchase_count') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtCount(adset.purchase_count ?? 0)}</span></div>
              {platformFilter === 'tiktok' && (
                <>
                  <div style={{ display: hiddenCols.has('cta_registration') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtCount(adset.cta_registration ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtCount(adset.cta_purchase ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase_value') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontWeight: 600, fontSize: 12 }}>{fmtMoney(adset.cta_purchase_value ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase_roas') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><ROASCell roas={adset.cta_purchase_roas} /></div>
                </>
              )}
              {/* 动态标签列（adset 级） */}
              {activeTagCols.map(td => {
                const val = adset.tags?.[td.key];
                const optLabel = val ? (findTagOptionLabel(td.options, val) ?? val) : undefined;
                return (
                  <div key={td.key} style={{ display: hiddenCols.has(`tag_${td.key}`) ? 'none' : 'flex', flex: `0 0 ${W_TAG_COL}px`, padding: '8px 6px', alignItems: 'center' }}>
                    {optLabel ? (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,.1)', color: '#2563eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: W_TAG_COL - 12 }}>
                        {optLabel}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text4)' }}>—</span>
                    )}
                  </div>
                );
              })}
              <div style={{ flex: 1 }} />
              <div style={{ flex: `0 0 ${W_OPS}px`, position: colSticky, right: 0, zIndex: 1, background: pinBg, borderLeft: '2px solid var(--border2)', boxShadow: '-2px 0 4px rgba(0,0,0,.04)', padding: '6px 4px 6px 4px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2, flexWrap: 'wrap', alignSelf: 'stretch', rowGap: 3 }}>
                <>
                    {canCopyRow && ops.copy && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile)}
                        onClick={() => openCopyModal(rowPlatform, 'adset', adset.id, adset.name || adset.third_ad_set_id)}
                      >
                        复制
                      </button>
                    )}
                    {canWriteRow && ops.createChild && rowPlatform === 'facebook' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--green)' })}
                        onClick={() => navigate(`/campaigns/facebook/create?mode=add_ad&adset_id=${adset.id}&platform=facebook`)}
                      >
                        新增广告
                      </button>
                    )}
                    {canWriteRow && ops.createChild && rowPlatform === 'tiktok' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--primary-light)' })}
                        onClick={() => navigate(`/campaigns/tiktok/create?under=adset&parent_id=${adset.id}`)}
                      >
                        新增广告
                      </button>
                    )}
                    {canWriteRow && ops.edit && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--primary-light)' })}
                        onClick={() => openEditPage(rowPlatform, 'adset', adset.id, { campaignStatus: filterCampaign?.publishStatus, adsetStatus: adset.publish_status })}
                      >
                        编辑
                      </button>
                    )}
                    {canWriteRow && ops.publish && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile)}
                        disabled={!!togglingIDs[`adset-${adset.id}`]}
                        onClick={() => { void publishEntity(rowPlatform, 'adset', adset.id); }}
                      >
                        {togglingIDs[`adset-${adset.id}`] ? '处理中…' : '投放'}
                      </button>
                    )}
                    {canWriteRow && ops.toggle && adset.publish_status === 'published' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: isDeliveryActive(adset.status, adset.publish_status) ? 'var(--text3)' : 'var(--primary-light)' })}
                        disabled={!!togglingIDs[`adset-${adset.id}`]}
                        onClick={() => { void toggleStatus(rowPlatform, 'adset', adset.id, deliveryToggleStatus(adset.status, adset.publish_status)); }}
                      >
                        {togglingIDs[`adset-${adset.id}`] ? '处理中…' : isDeliveryActive(adset.status, adset.publish_status) ? '关闭' : '投放'}
                      </button>
                    )}
                    {canWriteRow && ops.delete && rowPlatform !== 'google' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--red, #ef4444)' })}
                        onClick={() => { void handleAdSetDelete(adset); }}
                      >
                        删除
                      </button>
                    )}
                  </>
              </div>
            </div>
          );
        })}

        {/* Ad rows */}
        {activeTab === 'ad' && !loading && adList.map((ad) => {
          const rowPlatform = ((ad.platform || platformFilter || 'facebook') as PlatformFilter);
          const ops = operationRulesFor('ad', rowPlatform, ad.publish_status, ad.effective_status, ad.secondary_status);
          const isLocalAd = ad.third_ad_id.startsWith('local_');
          const canWriteRow = canWriteAccount(ad.platform_account_id || '');
          const canCopyRow = canCopyAccount(ad.platform_account_id || '');
          const canToggleRow = ops.toggle && ad.publish_status === 'published' && !isLocalAd && canWriteRow && !togglingIDs[`ad-${ad.id}`];
          return (
            <div key={ad.id} style={{
              display: 'flex', alignItems: 'center',
              borderBottom: '1px solid var(--border)',
              background: highlightAdID === ad.id ? '#1210b814' : 'var(--surface)',
              animation: highlightAdID === ad.id ? 'pulse 1s ease-in-out 4' : undefined,
            }}>
              <div style={{ flex: `0 0 ${W_NAME}px`, position: colSticky, left: 0, zIndex: 1, background: pinBg, padding: '10px 6px 10px 14px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, alignSelf: 'stretch' }}>
                <input
                  type="checkbox"
                  checked={selectedIDSet.has(ad.id)}
                  onChange={() => toggleSelectRow('ad', ad.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ margin: 0, flexShrink: 0 }}
                />
                {(ad.image_url || ad.video_url) ? (
                  <div
                    className="ad-thumb-wrap"
                    style={{ width: 28, height: 28, borderRadius: 4, flexShrink: 0 }}
                    onMouseEnter={(e) => {
                      const wrap = e.currentTarget;
                      const stickyParent = wrap.closest('[style*="sticky"]') as HTMLElement;
                      if (stickyParent) stickyParent.style.zIndex = '200';
                      const preview = wrap.querySelector('.ad-thumb-preview') as HTMLElement;
                      if (!preview) return;
                      const rect = wrap.getBoundingClientRect();
                      preview.style.left = `${rect.right + 8}px`;
                      preview.style.top = `${Math.max(8, rect.top - 80)}px`;
                    }}
                    onMouseLeave={(e) => {
                      const stickyParent = e.currentTarget.closest('[style*="sticky"]') as HTMLElement;
                      if (stickyParent) stickyParent.style.zIndex = '1';
                    }}
                  >
                    <img
                      src={ad.image_url || ad.video_url}
                      alt=""
                      style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', display: 'block' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).parentElement!.style.background = 'var(--surface2)'; }}
                    />
                    <div className="ad-thumb-preview">
                      <img src={ad.image_url || ad.video_url} alt="" />
                    </div>
                  </div>
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: 4, background: 'var(--surface2)', flexShrink: 0 }} />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }} title={ad.name}>{ad.name || ad.third_ad_id}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ad.third_ad_id}
                    {ad.adset_name && <span style={{ marginLeft: 6, opacity: 0.7 }}>· {ad.adset_name}</span>}
                  </div>
                  {canShowQuickEdit(rowPlatform, {
                    kind: 'field',
                    section: 'ad',
                    field: 'ad.name',
                    label: '广告名称',
                  }) && canWriteRow && (
                    <div style={{ marginTop: 4 }}>
                      <button
                        type="button"
                        style={quickEditButtonStyle}
                        onClick={() => openQuickEditModal(rowPlatform, 'ad', ad.id, ad.name || ad.third_ad_id, {
                          kind: 'field',
                          section: 'ad',
                          field: 'ad.name',
                          label: '广告名称',
                        })}
                      >
                        编辑名称
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: hiddenCols.has('toggle') ? 'none' : 'flex', flex: `0 0 ${W_TOGGLE}px`, position: colSticky, left: stickyToggleLeft, zIndex: 1, background: pinBg, justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch' }}>
                <label
                  className={`toggle-switch${!canToggleRow ? ' disabled' : ''}${togglingIDs[`ad-${ad.id}`] ? ' loading' : ''}`}
                  title={userLevel < 2 ? '只读角色，无法执行开关操作' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!canToggleRow) return;
                    void toggleStatus(rowPlatform, 'ad', ad.id, deliveryToggleStatus(ad.status, ad.publish_status));
                  }}
                >
                  <input type="checkbox" checked={isDeliveryActive(ad.status, ad.publish_status)} readOnly />
                  <span className="slider" />
                </label>
              </div>
              {(() => { const ds = getOwnDeliveryStatus(ad.status, ad.publish_status || '', ad.publish_error, ad.effective_status, ad.secondary_status, ad.issues_info, ad.platform || platformFilter); return (
              <div style={{ display: hiddenCols.has('status') ? 'none' : 'flex', flex: `0 0 ${W_STATUS}px`, position: colSticky, left: stickyStatusLeft, zIndex: 1, background: pinBg, padding: '4px 6px', textAlign: 'center', borderRight: '2px solid var(--border2)', boxShadow: '2px 0 4px rgba(0,0,0,.04)', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch', cursor: (ds.tooltip || ds.text) ? 'help' : undefined }} title={ds.tooltip || ds.text}>
                <div style={{ maxWidth: '100%', lineHeight: 1.2, display: 'grid', justifyItems: 'center', gap: 2 }}>
                  <div style={statusLineStyle(ds.color, 600)}>{renderStatusValue(ds.text)}</div>
                  <div style={{ ...statusLineStyle('var(--text3)', 500), fontSize: 10, marginTop: 2 }}>{renderStatusValue(ds.secondaryText)}</div>
                  {ds.showIssueIcon && <span title={ds.issueTooltip} style={{ display: 'inline-grid', placeItems: 'center', width: 14, height: 14, borderRadius: '50%', background: 'rgba(239,68,68,.12)', color: '#ef4444', fontSize: 10, fontWeight: 700, lineHeight: 1, cursor: 'help' }}>!</span>}
                </div>
              </div>
              ); })()}
              {platformFilter !== 'tiktok' && (
                <>
                  <div style={{ display: hiddenCols.has('link') ? 'none' : 'flex', flex: `0 0 ${W_BUDGET}px`, padding: '8px 6px', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: ad.link ? 'var(--text2)' : 'var(--text4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ad.link || '—'}>
                      {ad.link || '—'}
                    </span>
                    {canShowQuickEdit(rowPlatform, {
                      kind: 'field',
                      section: 'ad',
                      field: 'ad.link',
                      label: '链接',
                    }) && canWriteRow && (
                      <button
                        type="button"
                        style={quickEditButtonStyle}
                        onClick={() => openQuickEditModal(rowPlatform, 'ad', ad.id, ad.name || ad.third_ad_id, {
                          kind: 'field',
                          section: 'ad',
                          field: 'ad.link',
                          label: '链接',
                        })}
                      >
                        编辑
                      </button>
                    )}
                  </div>
                  <div style={{ display: hiddenCols.has('url_tags') ? 'none' : 'flex', flex: `0 0 ${W_BUDGET}px`, padding: '8px 6px', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: ad.url_tags ? 'var(--text2)' : 'var(--text4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ad.url_tags || '—'}>
                      {ad.url_tags || '—'}
                    </span>
                    {canShowQuickEdit(rowPlatform, {
                      kind: 'field',
                      section: 'ad',
                      field: 'ad.url_tags',
                      label: '网址参数',
                    }) && canWriteRow && (
                      <button
                        type="button"
                        style={quickEditButtonStyle}
                        onClick={() => openQuickEditModal(rowPlatform, 'ad', ad.id, ad.name || ad.third_ad_id, {
                          kind: 'field',
                          section: 'ad',
                          field: 'ad.url_tags',
                          label: '网址参数',
                        })}
                      >
                        编辑
                      </button>
                    )}
                  </div>
                </>
              )}
              {rowPlatform === 'tiktok' && (
                <div style={{ display: hiddenCols.has('landing_page_url') ? 'none' : 'flex', flex: `0 0 ${W_BUDGET}px`, padding: '8px 6px', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: 4, minWidth: 0 }}>
                  <span style={{ fontSize: 11, color: ad.landing_page_url ? 'var(--text2)' : 'var(--text4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }} title={ad.landing_page_url || '—'}>
                    {ad.landing_page_url || '—'}
                  </span>
                  {canWriteRow && (
                    <button
                      type="button"
                      style={quickEditButtonStyle}
                      onClick={() => openQuickEditModal(rowPlatform, 'ad', ad.id, ad.name || ad.third_ad_id, {
                        kind: 'field',
                        section: 'ad',
                        field: 'ad.landing_page_url',
                        label: '目标页面URL',
                      })}
                    >
                      编辑目标URL
                    </button>
                  )}
                </div>
              )}
              {/* 动态标签列（ad 级） */}
              <div style={{ display: hiddenCols.has('spend') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtMoney(ad.spend ?? 0)}</span></div>
              <div style={{ display: hiddenCols.has('roas') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><ROASCell roas={ad.roas} /></div>
              <div style={{ display: hiddenCols.has('cpc') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtMoney(ad.cpc ?? 0)}</span></div>
              <div style={{ display: hiddenCols.has('cpm') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtMoney(ad.cpm ?? 0)}</span></div>
              <div style={{ display: hiddenCols.has('cpa') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtMoney(ad.cpa ?? 0)}</span></div>
              <div style={{ display: hiddenCols.has('purchase_count') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtCount(ad.purchase_count ?? 0)}</span></div>
              {platformFilter === 'tiktok' && (
                <>
                  <div style={{ display: hiddenCols.has('cta_registration') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtCount(ad.cta_registration ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtCount(ad.cta_purchase ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase_value') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><span style={{ fontSize: 11 }}>{fmtMoney(ad.cta_purchase_value ?? 0)}</span></div>
                  <div style={{ display: hiddenCols.has('cta_purchase_roas') ? 'none' : undefined, flex: `0 0 ${W_METRIC}px`, textAlign: 'right', padding: '10px 6px' }}><ROASCell roas={ad.cta_purchase_roas} /></div>
                </>
              )}
              {activeTagCols.map(td => {
                const val = ad.tags?.[td.key];
                const optLabel = val ? (findTagOptionLabel(td.options, val) ?? val) : undefined;
                return (
                  <div key={td.key} style={{ display: hiddenCols.has(`tag_${td.key}`) ? 'none' : 'flex', flex: `0 0 ${W_TAG_COL}px`, padding: '8px 6px', alignItems: 'center' }}>
                    {optLabel ? (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,.1)', color: '#2563eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: W_TAG_COL - 12 }}>
                        {optLabel}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text4)' }}>—</span>
                    )}
                  </div>
                );
              })}
              <div style={{ flex: 1 }} />
              <div style={{ flex: `0 0 ${W_OPS}px`, position: colSticky, right: 0, zIndex: 1, background: pinBg, borderLeft: '2px solid var(--border2)', boxShadow: '-2px 0 4px rgba(0,0,0,.04)', padding: '6px 4px 6px 4px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2, flexWrap: 'wrap', alignSelf: 'stretch', rowGap: 3 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={opsTextStyle(isMobile)}
                  onClick={() => {
                    setDiagAd(ad);
                    setDiagMetrics({ spend: ad.spend ?? 0, roas: ad.roas ?? 0, cpc: ad.cpc ?? 0, cpm: ad.cpm ?? 0, cpa: ad.cpa ?? 0, purchase_count: ad.purchase_count ?? 0 });
                  }}
                >
                  AI诊断
                </button>
                <>
                    {canCopyRow && ops.copy && !isLocalAd && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile)}
                        onClick={() => openCopyModal(rowPlatform, 'ad', ad.id, ad.name || ad.third_ad_id)}
                      >
                        复制
                      </button>
                    )}
                    {canWriteRow && ops.edit && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--primary-light)' })}
                        onClick={() => openEditPage(rowPlatform, 'ad', ad.id, { campaignStatus: filterCampaign?.publishStatus, adsetStatus: filterAdset?.publishStatus, adStatus: ad.publish_status })}
                      >
                        编辑
                      </button>
                    )}
                    {canWriteRow && ops.toggle && ad.publish_status === 'published' && !isLocalAd && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: isDeliveryActive(ad.status, ad.publish_status) ? 'var(--text3)' : 'var(--primary-light)' })}
                        disabled={!!togglingIDs[`ad-${ad.id}`]}
                        onClick={() => { void toggleStatus(rowPlatform, 'ad', ad.id, deliveryToggleStatus(ad.status, ad.publish_status)); }}
                      >
                        {togglingIDs[`ad-${ad.id}`] ? '处理中…' : isDeliveryActive(ad.status, ad.publish_status) ? '关闭' : '投放'}
                      </button>
                    )}
                    {canWriteRow && ops.publish && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--primary-light)' })}
                        title={ad.publish_error}
                        disabled={!!retryingAdIDs[ad.id] || !!deletingAdIDs[ad.id]}
                        onClick={() => {
                          void ((rowPlatform === 'facebook')
                            ? retryPublishAd(ad.id)
                            : publishEntity(rowPlatform, 'ad', ad.id));
                        }}
                      >
                        {retryingAdIDs[ad.id] ? '处理中…' : '投放'}
                      </button>
                    )}
                    {canWriteRow && ops.delete && rowPlatform !== 'google' && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={opsTextStyle(isMobile, { color: 'var(--red, #ef4444)' })}
                        onClick={() => { void deleteLocalAd(ad); }}
                        disabled={!!retryingAdIDs[ad.id] || !!deletingAdIDs[ad.id]}
                      >
                        {deletingAdIDs[ad.id] ? '删除中' : '删除'}
                      </button>
                    )}
                  </>
              </div>
            </div>
          );
        })}

      </div>{/* end scroll wrapper */}
      {!loading && (
        <div
          ref={summaryBarRef}
          style={{
            position: isSummaryFloating ? 'fixed' : 'static',
            left: isSummaryFloating ? (isMobile ? 0 : summaryViewportBounds.left) : undefined,
            right: isSummaryFloating ? (isMobile ? 0 : summaryViewportBounds.right) : undefined,
            bottom: isSummaryFloating ? 0 : undefined,
            zIndex: isSummaryFloating ? 20 : 2,
            borderTop: '1px solid var(--border)',
            background: 'rgba(255,255,255,.97)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 -8px 24px rgba(15,23,42,.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 0,
              overflowX: 'auto',
            }}
          >
            {summaryItems.map((item, index) => (
              <div
                key={item.key}
                style={{
                  flex: index === 0 ? '0 0 220px' : '0 0 132px',
                  minWidth: index === 0 ? 220 : 132,
                  padding: '12px 14px',
                  borderRight: index === summaryItems.length - 1 ? 'none' : '1px solid rgba(15,23,42,.06)',
                  display: 'grid',
                  gap: 6,
                  alignContent: 'center',
                }}
              >
                <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{item.label}</span>
                <span style={{ fontSize: index === 0 ? 16 : 15, fontWeight: 700, color: 'var(--text)' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Pagination — outside scroll, inside card */}
      <div style={{ borderTop: total === 0 ? '1px solid var(--border)' : 'none' }}>
        <Pagination page={page} totalPages={Math.max(1, totalPage)} onChange={(p) => setPage(p)} total={`共 ${total} 条`} pageSize={size} pageSizeOptions={[10, 20, 50, 100]} onPageSizeChange={nextSize => { setSize(nextSize); setCampaignPage(1); setAdsetPage(1); setAdPage(1); }} />
      </div>
      </div>

      {showBatchTagModal && (
        <div className="modal-overlay open" onClick={closeBatchTagModal}>
          <div className="modal" style={{ width: 680, maxWidth: 'calc(100vw - 32px)', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '1px solid rgba(15,23,42,.06)', background: 'rgba(255,255,255,.96)', padding: '14px 18px' }}>
              <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>批量打标</span>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 22,
                    padding: '0 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--primary-light)',
                    background: 'rgba(18,16,184,.08)',
                  }}>
                    {activeTab === 'campaign' ? '广告计划' : activeTab === 'adset' ? '广告组' : '广告'}
                  </span>
                </h3>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text2)' }}>
                  为当前勾选的数据统一补充层级标签，未填写的标签项会保持原样。
                </div>
              </div>
              <button className="modal-close" onClick={closeBatchTagModal} disabled={batchTagSubmitting}>&times;</button>
            </div>
            <div className="modal-body" style={{ background: '#fff', padding: '16px 18px' }}>
              {!batchTagHasAnyDefs ? (
                <div style={{
                  color: 'var(--text3)',
                  fontSize: 12,
                  border: '1px dashed var(--border)',
                  borderRadius: 14,
                  padding: '20px 16px',
                  textAlign: 'center',
                  background: 'rgba(255,255,255,.02)',
                }}>
                  当前层级暂无可配置标签
                </div>
              ) : isTikTokDualLevelBatchTag ? (
                <div style={{ display: 'grid', gap: 14 }}>
                  {([
                    { level: 'campaign' as const, title: '广告系列标签' },
                    { level: 'adset' as const, title: '广告组标签' },
                  ]).map(({ level, title }) => {
                    const defs = batchTagDefsByLevel[level];
                    if (!defs.length) return null;
                    return (
                      <div key={level} style={{ display: 'grid', gap: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>{title}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                          {defs.map((td) => (
                            <div key={`${level}_${td.key}`} style={{
                              marginBottom: 0,
                              border: batchTagFormsByLevel[level][td.key] ? '1px solid rgba(18,16,184,.18)' : '1px solid rgba(15,23,42,.08)',
                              borderRadius: 12,
                              padding: '12px',
                              background: '#fff',
                              boxShadow: batchTagFormsByLevel[level][td.key]
                                ? '0 6px 16px rgba(18,16,184,.05)'
                                : '0 2px 8px rgba(15,23,42,.035)',
                              transition: 'all .2s ease',
                              fontSize: 12,
                            }}>
                              <label className="form-label" style={{ marginBottom: 8, fontSize: 12 }}>{td.label}</label>
                              <MultiSelect
                                single
                                options={flattenTagOptions(td.options)}
                                value={batchTagFormsByLevel[level][td.key] ? [batchTagFormsByLevel[level][td.key]] : []}
                                onChange={(vals) => {
                                  const value = vals[0] ?? '';
                                  setBatchTagFormsByLevel((prev) => ({
                                    ...prev,
                                    [level]: { ...prev[level], [td.key]: value },
                                  }));
                                }}
                                placeholder={`请选择${td.label}`}
                                style={{ width: '100%', fontSize: 12 }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                  {batchTagCols.map((td) => (
                    <div key={td.key} style={{
                      marginBottom: 0,
                      border: batchTagForm[td.key] ? '1px solid rgba(18,16,184,.18)' : '1px solid rgba(15,23,42,.08)',
                      borderRadius: 12,
                      padding: '12px',
                      background: '#fff',
                      boxShadow: batchTagForm[td.key]
                        ? '0 6px 16px rgba(18,16,184,.05)'
                        : '0 2px 8px rgba(15,23,42,.035)',
                      transition: 'all .2s ease',
                      fontSize: 12,
                    }}>
                      <label className="form-label" style={{ marginBottom: 8, fontSize: 12 }}>{td.label}</label>
                      <MultiSelect
                        single
                        options={flattenTagOptions(td.options)}
                        value={batchTagForm[td.key] ? [batchTagForm[td.key]] : []}
                        onChange={(vals) => {
                          const value = vals[0] ?? '';
                          setBatchTagForm((prev) => ({ ...prev, [td.key]: value }));
                        }}
                        placeholder={`请选择${td.label}`}
                        style={{ width: '100%', fontSize: 12 }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ borderTop: '1px solid rgba(15,23,42,.06)', background: '#fff', padding: '14px 18px' }}>
              <div style={{ marginRight: 'auto', fontSize: 12, color: 'var(--text3)' }}>
                {(isTikTokDualLevelBatchTag ? batchTagFilledCountByLevel : batchTagFilledCount) > 0 ? `本次将更新 ${isTikTokDualLevelBatchTag ? batchTagFilledCountByLevel : batchTagFilledCount} 个标签维度` : '先选择至少一个标签值再提交'}
              </div>
              <button className="btn btn-outline" onClick={closeBatchTagModal} disabled={batchTagSubmitting}>取消</button>
              <button className="btn btn-primary" onClick={() => void submitBatchTags()} disabled={batchTagSubmitting || !batchTagHasAnyDefs}>
                {batchTagSubmitting ? '保存中...' : '确认打标'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Ad Modal ──────────────────────────────────────────────── */}
      {copyModal && (
        <div className="modal-overlay open" onClick={closeCopyModal}>
          <div className="modal" style={{ width: 520, maxWidth: 'calc(100vw - 32px)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>复制{getCopyPlatformLabel(copyModal.platform)} {copyModal.entity === 'campaign' ? 'Campaign' : copyModal.entity === 'adset' ? 'Ad Set' : 'Ad'}</h3>
              <button className="modal-close" onClick={closeCopyModal} disabled={!!copyingKey}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">来源对象</label>
                <input className="form-input" value={copyModal.sourceName} disabled />
              </div>
              <div className="form-group">
                <label className="form-label">目标{getCopyPlatformLabel(copyModal.platform)}账户</label>
                <MultiSelect
                  single
                  options={copyFilteredAccounts.map((item) => ({ value: item.account_id, label: item.account_name || item.account_id }))}
                  value={copyModal.targetAccountID ? [copyModal.targetAccountID] : []}
                  onChange={(vals) => setCopyModal((prev) => prev ? { ...prev, targetAccountID: vals[0] ?? '', targetCampaignID: '', targetAdSetID: '' } : prev)}
                  placeholder={`请选择目标${getCopyPlatformLabel(copyModal.platform)}账户`}
                />
              </div>
              {copyModal.entity !== 'campaign' && (
                <div className="form-group">
                  <label className="form-label">目标 Campaign</label>
                  <MultiSelect
                    single
                    options={copyAvailableCampaigns.map((item) => ({ value: String(item.id), label: item.name || item.third_ad_campaign_id }))}
                    value={copyModal.targetCampaignID ? [copyModal.targetCampaignID] : []}
                    onChange={(vals) => setCopyModal((prev) => prev ? { ...prev, targetCampaignID: vals[0] ?? '', targetAdSetID: '' } : prev)}
                    placeholder="请选择目标 Campaign"
                    style={copyLoadingTargets ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                  />
                </div>
              )}
              {copyModal.entity === 'ad' && (
                <div className="form-group">
                  <label className="form-label">目标 Ad Set</label>
                  <MultiSelect
                    single
                    options={copyAvailableAdSets.map((item) => ({ value: String(item.id), label: item.name || item.third_ad_set_id }))}
                    value={copyModal.targetAdSetID ? [copyModal.targetAdSetID] : []}
                    onChange={(vals) => setCopyModal((prev) => prev ? { ...prev, targetAdSetID: vals[0] ?? '' } : prev)}
                    placeholder="请选择目标 Ad Set"
                    style={copyLoadingTargets ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                  />
                </div>
              )}
              {copyLoadingTargets && <div className="form-hint">正在加载可选目标...</div>}
              <div className="form-hint">复制后的对象默认为未发布，需要手动点击发布。</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeCopyModal} disabled={!!copyingKey}>取消</button>
              <button className="btn btn-primary" onClick={() => void submitCopy()} disabled={!!copyingKey}>
                {copyingKey ? '复制中...' : '确认复制'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div className="modal-overlay open" onClick={() => !savingEdit && setEditModal(null)}>
          <div className="modal" style={{ width: 760, maxWidth: 'calc(100vw - 32px)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{editModal.quickEdit ? `快捷编辑 · ${editModal.quickEdit.label}` : `编辑 ${editModal.entity === 'campaign' ? 'Campaign' : editModal.entity === 'adset' ? 'Ad Set' : 'Ad'}`}</h3>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text3)' }}>{editModal.title}</div>
              </div>
              <button className="modal-close" onClick={() => !savingEdit && setEditModal(null)} disabled={savingEdit}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {editLoading && <div className="form-hint">加载编辑详情中...</div>}

              {!editLoading && editModal.quickEdit && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  {renderQuickEditBody()}
                </div>
              )}

              {!editModal.quickEdit && editField('campaign') && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>广告系列</div>
                  {hasField('campaign.name') && (
                    <div className="form-group">
                      <label className="form-label">名称</label>
                      <input className="form-input" value={editModal.campaign.name} onChange={(e) => setEditModal((prev) => prev ? { ...prev, campaign: { ...prev.campaign, name: e.target.value } } : prev)} />
                    </div>
                  )}
                  <div className="form-row">
                    {hasField('campaign.daily_budget') && (
                      <div className="form-group">
                        <label className="form-label">日预算 (USD)</label>
                        <input className="form-input" type="number" value={editModal.campaign.daily_budget} onChange={(e) => setEditModal((prev) => prev ? { ...prev, campaign: { ...prev.campaign, daily_budget: Number(e.target.value) || 0 } } : prev)} />
                      </div>
                    )}
                    {hasField('campaign.campaign_lifetime_budget') && (
                      <div className="form-group">
                        <label className="form-label">总预算 (USD)</label>
                        <input className="form-input" type="number" value={editModal.campaign.campaign_lifetime_budget} onChange={(e) => setEditModal((prev) => prev ? { ...prev, campaign: { ...prev.campaign, campaign_lifetime_budget: Number(e.target.value) || 0 } } : prev)} />
                      </div>
                    )}
                    {hasField('campaign.spend_cap') && (
                      <div className="form-group">
                        <label className="form-label">花费上限 (USD)</label>
                        <input className="form-input" type="number" value={editModal.campaign.spend_cap} onChange={(e) => setEditModal((prev) => prev ? { ...prev, campaign: { ...prev.campaign, spend_cap: Number(e.target.value) || 0 } } : prev)} />
                      </div>
                    )}
                  </div>
                  {hasField('campaign.special_ad_categories') && (
                    <div className="form-group">
                      <label className="form-label">特殊广告类别</label>
                      <MultiSelect
                        single
                        options={EDIT_SPECIAL_AD_CATEGORIES.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.campaign.special_ad_categories[0] ? [editModal.campaign.special_ad_categories[0]] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, campaign: { ...prev.campaign, special_ad_categories: vals[0] ?? '' ? [vals[0] ?? ''] : [] } } : prev)}
                        placeholder="无"
                      />
                    </div>
                  )}
                  {/* ── 动态广告标签（campaign 级）── */}
                  {tagConfig.filter(t => t.level === 'campaign').length > 0 && (
                    <>
                      <div style={{ gridColumn: '1/-1', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text2)' }}>广告标签</div>
                      <div className="tag-grid" style={{ gridColumn: '1/-1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {tagConfig.filter(t => t.level === 'campaign').map(td => {
                          const flatOpts = td.options.flatMap(o =>
                            o.children && o.children.length > 0
                              ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                              : [{ value: o.value, label: o.label }]
                          );
                          return (
                            <div key={td.key} className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label">{td.label}</label>
                              {isPlacementTag(td) ? (
                                <MultiSelect
                                  searchable
                                  clearable
                                  tagDisplay
                                  placeholder="请选择版位（可多选）"
                                  options={flatOpts}
                                  value={parseMultiTagValue(editModal.campaign.tags?.[td.key])}
                                  onChange={(vals) => setEditModal((prev) => prev ? { ...prev, campaign: { ...prev.campaign, tags: setTagValue(prev.campaign.tags, td.key, serializeMultiTagValue(vals)) } } : prev)}
                                />
                              ) : (
                                <MultiSelect single placeholder="请选择"
                                  options={flatOpts}
                                  value={editModal.campaign.tags?.[td.key] ? [editModal.campaign.tags[td.key]!] : []}
                                  onChange={(vals) => setEditModal((prev) => prev ? { ...prev, campaign: { ...prev.campaign, tags: { ...prev.campaign.tags, [td.key]: vals[0] ?? undefined } } } : prev)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {!editModal.quickEdit && editField('adset') && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>广告组</div>
                  {hasField('adset.name') && (
                    <div className="form-group">
                      <label className="form-label">名称</label>
                      <input className="form-input" value={editModal.adset.name} onChange={(e) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, name: e.target.value } } : prev)} />
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">预算类型</label>
                      <MultiSelect
                        single
                        options={[{ value: 'daily', label: 'Daily' }, { value: 'lifetime', label: 'Lifetime' }]}
                        value={editModal.adset.budget_type ? [editModal.adset.budget_type] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, budget_type: vals[0] ?? '' } } : prev)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">日预算 (USD)</label>
                      <input className="form-input" type="number" value={editModal.adset.adset_daily_budget} onChange={(e) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, adset_daily_budget: Number(e.target.value) || 0 } } : prev)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">总预算 (USD)</label>
                      <input className="form-input" type="number" value={editModal.adset.adset_lifetime_budget} onChange={(e) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, adset_lifetime_budget: Number(e.target.value) || 0 } } : prev)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">计费事件</label>
                      <MultiSelect
                        single
                        options={EDIT_BILLING_EVENTS.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.adset.billing_event ? [editModal.adset.billing_event] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, billing_event: vals[0] ?? '' } } : prev)}
                        placeholder="默认"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">优化目标</label>
                      <MultiSelect
                        single
                        options={EDIT_OPTIMIZATION_GOALS.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.adset.optimization_goal ? [editModal.adset.optimization_goal] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, optimization_goal: vals[0] ?? '' } } : prev)}
                        placeholder="默认"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">出价策略</label>
                      <MultiSelect
                        single
                        options={EDIT_BID_STRATEGIES.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.adset.bid_strategy ? [editModal.adset.bid_strategy] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, bid_strategy: vals[0] ?? '' } } : prev)}
                        placeholder="默认"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">地区</label>
                      {editModal.adset.countries.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                          {editModal.adset.countries.map((key) => {
                            const geo = editGeoLocations.find((g) => (g.country_code || g.key) === key);
                            return (
                              <span key={key} style={{
                                display: 'flex', alignItems: 'center', gap: 3, fontSize: 11,
                                padding: '2px 7px', borderRadius: 16, background: 'var(--primary-bg)', border: '1px solid rgba(18,16,184,.3)', color: 'var(--primary-light)',
                              }}>
                                {geo ? geo.name : key}
                                <button type="button" onClick={() => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, countries: prev.adset.countries.filter((x) => x !== key) } } : prev)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 11, lineHeight: 1 }}>×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <div ref={editGeoDropdownRef} style={{ position: 'relative' }}>
                        <input
                          className="form-input"
                          placeholder={editGeoLoading ? '加载地区中...' : '搜索国家/地区...'}
                          value={editGeoFilter}
                          onChange={(e) => setEditGeoFilter(e.target.value)}
                          onFocus={() => setEditGeoDropdownOpen(true)}
                          disabled={editGeoLoading}
                        />
                        {editGeoDropdownOpen && editGeoLocations.length > 0 && (
                          <div
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                              marginTop: 2, maxHeight: 200, overflowY: 'auto', padding: 4,
                              boxShadow: '0 4px 16px rgba(0,0,0,.15)',
                            }}>
                            {editGeoLocations
                              .filter((g) => {
                                if (!editGeoFilter) return true;
                                const q = editGeoFilter.toLowerCase();
                                return g.name.toLowerCase().includes(q) || (g.country_code || g.key).toLowerCase().includes(q);
                              })
                              .map((g) => {
                                const code = g.country_code || g.key;
                                const sel = editModal.adset.countries.includes(code);
                                return (
                                  <div key={code} style={{
                                    display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                                    background: sel ? 'var(--primary-bg)' : 'transparent',
                                  }}
                                    onClick={() => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, countries: prev.adset.countries.includes(code) ? prev.adset.countries.filter((x) => x !== code) : [...prev.adset.countries, code] } } : prev)}
                                  >
                                    <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${sel ? 'var(--primary)' : 'var(--border)'}`, background: sel ? 'var(--primary)' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, lineHeight: 1, flexShrink: 0 }}>{sel ? '✓' : ''}</span>
                                    <span>{g.name}</span>
                                    <span style={{ color: 'var(--text3)', fontSize: 10 }}>({code})</span>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Genders (逗号分隔 1/2)</label>
                      <input className="form-input" value={editModal.adset.genders.join(',')} onChange={(e) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, genders: parseNumCSV(e.target.value) } } : prev)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Publisher Platforms</label>
                      <MultiSelect
                        single
                        options={EDIT_PUBLISHER_PLATFORMS.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.adset.publisher_platforms[0] ? [editModal.adset.publisher_platforms[0]] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, publisher_platforms: vals[0] ?? '' ? [vals[0] ?? ''] : [] } } : prev)}
                        placeholder="默认"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Facebook Position</label>
                      <MultiSelect
                        single
                        options={EDIT_FB_POSITIONS.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.adset.facebook_positions[0] ? [editModal.adset.facebook_positions[0]] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, facebook_positions: vals[0] ?? '' ? [vals[0] ?? ''] : [] } } : prev)}
                        placeholder="默认"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Instagram Position</label>
                      <MultiSelect
                        single
                        options={EDIT_IG_POSITIONS.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.adset.instagram_positions[0] ? [editModal.adset.instagram_positions[0]] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, instagram_positions: vals[0] ?? '' ? [vals[0] ?? ''] : [] } } : prev)}
                        placeholder="默认"
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginBottom: 8 }}>自定义受众</div>
                    {editCustomAudiencesLoading ? (
                      <div className="form-hint">加载自定义受众中...</div>
                    ) : editCustomAudiences.length === 0 ? (
                      <div className="form-hint">暂无自定义受众（需在 Facebook Ads Manager 中创建或同步）</div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label">包含受众</label>
                          {editModal.adset.custom_audience_ids.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                              {editModal.adset.custom_audience_ids.map((id) => {
                                const audience = editCustomAudienceByID.get(id);
                                return (
                                  <span key={id} style={{
                                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                                    padding: '3px 8px', borderRadius: 20, background: 'var(--primary-bg)', border: '1px solid rgba(18,16,184,.3)', color: 'var(--primary-light)',
                                  }}>
                                    {audience ? formatEditCustomAudienceLabel(audience) : id}
                                    <button
                                      type="button"
                                      onClick={() => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, custom_audience_ids: prev.adset.custom_audience_ids.filter((item) => item !== id) } } : prev)}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 12, lineHeight: 1 }}
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          <input
                            className="form-input"
                            style={{ marginBottom: 6 }}
                            placeholder="搜索并多选包含受众"
                            value={editIncludeAudienceFilter}
                            onChange={(e) => setEditIncludeAudienceFilter(e.target.value)}
                          />
                          <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
                            {editIncludeAudienceOptions.length === 0 ? (
                              <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>暂无匹配受众</div>
                            ) : editIncludeAudienceOptions.slice(0, 120).map((audience) => {
                              const checked = editModal.adset.custom_audience_ids.includes(audience.id);
                              const disabled = editModal.adset.excluded_custom_audience_ids.includes(audience.id);
                              return (
                                <label key={audience.id} style={{
                                  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px', borderRadius: 6,
                                  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
                                  background: checked ? 'var(--primary-bg)' : 'transparent',
                                }}>
                                  <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleEditIncludedAudience(audience.id)} style={{ marginTop: 2 }} />
                                  <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text)' }}>{formatEditCustomAudienceLabel(audience)}</span>
                                    {(audience.subtype || audience.description) && (
                                      <span style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{[audience.subtype, audience.description].filter(Boolean).join(' · ')}</span>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label">排除受众</label>
                          {editModal.adset.excluded_custom_audience_ids.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                              {editModal.adset.excluded_custom_audience_ids.map((id) => {
                                const audience = editCustomAudienceByID.get(id);
                                return (
                                  <span key={id} style={{
                                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                                    padding: '3px 8px', borderRadius: 20, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', color: 'var(--danger)',
                                  }}>
                                    {audience ? formatEditCustomAudienceLabel(audience) : id}
                                    <button
                                      type="button"
                                      onClick={() => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, excluded_custom_audience_ids: prev.adset.excluded_custom_audience_ids.filter((item) => item !== id) } } : prev)}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 12, lineHeight: 1 }}
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          <input
                            className="form-input"
                            style={{ marginBottom: 6 }}
                            placeholder="搜索并多选排除受众"
                            value={editExcludeAudienceFilter}
                            onChange={(e) => setEditExcludeAudienceFilter(e.target.value)}
                          />
                          <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
                            {editExcludeAudienceOptions.length === 0 ? (
                              <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>暂无匹配受众</div>
                            ) : editExcludeAudienceOptions.slice(0, 120).map((audience) => {
                              const checked = editModal.adset.excluded_custom_audience_ids.includes(audience.id);
                              const disabled = editModal.adset.custom_audience_ids.includes(audience.id);
                              return (
                                <label key={audience.id} style={{
                                  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px', borderRadius: 6,
                                  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
                                  background: checked ? 'rgba(239,68,68,.08)' : 'transparent',
                                }}>
                                  <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleEditExcludedAudience(audience.id)} style={{ marginTop: 2 }} />
                                  <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text)' }}>{formatEditCustomAudienceLabel(audience)}</span>
                                    {(audience.subtype || audience.description) && (
                                      <span style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{[audience.subtype, audience.description].filter(Boolean).join(' · ')}</span>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                          <div className="form-hint">被排除的受众不会看到此广告组的广告</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* ── 动态广告标签（adset 级）── */}
                  {tagConfig.filter(t => t.level === 'adset').length > 0 && (
                    <>
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text2)' }}>广告标签</div>
                      <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                        {tagConfig.filter(t => t.level === 'adset').map(td => {
                          const flatOpts = td.options.flatMap(o =>
                            o.children && o.children.length > 0
                              ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                              : [{ value: o.value, label: o.label }]
                          );
                          return (
                            <div key={td.key} className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label">{td.label}</label>
                              {isPlacementTag(td) ? (
                                <MultiSelect
                                  searchable
                                  clearable
                                  tagDisplay
                                  placeholder="请选择版位（可多选）"
                                  options={flatOpts}
                                  value={parseMultiTagValue(editModal.adset.tags?.[td.key])}
                                  onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, tags: setTagValue(prev.adset.tags, td.key, serializeMultiTagValue(vals)) } } : prev)}
                                />
                              ) : (
                                <MultiSelect single placeholder="请选择"
                                  options={flatOpts}
                                  value={editModal.adset.tags?.[td.key] ? [editModal.adset.tags[td.key]!] : []}
                                  onChange={(vals) => setEditModal((prev) => prev ? { ...prev, adset: { ...prev.adset, tags: { ...prev.adset.tags, [td.key]: vals[0] ?? undefined } } } : prev)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {!editModal.quickEdit && editField('ad') && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>广告</div>
                  <div className="form-group">
                    <label className="form-label">名称</label>
                    <input className="form-input" value={editModal.ad.name} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, name: e.target.value } } : prev)} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">图片 Hash</label>
                      <input className="form-input" value={editModal.ad.image_hash} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, image_hash: e.target.value } } : prev)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">视频 ID</label>
                      <input className="form-input" value={editModal.ad.video_id} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, video_id: e.target.value } } : prev)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">CTA</label>
                      <MultiSelect
                        single
                        options={EDIT_CTA_TYPES.map((item) => ({ value: item.value, label: item.label }))}
                        value={editModal.ad.cta_type ? [editModal.ad.cta_type] : []}
                        onChange={(vals) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, cta_type: vals[0] ?? '' } } : prev)}
                        placeholder="默认"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">主文案</label>
                    <textarea className="form-textarea" value={editModal.ad.message} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, message: e.target.value } } : prev)} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">标题</label>
                      <input className="form-input" value={editModal.ad.headline} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, headline: e.target.value } } : prev)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">链接</label>
                      <input className="form-input" value={editModal.ad.link} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, link: e.target.value } } : prev)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">网址参数</label>
                    <input className="form-input" value={editModal.ad.url_tags} onChange={(e) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, url_tags: e.target.value } } : prev)} />
                  </div>
                  {/* ── 动态广告标签（ad 级）── */}
                  {tagConfig.filter(t => t.level === 'ad').length > 0 && (
                    <>
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text2)' }}>广告标签</div>
                      <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                        {tagConfig.filter(t => t.level === 'ad').map(td => {
                          const flatOpts = td.options.flatMap(o =>
                            o.children && o.children.length > 0
                              ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                              : [{ value: o.value, label: o.label }]
                          );
                          return (
                            <div key={td.key} className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label">{td.label}</label>
                              {isPlacementTag(td) ? (
                                <MultiSelect
                                  searchable
                                  clearable
                                  tagDisplay
                                  placeholder="请选择版位（可多选）"
                                  options={flatOpts}
                                  value={parseMultiTagValue(editModal.ad.tags?.[td.key])}
                                  onChange={(vals) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, tags: setTagValue(prev.ad.tags, td.key, serializeMultiTagValue(vals)) } } : prev)}
                                />
                              ) : (
                                <MultiSelect single placeholder="请选择"
                                  options={flatOpts}
                                  value={editModal.ad.tags?.[td.key] ? [editModal.ad.tags[td.key]!] : []}
                                  onChange={(vals) => setEditModal((prev) => prev ? { ...prev, ad: { ...prev.ad, tags: { ...prev.ad.tags, [td.key]: vals[0] ?? undefined } } } : prev)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(null)} disabled={savingEdit}>取消</button>
              <button className="btn btn-primary" onClick={() => void saveEditModal()} disabled={savingEdit || editLoading}>
                {savingEdit ? '保存中...' : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveViewModal && (
        <div className="modal-overlay open" onClick={() => !saveViewSubmitting && setShowSaveViewModal(false)}>
          <div className="modal" style={{ width: 520, maxWidth: 'calc(100vw - 32px)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>保存筛选视图</h3>
              <button className="modal-close" onClick={() => !saveViewSubmitting && setShowSaveViewModal(false)} disabled={saveViewSubmitting}>&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
              {selectedSavedViewName !== SAVED_VIEW_ALL_NAME && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" checked={saveViewMode === 'overwrite'} onChange={() => setSaveViewMode('overwrite')} />
                  <span>覆盖已有视图「{selectedSavedViewName}」</span>
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="radio" checked={saveViewMode === 'new'} onChange={() => setSaveViewMode('new')} />
                <span>另存为新视图</span>
              </label>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">视图名称</label>
                <input
                  className="form-input"
                  value={saveViewMode === 'overwrite' ? selectedSavedViewName : saveViewNameInput}
                  onChange={(e) => setSaveViewNameInput(e.target.value)}
                  disabled={saveViewMode === 'overwrite'}
                  placeholder="请输入视图名称"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowSaveViewModal(false)} disabled={saveViewSubmitting}>取消</button>
              <button className="btn btn-primary" onClick={() => void submitSaveView()} disabled={saveViewSubmitting}>
                {saveViewSubmitting ? '保存中...' : '保存视图'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay open" onClick={() => !publishing && setShowCreateModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>新建广告（基于模板）</h3>
              <button className="modal-close" onClick={() => !publishing && setShowCreateModal(false)}>&times;</button>
            </div>
            <div style={{ padding: '12px 24px 0' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <div className="search-box" style={{ flex: 1 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                  <input placeholder="搜索历史广告模板..." value={templateSearch} onChange={(e) => setTemplateSearch(e.target.value)} />
                </div>
                <MultiSelect
                  single
                  options={[{ value: 'facebook', label: 'Facebook' }, { value: 'tiktok', label: 'TikTok' }, { value: 'google', label: 'Google' }]}
                  value={platformFilter ? [platformFilter] : []}
                  onChange={() => {}}
                  style={{ minWidth: 110, pointerEvents: 'none', opacity: 0.75 }}
                />
              </div>
              <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 8, maxHeight: 200, overflowY: 'auto', padding: 8 }}>
                {templateLoading && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 8 }}>模板加载中...</div>}
                {!templateLoading && templates.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 8 }}>当前账号暂无可用模板</div>}
                {!templateLoading && templates.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {templates.map((tpl) => (
                      <div key={tpl.id} onClick={() => onSelectTemplate(tpl)}
                        style={{ border: `1px solid ${selectedTemplateID === tpl.id ? 'rgba(18,16,184,.45)' : 'var(--border)'}`, borderRadius: 8, padding: '8px 10px', cursor: 'pointer', background: selectedTemplateID === tpl.id ? 'var(--primary-bg)' : 'rgba(0,0,0,.01)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span className="ch-tag fb" style={{ fontSize: 9, padding: '1px 6px' }}>FB</span>
                          <strong style={{ fontSize: 12 }}>{tpl.name || tpl.third_ad_campaign_id}</strong>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{tpl.account_name} · AdSet {tpl.adset_count} · Ad {tpl.ad_count}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedTemplate && (
                <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--primary-bg)', border: '1px solid rgba(18,16,184,.15)', fontSize: 12 }}>
                  基于模板：<strong style={{ color: 'var(--primary-light)' }}>{selectedTemplate.name || selectedTemplate.third_ad_campaign_id}</strong>
                </div>
              )}
            </div>
            <div className="wizard-steps" style={{ marginTop: 12 }}>
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className={`wizard-step ${wizardStep === step ? 'active' : wizardStep > step ? 'done' : ''}`}>
                  <div className="step-num">{step}</div>
                  <span className="step-label">{step === 1 ? '归属 & 渠道' : step === 2 ? '创意素材' : step === 3 ? '投放设置' : '确认发布'}</span>
                </div>
              ))}
            </div>
            <div className="modal-body">
              {wizardError && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  marginBottom: 16, padding: '12px 14px',
                  borderRadius: 8, border: '1px solid rgba(239,68,68,.4)',
                  background: 'rgba(239,68,68,.10)', color: 'var(--red)',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.5 }}>{wizardError}</span>
                </div>
              )}
              {wizardStep === 1 && (
                <div className="wizard-page active">
                  <div className="form-group">
                    <label className="form-label">所属广告系列 (Campaign)</label>
                    <MultiSelect
                      single
                      options={targetCampaigns.map((tpl) => ({ value: String(tpl.id), label: tpl.name || tpl.third_ad_campaign_id }))}
                      value={wizardForm.campaignID ? [wizardForm.campaignID] : []}
                      onChange={(vals) => setWizardForm((f) => ({ ...f, campaignID: vals[0] ?? '', adSetID: '' }))}
                      placeholder="-- 请选择广告系列 --"
                    />
                    {targetLoading && <div className="form-hint">正在加载当前账户下可投放的 Campaign...</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">所属广告组 (Ad Set)</label>
                    <MultiSelect
                      single
                      options={availableAdSets.map((it) => ({ value: String(it.id), label: it.name || it.third_ad_set_id }))}
                      value={wizardForm.adSetID ? [wizardForm.adSetID] : []}
                      onChange={(vals) => setWizardForm((f) => ({ ...f, adSetID: vals[0] ?? '' }))}
                      placeholder="-- 请选择广告组 --"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">渠道</label>
                    <input className="form-input" value={selectedPlatformLabel} disabled />
                    <div className="form-hint">模板已确定渠道，当前不支持跨渠道创建</div>
                  </div>
                </div>
              )}
              {wizardStep === 2 && (
                <div className="wizard-page active">
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <label className="form-label" style={{ marginBottom: 0 }}>已有素材（单选）</label>
                      <button className="btn btn-outline btn-sm" onClick={() => setShowUploadMaterialModal(true)} type="button">+ 创建新素材</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input className="form-input" placeholder="搜索素材名称 / Facebook 素材 ID" value={materialSearch} onChange={(e) => setMaterialSearch(e.target.value)} />
                    </div>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 8, maxHeight: 210, overflowY: 'auto' }}>
                      {materialLoading && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 8 }}>素材加载中...</div>}
                      {!materialLoading && materials.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 8 }}>当前模板暂无素材，请先上传新素材</div>}
                      {!materialLoading && materials.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          {materials.map((m) => (
                            <label key={`${m.id}-${m.facebook_media_id}`}
                              style={{ border: `1px solid ${selectedMaterialID === m.id ? 'rgba(18,16,184,.45)' : 'var(--border)'}`, borderRadius: 8, padding: '8px 10px', cursor: 'pointer', background: selectedMaterialID === m.id ? 'var(--primary-bg)' : 'rgba(0,0,0,.01)', display: 'block' }}>
                              <input type="radio" name="selected_material" checked={selectedMaterialID === m.id}
                                onChange={() => { setSelectedMaterialID(m.id); setWizardForm((prev) => ({ ...prev, material: m.material_name || prev.material })); }}
                                style={{ marginRight: 8 }} />
                              <strong style={{ fontSize: 12 }}>{m.material_name || '未命名素材'}</strong>
                              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                                {m.material_type?.toUpperCase() || '-'} ? {m.source === 'uploaded' ? '新上传' : '模板'} ? {m.facebook_media_id || '-'}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">广告名称</label>
                    <input className="form-input" value={wizardForm.adName} onChange={(e) => setWizardForm((f) => ({ ...f, adName: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">广告文案</label>
                    <textarea className="form-textarea" value={wizardForm.adCopy} onChange={(e) => setWizardForm((f) => ({ ...f, adCopy: e.target.value }))} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">CTA</label>
                      <MultiSelect
                        single
                        options={[
                          ...(wizardForm.cta && !['Shop Now', 'Learn More', 'Sign Up'].includes(wizardForm.cta) ? [{ value: wizardForm.cta, label: wizardForm.cta }] : []),
                          { value: 'Shop Now', label: 'Shop Now' },
                          { value: 'Learn More', label: 'Learn More' },
                          { value: 'Sign Up', label: 'Sign Up' },
                        ]}
                        value={wizardForm.cta ? [wizardForm.cta] : []}
                        onChange={(vals) => setWizardForm((f) => ({ ...f, cta: vals[0] ?? '' }))}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">目标页面URL</label>
                      <input className="form-input" value={wizardForm.landingURL} onChange={(e) => setWizardForm((f) => ({ ...f, landingURL: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}
              {wizardStep === 3 && (
                <div className="wizard-page active">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">站点</label>
                      <MultiSelect
                        single
                        options={[{ value: 'us', label: 'US' }, { value: 'uk', label: 'UK' }, { value: 'de', label: 'DE' }]}
                        value={wizardForm.site ? [wizardForm.site] : []}
                        onChange={(vals) => setWizardForm((f) => ({ ...f, site: vals[0] ?? '' }))}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">渠道</label>
                      <input className="form-input" value={selectedPlatformLabel} disabled />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">开始日期</label>
                      <input className="form-input" type="date" value={wizardForm.startDate} onChange={(e) => setWizardForm((f) => ({ ...f, startDate: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">结束日期</label>
                      <input className="form-input" type="date" value={wizardForm.endDate} onChange={(e) => setWizardForm((f) => ({ ...f, endDate: e.target.value }))} />
                    </div>
                  </div>
                </div>
              )}
              {wizardStep === 4 && (
                <div className="wizard-page active">
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>广告总览</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                      <div><span style={{ color: 'var(--text3)' }}>广告系列:</span> <strong>{selectedTargetCampaign?.name || '-'}</strong></div>
                      <div><span style={{ color: 'var(--text3)' }}>广告组:</span> <strong>{availableAdSets.find((a) => String(a.id) === wizardForm.adSetID)?.name || '-'}</strong></div>
                      <div><span style={{ color: 'var(--text3)' }}>广告名称:</span> <strong>{wizardForm.adName || '-'}</strong></div>
                      <div><span style={{ color: 'var(--text3)' }}>渠道:</span> <strong>Facebook</strong></div>
                      <div><span style={{ color: 'var(--text3)' }}>CTA:</span> <strong>{wizardForm.cta}</strong></div>
                      <div><span style={{ color: 'var(--text3)' }}>站点:</span> <strong>{wizardForm.site.toUpperCase()}</strong></div>
                      <div><span style={{ color: 'var(--text3)' }}>素材:</span> <strong>{materials.find((m) => m.id === selectedMaterialID)?.material_name || wizardForm.material || '-'}</strong></div>
                    </div>
                  </div>
                  <div style={{ padding: 12, background: 'var(--green-bg)', border: '1px solid rgba(16,185,129,.15)', borderRadius: 8, fontSize: 12 }}>
                    <strong style={{ color: 'var(--green)' }}>AI 预估:</strong> 预计首日 CTR 2.8%-3.5%，学习期后 CPA 可降至 $7-$9。
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" style={{ marginRight: 'auto', visibility: wizardStep > 1 ? 'visible' : 'hidden' }} onClick={wizardPrev} disabled={publishing}>← 上一步</button>
              <button className="btn btn-outline" onClick={() => setShowCreateModal(false)} disabled={publishing}>取消</button>
              <button className="btn btn-primary" onClick={() => void wizardNext()} disabled={publishing}>
                {wizardStep === 4 ? (publishing ? '发布中...' : '确认发布') : '下一步 →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload Material Modal ────────────────────────────────────────── */}
      {showUploadMaterialModal && (
        <div className="modal-overlay open" onClick={() => { if (!uploadingMaterial) { setShowUploadMaterialModal(false); setUploadMaterialFile(null); setUploadMaterialName(''); if (uploadFileInputRef.current) uploadFileInputRef.current.value = ''; } }}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>上传素材</h3>
              <button className="modal-close" onClick={() => { if (!uploadingMaterial) { setShowUploadMaterialModal(false); setUploadMaterialFile(null); setUploadMaterialName(''); if (uploadFileInputRef.current) uploadFileInputRef.current.value = ''; } }}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="upload-zone"
                style={uploadMaterialFile ? { borderColor: 'var(--green)', background: 'rgba(16,185,129,.04)' } : {}}
                onClick={() => uploadFileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragLeave={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0] || null;
                  if (f) { setUploadMaterialFile(f); if (!uploadMaterialName.trim()) setUploadMaterialName(f.name.replace(/\.[^/.]+$/, '')); setUploadMaterialType(f.type.startsWith('video') ? 'video' : 'image'); }
                }}>
                {uploadMaterialFile ? (
                  <>
                    <div className="uz-icon" style={{ color: 'var(--green)' }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 12 18 15 15"/><line x1="12" y1="12" x2="12" y2="18"/>
                      </svg>
                    </div>
                    <div className="uz-text" style={{ color: 'var(--green)', fontWeight: 600 }}>{uploadMaterialFile.name}</div>
                    <div className="uz-hint">{(uploadMaterialFile.size / 1024 / 1024).toFixed(1)} MB ? 点击重新选择</div>
                  </>
                ) : (
                  <>
                    <div className="uz-icon">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                    </div>
                    <div className="uz-text">点击或拖拽文件到此处上传</div>
                    <div className="uz-hint">支持 MP4 / MOV / JPG / PNG ? 单文件最大 500MB</div>
                  </>
                )}
              </div>
              <input ref={uploadFileInputRef} type="file" style={{ display: 'none' }} accept="video/*,image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f) { setUploadMaterialFile(f); if (!uploadMaterialName.trim()) setUploadMaterialName(f.name.replace(/\.[^/.]+$/, '')); setUploadMaterialType(f.type.startsWith('video') ? 'video' : 'image'); }
                }} />
              <div style={{ marginTop: 16 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">素材名称</label>
                    <input className="form-input" value={uploadMaterialName} onChange={(e) => setUploadMaterialName(e.target.value)} placeholder="系统将自动生成" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">素材类型</label>
                    <MultiSelect
                      single
                      options={[{ value: 'video', label: '视频' }, { value: 'image', label: '静态图' }]}
                      value={uploadMaterialType ? [uploadMaterialType] : []}
                      onChange={(vals) => setUploadMaterialType((vals[0] ?? 'video') as 'video' | 'image')}
                    />
                  </div>
                </div>
                <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 8, border: '1px solid rgba(18,16,184,.1)', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--primary-light)' }}>AI 自动处理：</strong> 上传后同步至 Facebook 并返回素材标识，自动绑定到本次新建广告。
                </div>
              </div>
            </div>
            {uploadingMaterial && (
              <div style={{ padding: '0 20px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                  <span>{uploadMaterialType === 'video' ? '视频上传中，请勿关闭...' : '图片上传中...'}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'var(--primary)', borderRadius: 3, transition: 'width 0.3s ease' }} />
                </div>
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowUploadMaterialModal(false)} disabled={uploadingMaterial}>取消</button>
              <button className="btn btn-primary" onClick={() => void submitUploadMaterial()} disabled={uploadingMaterial}>
                {uploadingMaterial ? `上传中 ${uploadProgress}%` : '开始上传'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast stack ──────────────────────────────────────────────────── */}
      <div style={{ position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
        {toasts.map((t) => {
          const c = TOAST_COLOR[t.type];
          return (
            <div key={t.id} style={{
              pointerEvents: 'auto',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              padding: '11px 16px',
              fontSize: 13, fontWeight: 500,
              color: c.color,
              boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
              animation: 'fadeIn 0.2s ease',
              minWidth: 260, maxWidth: 460,
              wordBreak: 'break-word',
            }}>
              <ToastIcon type={t.type} />
              <span style={{ flex: 1, lineHeight: 1.5 }}>{t.message}</span>
              <button onClick={() => dismissToast(t.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.55, padding: '0 0 0 4px', fontSize: 17, lineHeight: 1, flexShrink: 0, marginTop: -1 }}>
                ×
              </button>
            </div>
          );
        })}
      </div>

      {/* ── AI Diagnosis Modal ──────────────────────────────────────────── */}
      <AdDiagnosisModal
        open={!!diagAd}
        onClose={() => { setDiagAd(null); setDiagMetrics(null); }}
        ad={diagAd}
        metrics={diagMetrics}
      />
    </div>
  );
}
